import React, { useState, useContext, Component, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex } from '@rebass/grid';
import styled, { css, withTheme, createGlobalStyle } from 'styled-components';
import { formatDate, parseDate, isNilOrEmpty, phoneNumberFormatter, randomString, encodeToJSR, decodeMessageString } from '@amtrav/utils';
import { Modal, Notice, InputLabel, DropdownSelect, TextInput, ErrorMessage, Button, CharacterCounter, Table, Heading2, Card, GatherBadge, P, Text as Text$2, Badge, InfoTooltip, TitleCase, CardShell, Avatar, ActivityIndicator, BookStatusIndicator, AirlineLogo, Tooltip, IconFlex, Checkbox, CarVendorLogo, RadioButton, InlineLoader, Image, HotelThumbnail, Currency, DotSeparator, Popover, ToastProvider, useToasts, ApprovalTray, ApprovalAccept, ToastNotice, Heading3 } from '@amtrav/view-components';
import { media, getThemeProp, MatchMedia } from '@amtrav/themes';
import { IconLine, IconPlaneAlt, IconTrain, IconInfo, IconWiFi, IconStreamingVideo, IconPersonalVideo, IconLiveTV, IconAngledFlatSeat, IconLieFlatSeat, IconLieFlatSuite, IconPower, IconUSB, IconWiFiFast, IconReclinerSeat, IconMiddleSeatFree, IconSnack, IconMeal, IconFreeAlcoholicDrinks, IconWarningTriangle, IconCarLine, IconPlaneLine, IconHotelLine, IconLineCar, IconCreditCard, IconCaretDown, IconCaretUp, IconTriangle, IconAngleDown, IconCheckCircle, IconCircleX, IconYahoo, IconOutlookOnline, IconOutlook, IconApple, IconGoogle, IconCalendarLine } from '@amtrav/icons';
import { SPACE, getTokenValue, THEME_TOKENS } from '@amtrav/theme-system';
import { useFormik, Formik } from 'formik';
import { makeAppApiProvider, BOOKING_ATTACHMENT_TYPES } from '@amtrav/webservice';
import { useAmtravApiWithNotice } from '@amtrav/react-hooks';
import { parseDate as parseDate$1, hourMinuteFormat, meridianFormat, dayOfWeekShortFormatter, monthShortFormatter, dayOfMonthFormatter, monthLongFormatter, isoExtendedFormat, yearFourDigitFormatter, dayOfWeekLongFormatter, hourMinuteMeridianCondensed, monthOfYearFormatter, yearTwoDigitFormatter, militaryHourFormatter, dayOfMonthLongFormatter, weekdayMonthDayYear, differenceInHours, differenceInMinutes, parseFromTimeZone, leadingZeroHourMinuteSecondFormatter, timeZoneOffsetSeparatedFormatter, isoBasicFormat, apiHourMinuteSecondFormatter } from '@amtrav/dates';
import tinycolor from 'tinycolor2';
import { createSelector } from 'reselect';
import { encodeObjToJsrString } from '@amtrav/jsr';

var _a;
var stubber = function () { return Promise.resolve(true); };
// This is a mock of the webservices that TripDetails will need
// The type signature of this object should match that of the real methods
var TripDetailsApi = {
    bookingApproveOrReject: stubber,
    bookingModPendingAlert: stubber,
    editTripTitle: stubber,
    postEmailItinService: stubber,
    getPdpCheck: stubber,
    getHotelVCardImages: stubber,
    downloadBlob: stubber,
    openWebUrl: function (url, target) {
        console.log("Asked to open url: ".concat(url, " - with target: ").concat(target));
    },
    getJsr: function () { return ({ lastName: 'Tester', src: 'p' }); },
    openSeatSelection: function (bn, fromCode, flightNumber, al, lastName) {
        console.log("Asked to open seat selection with args: ".concat(bn, ", ").concat(fromCode, " ").concat(flightNumber, " ").concat(al, " ").concat(lastName));
    },
    openChangeTripForm: function (bn) {
        console.log("Asked to open change trip for args - bn: ".concat(bn));
    },
    BOOKING_ATTACHMENT_TYPES: BOOKING_ATTACHMENT_TYPES,
};
var useAppApi = (_a = makeAppApiProvider(TripDetailsApi), _a.useAppApi), AppApiContext = _a.AppApiContext, StubProvider = _a.StubProvider;

const BillingTagsModal = _ref => {
  let {
    onClose,
    refetchData,
    companyBillingFields,
    tripDetails
  } = _ref;
  const [isWaiting, setIsWaiting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const {
    editTripBillingTags
  } = useAppApi();
  const billingFields = companyBillingFields.map(bf => ({
    id: bf.id,
    name: bf.name,
    options: bf.options,
    required: bf.mandatoryPNR
  }));
  const presetBillingFields = tripDetails.billRefFields;

  // Generate the initial state from dynamic data
  const initialState = billingFields.reduce((acc, curr) => {
    // Pre-populate these from the backend.
    const existingField = presetBillingFields && presetBillingFields.find(ibf => ibf.id === curr.id);
    acc[curr.id] = existingField ? existingField.value : '';
    return acc;
  }, {});

  // the back-end is expected to respond with an array of strings for 'options'
  const getFieldOptions = options => [{
    label: 'None',
    value: ''
  }, ...options.map(opt => ({
    label: opt,
    value: opt
  }))];
  const formik = useFormik({
    initialValues: initialState,
    validate: values => {
      const errors = {};
      Object.entries(values).forEach(entry => {
        if (entry[1].length > 85) {
          errors[entry[0]] = 'Maximum billing reference length is 85 characters.';
        }
        // checks if billingTag is required and shows error
        const currBillingTag = billingFields.find(e => {
          return e.id.toString() === entry[0];
        });
        if (currBillingTag.required && !entry[1]) {
          errors[entry[0]] = 'This billing tag is required.';
        }
      });
      return errors;
    },
    onSubmit: async values => {
      const resultArray = Object.entries(values).map(_ref2 => {
        let [id, value] = _ref2;
        return {
          id,
          value,
          deleting: !value
        };
      });
      setIsWaiting(true);
      try {
        await editTripBillingTags(tripDetails.bn, tripDetails.anum, resultArray);
        await refetchData();
      } catch (err) {
        console.log(err);
        setIsWaiting(false);
        setErrorMessage(err.message);
      }
    }
  });
  const stripHtmlTags = text => {
    return text.replace(/<\/?[^>]+(>|$)/g, '');
  };
  return /*#__PURE__*/React.createElement(Modal, {
    drawer: true,
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Billing Tags"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Box, {
    width: "325px"
  }, errorMessage ? /*#__PURE__*/React.createElement(Box, {
    pb: 3
  }, /*#__PURE__*/React.createElement(Notice, {
    kind: "danger"
  }, stripHtmlTags(errorMessage))) : null, billingFields.map(field => /*#__PURE__*/React.createElement(Box, {
    key: field.id
  }, /*#__PURE__*/React.createElement(InputLabel, {
    label: field.name,
    required: field.required
  }), field.options ? /*#__PURE__*/React.createElement(DropdownSelect, {
    options: getFieldOptions(field.options),
    value: formik.values[field.id],
    onChange: formik.handleChange,
    name: field.id
  }) : /*#__PURE__*/React.createElement(TextInput, {
    onChange: formik.handleChange,
    value: formik.values[field.id],
    name: field.id
  }), /*#__PURE__*/React.createElement(ErrorMessage, {
    message: formik.errors[field.id] || ''
  }))))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose,
    text: "Cancel",
    bordered: true
  })), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    text: "Save",
    onClick: formik.handleSubmit,
    isLoading: isWaiting
  })));
};
BillingTagsModal.propTypes = {
  onClose: PropTypes.func,
  refetchData: PropTypes.func,
  companyBillingFields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    mandatoryPNR: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.string)
  })),
  tripDetails: PropTypes.shape({
    bn: PropTypes.string,
    anum: PropTypes.number,
    billRefFields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string
    }))
  })
};

const EditNameModal = _ref => {
  let {
    onClose,
    bookingNum,
    refetchData,
    currTripTitle = ''
  } = _ref;
  const {
    editTripTitle
  } = useAppApi();
  const [newTripTitle, setNewTripTitle] = useState(currTripTitle);
  const [titleLengthValid, setTitleLengthValid] = useState(true);
  const [updateTripTitle, isLoading, errorNotice] = useAmtravApiWithNotice(async () => {
    await editTripTitle(bookingNum, newTripTitle);
    refetchData();
  });
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose,
    drawer: true
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Edit Trip Name"), /*#__PURE__*/React.createElement(Modal.Body, null, errorNotice, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(InputLabel, {
    label: "New Trip Name",
    required: true
  }), /*#__PURE__*/React.createElement(TextInput, {
    name: "newTripTitle",
    onChange: event => setNewTripTitle(event.target.value),
    value: newTripTitle
  }), /*#__PURE__*/React.createElement(CharacterCounter, {
    string: newTripTitle,
    limit: 75,
    setIsValid: setTitleLengthValid
  }, titleLengthValid ? null : /*#__PURE__*/React.createElement(ErrorMessage, {
    message: "Trip name must be less that 75 characters."
  }), newTripTitle === '' ? /*#__PURE__*/React.createElement(ErrorMessage, {
    message: "Trip name cannot be empty."
  }) : null))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose,
    text: "Cancel",
    bordered: true
  })), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    text: "Update",
    onClick: updateTripTitle,
    isLoading: isLoading,
    disabled: newTripTitle === currTripTitle || newTripTitle === '' || !titleLengthValid
  })));
};
EditNameModal.propTypes = {
  onClose: PropTypes.func,
  bookingNum: PropTypes.string,
  refetchData: PropTypes.func,
  currTripTitle: PropTypes.string
};

const STATUS_KIND_MAP = {
  CONFIRMED: 'success',
  TICKETED: 'success',
  PENDING: 'warning',
  'NEEDS APPROVAL': 'warning',
  'ON HOLD': 'warning',
  'CANCEL PENDING': 'warning',
  CANCELLED: 'danger',
  CANCELED: 'danger',
  EXPIRED: 'danger',
  REJECTED: 'danger',
  'PROCESSING PAYMENT': 'warning'
};
const ColLeft = styled.col`
  width: 140px;

  ${media.Small`
    width: 228px;
  `}
`;
const ColRight = styled.col`
  width: auto;
`;
const SummaryInfo = styled.div`
  font-size: 18px;
  padding: 2px 0;
`;
const RowCustom = styled(Table.Row)`
  border-style: dotted;
  border-width: 1px 0 0 0;
  border-color: ${getThemeProp('global.border--color')};

  &:first-of-type {
    border-style: solid;
  }

  td {
    padding: 8px 8px 8px 16px !important;
    border: none !important;
  }
`;
const TitleCaseBadge = styled.span`
  text-transform: capitalize;
`;

// Accessor function for code isolation
// eslint-disable-next-line no-console
const getStatusBadgeKind = status => STATUS_KIND_MAP[status] || console.warn(`${status} is not an accepted status`);

// A small helper component for displaying rows of summary data
const SummaryRow = _ref => {
  let {
    label,
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(RowCustom, {
    white: true
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(TitleCase, null, label)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(SummaryInfo, null, children)));
};
SummaryRow.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node
};
SummaryRow.defaultProps = {
  label: '',
  children: []
};
const TripSummarySection = _ref2 => {
  let {
    details,
    handleEditTripName = null
  } = _ref2;
  const {
    bn,
    tripTitle,
    dateCreated,
    status,
    statusHint,
    bookedBy,
    updatedBy,
    meetingInvitation,
    bookingStatus
  } = details;
  return /*#__PURE__*/React.createElement(React.Fragment, null, bookingStatus ? /*#__PURE__*/React.createElement(Notice, {
    mb: SPACE.size24
  }, bookingStatus) : null, /*#__PURE__*/React.createElement(Heading2, {
    mb: SPACE.size16
  }, "Trip Summary"), /*#__PURE__*/React.createElement(Card, {
    style: {
      overflow: 'hidden'
    }
  }, tripTitle && /*#__PURE__*/React.createElement(Flex, {
    ml: 3,
    py: meetingInvitation ? 2 : 3,
    alignItems: "center"
  }, meetingInvitation ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(GatherBadge, {
    mr: 2,
    size: 35,
    iconSize: 22,
    iconHeightOffset: -1
  })) : null, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    width: "100%"
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, /*#__PURE__*/React.createElement("b", null, tripTitle)), handleEditTripName ? /*#__PURE__*/React.createElement(Box, {
    pr: 3
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    text: "Edit",
    onClick: handleEditTripName
  })) : null)), /*#__PURE__*/React.createElement(Table, {
    style: {
      borderCollapse: 'collapse'
    }
  }, /*#__PURE__*/React.createElement("colgroup", null, /*#__PURE__*/React.createElement(ColLeft, null), /*#__PURE__*/React.createElement(ColRight, null)), /*#__PURE__*/React.createElement(Table.Body, {
    dotted: true
  }, /*#__PURE__*/React.createElement(SummaryRow, {
    label: "BOOKING NUMBER"
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, bn)), dateCreated ? /*#__PURE__*/React.createElement(SummaryRow, {
    label: "BOOKING DATE"
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, formatDate(parseDate(dateCreated), 'MMMM d, yyyy'))) : null, /*#__PURE__*/React.createElement(SummaryRow, {
    label: "STATUS"
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Badge, {
    kind: getStatusBadgeKind(status)
  }, /*#__PURE__*/React.createElement(TitleCaseBadge, null, status === null || status === void 0 ? void 0 : status.toLowerCase())), /*#__PURE__*/React.createElement(Box, {
    mt: 1,
    ml: 1
  }, /*#__PURE__*/React.createElement(InfoTooltip, {
    content: statusHint
  })))), bookedBy ? /*#__PURE__*/React.createElement(SummaryRow, {
    label: "BOOKED BY"
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, bookedBy)) : null, updatedBy ? /*#__PURE__*/React.createElement(SummaryRow, {
    label: "UPDATED BY"
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, updatedBy)) : null))));
};
TripSummarySection.propTypes = {
  details: PropTypes.shape({
    bn: PropTypes.string,
    tripTitle: PropTypes.string,
    dateCreated: PropTypes.string,
    meetingInvitation: PropTypes.shape({}),
    status: PropTypes.string,
    statusHint: PropTypes.string,
    bookedBy: PropTypes.string,
    bookingStatus: PropTypes.string,
    updatedBy: PropTypes.string
  }),
  handleEditTripName: PropTypes.func
};
const makeEmptyBillingTagsCard = (setTagsModalOpen, companyBillingFields) => {
  return companyBillingFields && companyBillingFields !== null && companyBillingFields !== void 0 && companyBillingFields.length ? /*#__PURE__*/React.createElement(Box, {
    pt: SPACE.size24
  }, /*#__PURE__*/React.createElement(CardShell, {
    icon: /*#__PURE__*/React.createElement(IconLine, {
      icon: "FileDollar"
    })
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    width: "55%"
  }, "No Billing Tags"), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Button, {
    text: "Set Billing Tags",
    size: "small",
    onClick: () => setTagsModalOpen(true)
  }))))) : null;
};

// The TripSummary component
const TripSummary = _ref3 => {
  let {
    details,
    refetchData,
    companyBillingFields
  } = _ref3;
  const {
    bn,
    tripTitle,
    billRefFields
  } = details;
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const handleEditTripName = () => {
    setEditNameModalOpen(true);
  };
  const filteredCompanyBillingFields = companyBillingFields === null || companyBillingFields === void 0 ? void 0 : companyBillingFields.filter(bf => !bf.staticPerPax);
  return /*#__PURE__*/React.createElement(React.Fragment, null, tagsModalOpen ? /*#__PURE__*/React.createElement(BillingTagsModal, {
    tripDetails: details,
    companyBillingFields: filteredCompanyBillingFields,
    refetchData: refetchData,
    onClose: () => setTagsModalOpen(false)
  }) : null, editNameModalOpen ? /*#__PURE__*/React.createElement(EditNameModal, {
    onClose: () => setEditNameModalOpen(false),
    bookingNum: bn,
    refetchData: refetchData,
    currTripTitle: tripTitle
  }) : null, /*#__PURE__*/React.createElement(TripSummarySection, {
    details: details,
    handleEditTripName: handleEditTripName
  }), billRefFields && billRefFields.length ? /*#__PURE__*/React.createElement(Card, {
    mt: SPACE.size24,
    mb: [2, 2, '24px'],
    style: {
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    width: "100%",
    pl: 3,
    py: 3,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, "Billing Tags"), /*#__PURE__*/React.createElement(Box, {
    mr: 3
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    text: "Edit",
    onClick: () => setTagsModalOpen(true)
  }))), /*#__PURE__*/React.createElement(Table, {
    style: {
      borderCollapse: 'collapse'
    }
  }, /*#__PURE__*/React.createElement("colgroup", null, /*#__PURE__*/React.createElement(ColLeft, null), /*#__PURE__*/React.createElement(ColRight, null)), /*#__PURE__*/React.createElement(Table.Body, {
    dotted: true
  }, billRefFields.map(ref => /*#__PURE__*/React.createElement(SummaryRow, {
    key: ref.id,
    label: ref.name
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, ref.value)))))) : makeEmptyBillingTagsCard(setTagsModalOpen, filteredCompanyBillingFields));
};
TripSummary.propTypes = {
  details: PropTypes.shape({
    bn: PropTypes.string,
    tripTitle: PropTypes.string,
    dateCreated: PropTypes.string,
    meetingInvitation: PropTypes.shape({
      aNum: PropTypes.number,
      attendees: PropTypes.arrayOf(PropTypes.shape({
        attendeeId: PropTypes.number,
        invitationToken: PropTypes.string,
        nameNum: PropTypes.number
      })),
      meetingId: PropTypes.number
    }),
    status: PropTypes.string,
    statusHint: PropTypes.string,
    bookedBy: PropTypes.string,
    updatedBy: PropTypes.string,
    billRefFields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string
    }))
  }),
  refetchData: PropTypes.func,
  companyBillingFields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    mandatoryPNR: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.string)
  }))
};
TripSummary.defaultProps = {
  details: {
    bn: '',
    tripTitle: '',
    dateCreated: '',
    status: '',
    statusHint: '',
    bookedBy: '',
    updatedBy: '',
    billRefFields: PropTypes.arrayOf(PropTypes.shape({
      id: 0,
      name: '',
      value: ''
    }))
  }
};

const AvatarWrapper = styled(Avatar)`
  width: 40px;
  height: 40px;
  font-size: 18px;
`;
const TravelerWrapper = styled.div`
  &:first-child {
    hr {
      display: none;
    }
  }
`;
const TravDivider = styled.hr`
  height: 1px;
  border-top: 1px ${getThemeProp('global.border--color')};
  margin: 8px -8px;
`;
const PrintableFlex$2 = styled(Flex)`
  @media print {
    width: auto;
  }
`;
const Travelers = _ref => {
  let {
    travelers
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading2, {
    mt: [SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size48],
    mb: SPACE.size16
  }, "Travelers"), /*#__PURE__*/React.createElement(Card, {
    mb: [2, 2, '24px']
  }, /*#__PURE__*/React.createElement(Box, {
    p: 2
  }, travelers.map(traveler => {
    return /*#__PURE__*/React.createElement(TravelerWrapper, {
      key: traveler.nameNum
    }, /*#__PURE__*/React.createElement(TravDivider, null), /*#__PURE__*/React.createElement(Flex, {
      width: 1
    }, /*#__PURE__*/React.createElement(Box, {
      width: "53px",
      alignSelf: "center"
    }, /*#__PURE__*/React.createElement(AvatarWrapper, {
      imgUrl: traveler.profileImageUrl,
      initials: traveler.initials
    })), /*#__PURE__*/React.createElement(PrintableFlex$2, {
      flexDirection: "column",
      width: ['80%', '80%', 'auto'],
      alignSelf: "center"
    }, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(P, {
      fontSize: "18px"
    }, traveler.displayName), traveler.isOutsideTraveler ? /*#__PURE__*/React.createElement(Box, {
      ml: 2
    }, /*#__PURE__*/React.createElement(Badge, {
      size: "small",
      noBorderRadius: true,
      kind: "info",
      featured: true
    }, "GUEST")) : null), traveler.knownTraveler && /*#__PURE__*/React.createElement(Flex, {
      alignItems: "baseline"
    }, /*#__PURE__*/React.createElement(TitleCase, null, "KNOWN TRAVELER # "), /*#__PURE__*/React.createElement(P, {
      fontSize: "16px"
    }, traveler.knownTraveler)))));
  }))));
};
Travelers.propTypes = {
  travelers: PropTypes.arrayOf(PropTypes.shape({
    nameNum: PropTypes.number,
    displayName: PropTypes.string,
    knownTraveler: PropTypes.string,
    initials: PropTypes.string,
    isOutsideTraveler: PropTypes.bool
  }))
};
Travelers.defaultProps = {
  travelers: []
};

const defaultVal = {
  details: {},
  readOnly: false,
  reload: () => {},
  postCancel: () => {
    return defaultVal.reload();
  },
  postVirtualCardInfo: () => {},
  // nice to have: add promise
  openBookingModal: () => {},
  closeBookingModal: () => {}
};
const TripDetailsContext = /*#__PURE__*/React.createContext(defaultVal);

const BookingShellLayout = styled(Flex)`
  width: 100%;
  min-height: 100px;
  align-items: center;
  justify-content: space-between;
`;
const BookingShellDefault = styled(BookingShellLayout)`
  background-color: transparent;
  padding: 8px;
`;
const StatusText = styled.div`
  font-weight: bold;
  line-height: 18px;
`;
const CarAndHotelBooking = _ref => {
  let {
    attendee,
    showHotelBooking,
    showCarBooking,
    onClick
  } = _ref;
  const {
    value: hotelStatus
  } = attendee.bookingHotelStatus;
  const {
    value: carStatus
  } = attendee.bookingCarStatus;
  const {
    BOOKING_ATTACHMENT_TYPES
  } = useAppApi();
  const hotelStatusText = hotelStatus === BOOKING_ATTACHMENT_TYPES.OFFERED ? "You haven't booked a hotel." : 'You have declined to book a hotel.';
  const carStatusText = carStatus === BOOKING_ATTACHMENT_TYPES.OFFERED ? "You haven't booked a car rental." : 'You have declined to book a car rental.';
  const additionalItems = [];
  if (showHotelBooking) {
    additionalItems.push('hotel');
  }
  if (showCarBooking) {
    additionalItems.push('car');
  }
  const buttonText = `Book ${additionalItems.join(' or ')}`;
  return /*#__PURE__*/React.createElement(BookingShellDefault, null, /*#__PURE__*/React.createElement(Box, {
    width: ['55%', '55%', 'auto']
  }, showHotelBooking && /*#__PURE__*/React.createElement(Flex, {
    width: [1, 1, 'auto'],
    py: 2,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(BookStatusIndicator, {
    mr: 2,
    type: "hotel",
    size: "small",
    status: attendee.bookingHotelStatus
  }), /*#__PURE__*/React.createElement(StatusText, null, hotelStatusText)), showCarBooking && /*#__PURE__*/React.createElement(Flex, {
    width: [1, 1, 'auto'],
    py: 2,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(BookStatusIndicator, {
    mr: 2,
    type: "car",
    size: "small",
    status: attendee.bookingCarStatus
  }), /*#__PURE__*/React.createElement(StatusText, null, carStatusText))), /*#__PURE__*/React.createElement(Box, {
    width: ['45%', '45%', 'auto'],
    px: 1
  }, /*#__PURE__*/React.createElement(Button, {
    text: buttonText,
    onClick: onClick
  })));
};
const AttendeeInfo = _ref2 => {
  let {
    attendee
  } = _ref2;
  const {
    BOOKING_ATTACHMENT_TYPES
  } = useAppApi();
  const SHOWN_STATUS_LIST = [BOOKING_ATTACHMENT_TYPES.OFFERED, BOOKING_ATTACHMENT_TYPES.DECLINED];
  const {
    isHotelInviteLoading,
    isCarInviteLoading,
    openBookingModal
  } = useContext(TripDetailsContext);
  const {
    bookingCarStatus,
    bookingHotelStatus
  } = attendee;
  const showHotelBooking = SHOWN_STATUS_LIST.includes(bookingHotelStatus.value);
  const showCarBooking = SHOWN_STATUS_LIST.includes(bookingCarStatus.value);
  if (!showCarBooking && !showHotelBooking) {
    return null;
  }
  return /*#__PURE__*/React.createElement(CardShell, {
    borderOnly: true,
    mb: "24px"
  }, /*#__PURE__*/React.createElement(BookingShellLayout, null, isHotelInviteLoading || isCarInviteLoading ? /*#__PURE__*/React.createElement(ActivityIndicator, {
    ml: "50px",
    pr: "24px",
    width: 1
  }) : /*#__PURE__*/React.createElement(CarAndHotelBooking, {
    attendee: attendee,
    showHotelBooking: showHotelBooking,
    showCarBooking: showCarBooking,
    onClick: openBookingModal
  })));
};
const attendeeShape = PropTypes.shape({
  bookingCarStatus: PropTypes.shape({
    value: PropTypes.string
  }),
  bookingHotelStatus: PropTypes.shape({
    value: PropTypes.string
  })
});
CarAndHotelBooking.propTypes = {
  attendee: attendeeShape,
  showHotelBooking: PropTypes.bool,
  showCarBooking: PropTypes.bool,
  onClick: PropTypes.func
};
AttendeeInfo.propTypes = {
  attendee: attendeeShape
};

const Code = styled.span`
  background-color: ${getThemeProp('card.subheaderBackground--color')};
  padding: 0 5px;
  color: ${getThemeProp('global.dark--color')};
  display: inline-block;
  min-width: 34px;
  text-align: center;
  border: 1px solid ${getThemeProp('global.border--color')};
  font-weight: bold;
  border-radius: 3px;
  font-family: ${getThemeProp('global.textBody--fontFamily')};
  font-size: 12px;
  line-height: 1.4;

  ${media.Medium`
    min-width: 40px;
    font-size: 14px;
  `};
`;
const AirportCode = _ref => {
  let {
    code,
    className
  } = _ref;
  return /*#__PURE__*/React.createElement(Code, {
    className: className
  }, code.toUpperCase());
};
AirportCode.propTypes = {
  code: PropTypes.string.isRequired,
  className: PropTypes.string
};
AirportCode.defaultProps = {
  className: undefined
};

const PrintableBox$4 = styled(Box)`
  @media print {
    margin-right: 0;
  }
`;
const PrintableFlex$1 = styled(Flex)`
  @media print {
    flex-direction: row;
  }
`;
const FlightNumberWrapper = styled(PrintableFlex$1)`
  align-items: center;
  min-width: 76px;
`;
const PlaneIconSeparator = styled(IconPlaneAlt)`
  font-size: 28px;
  color: silver;
  width: 28px;
  margin-right: 32px;
`;
const TrainIconSeparator = styled(IconTrain)`
  font-size: 24px;
  color: silver;
  width: 24px;
  margin-right: 32px;
`;
const AirlineName = styled.strong`
  text-align: center;
`;
const OperatedByRegion = styled(Flex)`
  background-color: ${props => tinycolor.mix(getThemeProp('global.warning--color')(props), 'white', 90).toString()};
  border-radius: 3px;
`;
const weekdayMonthDay$1 = date => {
  return `${dayOfWeekShortFormatter(date)}, ${monthShortFormatter(date)} ${dayOfMonthFormatter(date)}`;
};
const TerminalTime = _ref => {
  let {
    direction,
    segment
  } = _ref;
  const [date, code, terminal] = direction === 'departs' ? [parseDate$1(segment.departs), segment.fromCode, segment.fromTerminal] : [parseDate$1(segment.arrives), segment.toCode, segment.toTerminal];
  return /*#__PURE__*/React.createElement(Box, {
    mr: "24px"
  }, /*#__PURE__*/React.createElement(TitleCase, null, direction.toUpperCase()), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "24px",
    mr: "2px"
  }, hourMinuteFormat(date)), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, meridianFormat(date).toUpperCase())), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px",
    mb: "2px"
  }, weekdayMonthDay$1(date)), /*#__PURE__*/React.createElement(AirportCode, {
    code: code
  }), "\xA0", terminal && /*#__PURE__*/React.createElement("span", null, "Terminal ", terminal));
};
TerminalTime.propTypes = {
  direction: PropTypes.string,
  segment: PropTypes.shape({
    departs: PropTypes.string,
    arrives: PropTypes.string,
    fromCode: PropTypes.string,
    toCode: PropTypes.string,
    fromTerminal: PropTypes.string,
    toTerminal: PropTypes.string
  })
};
TerminalTime.defaultProps = {
  direction: '',
  segment: PropTypes.shape({
    departs: '',
    arrives: '',
    fromCode: '',
    toCode: '',
    fromTerminal: '',
    toTerminal: ''
  })
};
const SegmentSchedule = _ref2 => {
  let {
    segment
  } = _ref2;
  return /*#__PURE__*/React.createElement(PrintableFlex$1, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(FlightNumberWrapper, {
    flexDirection: ['row', 'row', 'column'],
    mr: [0, 0, '24px'],
    mb: [2, 2, 0]
  }, /*#__PURE__*/React.createElement(PrintableBox$4, {
    mr: ['24px', '24px', 0]
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    alCode: segment.airlineCode
  })), /*#__PURE__*/React.createElement(AirlineName, null, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, segment.airlineName, " ", /*#__PURE__*/React.createElement("br", null), "#", segment.flightNumber))), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(TerminalTime, {
    direction: "departs",
    segment: segment
  }), segment.isRail ? /*#__PURE__*/React.createElement(TrainIconSeparator, null) : /*#__PURE__*/React.createElement(PlaneIconSeparator, null), /*#__PURE__*/React.createElement(TerminalTime, {
    direction: "arrives",
    segment: segment
  })), segment.opAirlineName && /*#__PURE__*/React.createElement(OperatedByRegion, {
    alignItems: ['start', 'center'],
    p: 2,
    mt: 3
  }, /*#__PURE__*/React.createElement(AirlineLogo, {
    size: "16px",
    alCode: segment.opAirlineCode
  }), /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    pl: 2
  }, "Flight operated by ", segment.opAirlineName))));
};
SegmentSchedule.propTypes = {
  segment: PropTypes.shape({
    airlineCode: PropTypes.string,
    airlineName: PropTypes.string,
    opAirlineName: PropTypes.string,
    opAirlineCode: PropTypes.string,
    flightNumber: PropTypes.number,
    isRail: PropTypes.bool
  })
};
SegmentSchedule.defaultProps = {
  segment: {
    airlineCode: '',
    airlineName: '',
    flightNumber: null
  }
};

const getApiDetails = api => api.details;
const getItinItems = createSelector([getApiDetails], details => details.itinItems);
const getFirstAirlineCode = createSelector([getItinItems], itins => itins.map(itin => itin.flight).filter(i => i).map(flight => flight.segments[0].airlineCode).shift());
const getTravelers = createSelector([getApiDetails], details => details.travelers);
const getFirstNameNum = createSelector([getTravelers], travelers => travelers[0] && travelers[0].nameNum);

// Some basic styling for the info tooltip
const StyledInfo = styled.span`
  color: ${getThemeProp('global.info--color')};
  &:hover {
    color: ${getThemeProp('global.info--color')};
  }
  @media (any-hover: none) {
    display: none;
  }
`;
const LinkStyled = styled.span`
  color: ${getThemeProp('global.link--color')};
  text-decoration: underline;
  cursor: pointer;
`;
const SEAT_CODES = {
  CHANGE: 'C',
  PICK: 'P',
  NONE: 'N'
};
const seatBooking = {
  C: {
    code: 'C',
    name: 'Change'
  },
  P: {
    code: 'P',
    name: 'Pick'
  },
  N: {
    code: 'N',
    name: 'Seat Info'
  }
};
const DetailContent = _ref => {
  let {
    label,
    value
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    mb: "24px"
  }, /*#__PURE__*/React.createElement(TitleCase, null, label), /*#__PURE__*/React.createElement("br", null), value);
};
DetailContent.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node
};
DetailContent.defaultProps = {
  label: '',
  value: null
};
const convertMinutesToHours = minutes => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return {
    hours: h,
    minutes: m
  };
};
const createSeatsSegmentUrl = (bn, fromCode, flightNum, al, lastName) => {
  return `/seats/?bn=${bn}&lastName=${lastName}&al=${al}&src=t&fl=${fromCode + flightNum}`;
};
const PrintableBox$3 = styled(Box)`
  @media print {
    width: 33%;
  }
`;
const PrintableFlex = styled(Flex)`
  @media print {
    flex-direction: row;
  }
`;
const SeatSelection = _ref2 => {
  let {
    seatText,
    seatBookingLevel,
    fromCode,
    flightNumber,
    seatBookingHint
  } = _ref2;
  const api = useContext(TripDetailsContext);
  const {
    bn
  } = api.details;
  const al = getFirstAirlineCode(api);
  const {
    getJsr,
    openSeatSelection
  } = useAppApi();
  const jsr = getJsr();
  const {
    bookingModPendingAlert
  } = useAppApi();
  const handleSeatChange = () => () => {
    bookingModPendingAlert({
      bn
    }).then(() => {
      openSeatSelection(bn, fromCode, flightNumber, al, jsr.lastName);
    });
  };
  return /*#__PURE__*/React.createElement(PrintableBox$3, {
    width: [1, 1, 1 / 3]
  }, /*#__PURE__*/React.createElement(DetailContent, {
    label: "SEAT(S)",
    value: /*#__PURE__*/React.createElement(Flex, {
      alignItems: "baseline"
    }, /*#__PURE__*/React.createElement(Box, {
      mr: 1
    }, /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18,
      mt: "6px"
    }, seatText)), seatBookingLevel !== SEAT_CODES.NONE ? /*#__PURE__*/React.createElement(LinkStyled, {
      onClick: handleSeatChange(bn, createSeatsSegmentUrl(bn, fromCode, flightNumber, al, jsr.lastName))
    }, seatBooking[seatBookingLevel].name) : seatBookingHint && /*#__PURE__*/React.createElement(Tooltip, {
      content: seatBookingHint
    }, /*#__PURE__*/React.createElement(StyledInfo, null, /*#__PURE__*/React.createElement(IconInfo, null))))
  }));
};
SeatSelection.propTypes = {
  fromCode: PropTypes.string,
  flightNumber: PropTypes.number,
  seatText: PropTypes.string,
  seatBookingLevel: PropTypes.string,
  seatBookingHint: PropTypes.string
};
const SegmentDetails = _ref3 => {
  let {
    segment
  } = _ref3;
  useContext(TripDetailsContext);
  const convertedDuration = convertMinutesToHours(segment.duration);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PrintableFlex, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(PrintableBox$3, {
    width: [1, 1, 1 / 3]
  }, /*#__PURE__*/React.createElement(DetailContent, {
    label: "STOP(S)",
    value: segment.numStops === 0 ? /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18,
      mt: "6px"
    }, "Nonstop") : /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18
    }, segment.numStops)
  })), /*#__PURE__*/React.createElement(PrintableBox$3, {
    width: [1, 1, 1 / 3]
  }, /*#__PURE__*/React.createElement(DetailContent, {
    label: "CABIN",
    value: /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18,
      mt: "6px"
    }, segment.cabinText)
  })), /*#__PURE__*/React.createElement(SeatSelection, {
    seatText: segment.seatText,
    seatBookingLevel: segment.seatBookingLevel,
    fromCode: segment.fromCode,
    flightNumber: segment.flightNumber,
    seatBookingHint: segment.seatBookingHint
  })), /*#__PURE__*/React.createElement(PrintableFlex, {
    flexDirection: ['column', 'column', 'row']
  }, segment.aircraftTypeName && /*#__PURE__*/React.createElement(PrintableBox$3, {
    width: [1, 1, 1 / 3]
  }, /*#__PURE__*/React.createElement(DetailContent, {
    label: "AIRCRAFT",
    value: /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18,
      mt: "6px"
    }, segment.aircraftTypeName)
  })), /*#__PURE__*/React.createElement(PrintableBox$3, {
    width: [1, 1, 1 / 3]
  }, /*#__PURE__*/React.createElement(DetailContent, {
    label: "DURATION",
    value: /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18,
      mt: "6px"
    }, convertedDuration.hours, "h ", convertedDuration.minutes, "m")
  })), /*#__PURE__*/React.createElement(PrintableBox$3, {
    width: [1, 1, 1 / 3]
  }, /*#__PURE__*/React.createElement(DetailContent, {
    label: "DISTANCE",
    value: /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18,
      mt: "6px"
    }, segment.miles ? `${segment.miles} miles` : /*#__PURE__*/React.createElement("em", null, "(None Provided)"))
  }))), /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(PrintableBox$3, {
    width: [1, 1, 1 / 3]
  }, /*#__PURE__*/React.createElement(DetailContent, {
    label: "LOYALTY #",
    value: !segment.loyaltyText ? /*#__PURE__*/React.createElement(Text$2, {
      primary: true,
      lineHeight: 1.2,
      weight: "400",
      size: 18,
      mt: "6px"
    }, /*#__PURE__*/React.createElement("em", null, "(None Provided)")) : segment.loyaltyText.map(text => /*#__PURE__*/React.createElement(P, {
      fontSize: "18px",
      mt: 1,
      key: text
    }, text))
  }))));
};
SegmentDetails.propTypes = {
  segment: PropTypes.shape({
    duration: PropTypes.number,
    numStops: PropTypes.number,
    fromCode: PropTypes.string,
    flightNumber: PropTypes.number,
    cabinText: PropTypes.string,
    seatText: PropTypes.string,
    aircraftTypeName: PropTypes.string,
    miles: PropTypes.number,
    loyaltyText: PropTypes.arrayOf(PropTypes.string),
    seatBookingLevel: PropTypes.string,
    seatBookingHint: PropTypes.string
  })
};
SegmentDetails.defaultProps = {
  segment: PropTypes.shape({
    duration: null,
    numStops: null,
    cabinText: '',
    seatText: '',
    aircraftTypeName: '',
    miles: null,
    loyaltyText: []
  })
};

const flightAmenityColor = '#bdbdbd';
const flightAmenityMaybeColor = '#e6e6e6';
const amenityIcons = {
  1: {
    label: 'WiFi',
    getIcon: () => /*#__PURE__*/React.createElement(IconWiFi, {
      color: flightAmenityColor
    })
  },
  2: {
    label: 'Maybe WiFi',
    getIcon: () => /*#__PURE__*/React.createElement(IconWiFi, {
      color: flightAmenityMaybeColor
    })
  },
  3: {
    label: 'WiFi',
    getIcon: () => /*#__PURE__*/React.createElement(IconWiFi, {
      color: flightAmenityColor
    })
  },
  7: {
    label: 'Streaming Video',
    getIcon: () => /*#__PURE__*/React.createElement(IconStreamingVideo, {
      color: flightAmenityColor
    })
  },
  9: {
    label: 'Personal Video Screen',
    getIcon: () => /*#__PURE__*/React.createElement(IconPersonalVideo, {
      color: flightAmenityColor
    })
  },
  10: {
    label: 'Personal Video on Demand',
    getIcon: () => /*#__PURE__*/React.createElement(IconPersonalVideo, {
      color: flightAmenityColor
    })
  },
  11: {
    label: 'LiveTV',
    getIcon: () => /*#__PURE__*/React.createElement(IconLiveTV, {
      color: flightAmenityColor
    })
  },
  12: {
    label: 'Angled Flat Seat',
    getIcon: () => /*#__PURE__*/React.createElement(IconAngledFlatSeat, {
      color: flightAmenityColor
    })
  },
  13: {
    label: 'Lie Flat Seat',
    getIcon: () => /*#__PURE__*/React.createElement(IconLieFlatSeat, {
      color: flightAmenityColor
    })
  },
  14: {
    label: 'Lie Flat Suite',
    getIcon: () => /*#__PURE__*/React.createElement(IconLieFlatSuite, {
      color: flightAmenityColor
    })
  },
  15: {
    label: 'Power',
    getIcon: () => /*#__PURE__*/React.createElement(IconPower, {
      color: flightAmenityColor
    })
  },
  16: {
    label: 'Maybe Power',
    getIcon: () => /*#__PURE__*/React.createElement(IconPower, {
      color: flightAmenityMaybeColor
    })
  },
  17: {
    label: 'USB',
    getIcon: () => /*#__PURE__*/React.createElement(IconUSB, {
      color: flightAmenityColor
    })
  },
  18: {
    label: 'Maybe USB',
    getIcon: () => /*#__PURE__*/React.createElement(IconUSB, {
      color: flightAmenityMaybeColor
    })
  },
  19: {
    label: 'Maybe LiveTV',
    getIcon: () => /*#__PURE__*/React.createElement(IconLiveTV, {
      color: flightAmenityMaybeColor
    })
  },
  26: {
    label: 'WiFi Fast',
    getIcon: () => /*#__PURE__*/React.createElement(IconWiFiFast, {
      color: flightAmenityColor
    })
  },
  28: {
    label: 'Maybe Lie Flat',
    getIcon: () => /*#__PURE__*/React.createElement(IconLieFlatSeat, {
      color: flightAmenityMaybeColor
    })
  },
  29: {
    label: 'Recliner Seat',
    getIcon: () => /*#__PURE__*/React.createElement(IconReclinerSeat, {
      color: flightAmenityColor
    })
  },
  30: {
    label: 'Middle Seat Free',
    getIcon: () => /*#__PURE__*/React.createElement(IconMiddleSeatFree, {
      color: flightAmenityColor
    })
  },
  32: {
    label: 'Maybe Video on Demand',
    getIcon: () => /*#__PURE__*/React.createElement(IconPersonalVideo, {
      color: flightAmenityMaybeColor
    })
  },
  33: {
    label: 'Maybe Personal Video Screen',
    getIcon: () => /*#__PURE__*/React.createElement(IconPersonalVideo, {
      color: flightAmenityMaybeColor
    })
  },
  34: {
    label: 'Maybe Streaming Video',
    getIcon: () => /*#__PURE__*/React.createElement(IconStreamingVideo, {
      color: flightAmenityMaybeColor
    })
  },
  35: {
    label: 'Free Snack',
    getIcon: () => /*#__PURE__*/React.createElement(IconSnack, {
      color: flightAmenityColor
    })
  },
  37: {
    label: 'Free Meal',
    getIcon: () => /*#__PURE__*/React.createElement(IconMeal, {
      color: flightAmenityColor
    })
  },
  38: {
    label: 'Free Premium Meal',
    getIcon: () => /*#__PURE__*/React.createElement(IconMeal, {
      color: flightAmenityColor
    })
  },
  39: {
    label: 'Free Alcoholic Drinks',
    getIcon: () => /*#__PURE__*/React.createElement(IconFreeAlcoholicDrinks, {
      color: flightAmenityColor
    })
  },
  40: {
    label: 'Paid Snack',
    getIcon: () => /*#__PURE__*/React.createElement(IconSnack, {
      color: flightAmenityColor
    })
  }
};
const IconWithInfo = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 4px;
`;
const SegmentAmenities = _ref => {
  let {
    segment
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleCase, null, "AMENITIES*"), /*#__PURE__*/React.createElement(Flex, {
    fontSize: "24px",
    flexWrap: "wrap"
  }, segment.inflightAmenities.map(id => /*#__PURE__*/React.createElement(IconWithInfo, {
    key: id
  }, amenityIcons[id].getIcon(), /*#__PURE__*/React.createElement(P, {
    mr: 3,
    ml: 1
  }, amenityIcons[id].label)))));
};
SegmentAmenities.propTypes = {
  segment: PropTypes.shape({
    inflightAmenities: PropTypes.arrayOf(PropTypes.number)
  })
};
SegmentAmenities.defaultProps = {
  segment: []
};

const DateText = styled(Box)`
  font-size: 14px;
  line-height: 1.2;
  max-width: 270px;

  ${media.Small`
    max-width: unset;
  `};
`;

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

const PromptedComponent = (WrappedComponent, promptConfig) => {
  class PromptWrapper extends Component {
    constructor(props) {
      super(props);
      this.activators = Object.fromEntries(Object.entries(promptConfig).map(_ref => {
        let [key, config] = _ref;
        return [key, options => new Promise((resolve, reject) => {
          this.setState(prevState => {
            return {
              ...prevState,
              activePrompts: {
                ...prevState.activePrompts,
                [key]: config(resolve, reject, options)
              }
            };
          });
        }).finally(() => {
          this.setState(prevState => {
            const newPrompts = {
              ...prevState.activePrompts
            };
            delete newPrompts[key];
            return {
              ...prevState,
              activePrompts: newPrompts
            };
          });
        })];
      }));
      this.state = {
        activePrompts: {}
      };
    }
    render() {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, this.props, {
        prompts: this.activators
      })), Object.entries(this.state.activePrompts).map(_ref2 => {
        let [key, comp] = _ref2;
        return (
          /*#__PURE__*/
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          React.createElement("div", {
            key: key,
            onClick: e => e.stopPropagation()
          }, comp)
        );
      }));
    }
  }
  return PromptWrapper;
};

const ItinItemWrapper$2 = styled(Flex)`
  flex-wrap: wrap;
  align-items: center;
  border-width: 1px;
  border-style: solid;
  border-color: ${getThemeProp('global.border--color')};
  border-radius: ${getThemeProp('global.borderRadius--size')};
  padding: 10px;
  margin: 16px 0;
  max-width: 580px;
`;
const TripPieceTitle$2 = styled(Box)`
  font-weight: bold;
  margin-top: 4px;
`;
const TravelerName = styled(Box)`
  font-weight: 600;
  margin-top: 8px;
`;
const BoxCentered$1 = styled(Box)`
  text-align: center;
  align-self: center;
`;
const WarningText = styled.div`
  color: ${getThemeProp('global.warning--color')};
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
`;
const monthDay$3 = dateString => {
  return `${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}`;
};
const getCarForm$1 = (option, value, handleBlur, handleChange) => {
  return /*#__PURE__*/React.createElement(React.Fragment, {
    key: option.id
  }, /*#__PURE__*/React.createElement(ItinItemWrapper$2, null, /*#__PURE__*/React.createElement(BoxCentered$1, {
    width: ['30px', '30px', '24px']
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: value,
    value: value,
    name: option.id,
    id: option.id,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React.createElement(BoxCentered$1, {
    width: ['45px', '45px', '42px']
  }, /*#__PURE__*/React.createElement(IconFlex, {
    size: 24,
    widthOffset: 8,
    defaultColor: true
  }, /*#__PURE__*/React.createElement(IconCarLine, null))), /*#__PURE__*/React.createElement(Box, {
    width: ['73%', '73%', '480px']
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "16px"
  }, monthDay$3(option.car.pickUp), " - ", monthDay$3(option.car.dropOff)), /*#__PURE__*/React.createElement(TripPieceTitle$2, null, option.car.vendorName, " \u2014 ", option.car.carType), /*#__PURE__*/React.createElement(TravelerName, null, option.car.name)), /*#__PURE__*/React.createElement(Box, {
    mt: 3
  }, /*#__PURE__*/React.createElement(WarningText, null, option.rules))));
};
const getFlightsForm$1 = (option, value, handleBlur, handleChange) => {
  return /*#__PURE__*/React.createElement(React.Fragment, {
    key: option.id
  }, /*#__PURE__*/React.createElement(ItinItemWrapper$2, null, /*#__PURE__*/React.createElement(BoxCentered$1, {
    width: ['30px', '30px', '24px']
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: value,
    value: value,
    name: option.id,
    id: option.id,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React.createElement(BoxCentered$1, {
    width: ['45px', '45px', '42px']
  }, /*#__PURE__*/React.createElement(IconFlex, {
    size: 24,
    widthOffset: 8,
    defaultColor: true
  }, /*#__PURE__*/React.createElement(IconPlaneLine, null))), /*#__PURE__*/React.createElement(Box, {
    width: ['73%', '73%', '480px']
  }, /*#__PURE__*/React.createElement("ul", null, option.flights.map(flight => /*#__PURE__*/React.createElement("li", {
    key: flight.tag
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "16px"
  }, monthDay$3(flight.segments[0].departs)), /*#__PURE__*/React.createElement(TripPieceTitle$2, null, flight.segments[0].fromName, " to ", flight.segments[flight.segments.length - 1].toName))))), /*#__PURE__*/React.createElement(Box, {
    mt: 3
  }, /*#__PURE__*/React.createElement(WarningText, null, option.rules))));
};
const getHotelForm$1 = (option, value, handleBlur, handleChange) => {
  return /*#__PURE__*/React.createElement(React.Fragment, {
    key: option.id
  }, /*#__PURE__*/React.createElement(ItinItemWrapper$2, null, /*#__PURE__*/React.createElement(BoxCentered$1, {
    width: ['30px', '30px', '24px']
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: value,
    value: value,
    name: option.id,
    id: option.id,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React.createElement(BoxCentered$1, {
    width: ['45px', '45px', '42px']
  }, /*#__PURE__*/React.createElement(IconFlex, {
    size: 24,
    widthOffset: 8,
    defaultColor: true
  }, /*#__PURE__*/React.createElement(IconHotelLine, null))), /*#__PURE__*/React.createElement(Box, {
    width: ['73%', '73%', '480px']
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "16px"
  }, monthDay$3(option.hotel.arrives), " - ", monthDay$3(option.hotel.departs)), /*#__PURE__*/React.createElement(TripPieceTitle$2, null, option.hotel.hotelName), /*#__PURE__*/React.createElement(TravelerName, null, option.hotel.name)), /*#__PURE__*/React.createElement(Box, {
    mt: 3
  }, /*#__PURE__*/React.createElement(WarningText, null, option.rules))));
};
const getOptionForm$1 = (option, values, handleBlur, handleChange) => {
  if (option.car) {
    return getCarForm$1(option, values[option.id], handleBlur, handleChange);
  }
  if (option.flights) {
    return getFlightsForm$1(option, values[option.id], handleBlur, handleChange);
  }
  if (option.hotel) {
    return getHotelForm$1(option, values[option.id], handleBlur, handleChange);
  }
  return null;
};
const validateCancel = values => {
  // If we have at least one checked box, return an empty error object
  return Object.values(values).some(e => e) ? {} : values;
};
const CancelModalView = _ref => {
  let {
    cancelOptions,
    cancelId,
    onSubmit,
    onClose
  } = _ref;
  const {
    postCancel,
    details: {
      bn
    }
  } = useContext(TripDetailsContext);
  // State for a loading spinner while the cancel request is being processed
  const [isLoading, setLoading] = useState(false);
  const handleSubmit = values => {
    const cancelSegments = Object.entries(values).filter(_ref2 => {
      let [, cancel] = _ref2;
      return cancel;
    }) // Only send entries that have been cancelled
    .map(_ref3 => {
      let [id] = _ref3;
      // Include the id and the rules for each cancelled option
      const option = cancelOptions.find(opt => opt.id === id);
      const payload = {
        cancelId: id,
        rules: option.rules
      };
      if (option.hasOwnProperty('refundOffered')) {
        payload.refundOffered = option.refundOffered;
      }
      return payload;
    });
    setLoading(true);
    onSubmit(postCancel({
      bn,
      cancelSegments
    }).then(data => {
      setLoading(false);
      return data;
    }));
  };
  const initialValues = Object.fromEntries(cancelOptions.map(option => [option.id, option.id === cancelId]));
  return /*#__PURE__*/React.createElement(Formik, {
    initialValues: initialValues,
    isInitialValid: isNilOrEmpty(validateCancel(initialValues)),
    validate: validateCancel,
    onSubmit: handleSubmit
  }, _ref4 => {
    let {
      values,
      submitForm,
      handleChange,
      handleBlur,
      isValid
    } = _ref4;
    return /*#__PURE__*/React.createElement(Modal, {
      kind: "danger",
      onClose: onClose
    }, /*#__PURE__*/React.createElement(Modal.Header, null, "Select Itinerary Item(s) to Cancel"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Notice, {
      kind: "warning"
    }, /*#__PURE__*/React.createElement(Flex, {
      alignItems: ['center', 'center', 'flex-end']
    }, /*#__PURE__*/React.createElement(IconFlex, {
      size: 24,
      white: true
    }, /*#__PURE__*/React.createElement(IconWarningTriangle, null), ' '), /*#__PURE__*/React.createElement(Box, {
      ml: 2
    }, "Once items are cancelled, this process ", /*#__PURE__*/React.createElement("em", null, "cannot be undone"), "!"))), cancelOptions.map(option => getOptionForm$1(option, values, handleBlur, handleChange))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Box, {
      mr: 2
    }, /*#__PURE__*/React.createElement(Button, {
      text: "Go Back",
      kind: "danger",
      bordered: true,
      onClick: onClose
    })), /*#__PURE__*/React.createElement(Button, {
      text: "Cancel Selected Item(s)",
      kind: "danger",
      disabled: !isValid,
      onClick: submitForm,
      isLoading: isLoading
    })));
  });
};
CancelModalView.propTypes = {
  cancelId: PropTypes.string,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  cancelOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    rules: PropTypes.string,
    hotel: PropTypes.shape({
      hotelName: PropTypes.string,
      arrives: PropTypes.string,
      departs: PropTypes.string
    }),
    car: PropTypes.shape({
      pickUp: PropTypes.string,
      dropOff: PropTypes.string,
      vendorName: PropTypes.string,
      carType: PropTypes.string
    }),
    flights: PropTypes.arrayOf(PropTypes.shape({
      tag: PropTypes.string,
      segments: PropTypes.arrayOf(PropTypes.shape({
        departs: PropTypes.string,
        fromName: PropTypes.string
      }))
    }))
  }))
};
CancelModalView.defaultProps = {
  cancelId: '',
  onSubmit: () => {},
  onClose: () => {},
  cancelOptions: []
};

const ItinItemWrapper$1 = styled(Flex)`
  flex-wrap: wrap;
  align-items: center;
  border-width: 1px;
  border-style: solid;
  border-color: ${getThemeProp('global.border--color')};
  border-radius: ${getThemeProp('global.borderRadius--size')};
  padding: 10px;
  margin: 16px 0;
  max-width: 580px;
`;
const TripPieceTitle$1 = styled(Box)`
  font-weight: bold;
  margin-top: 8px;
`;
const BoxCentered = styled(Box)`
  text-align: center;
  align-self: center;
`;
const monthDay$2 = dateString => {
  return `${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}`;
};
const getCarForm = option => {
  return /*#__PURE__*/React.createElement(React.Fragment, {
    key: option.cancelId
  }, /*#__PURE__*/React.createElement(ItinItemWrapper$1, null, /*#__PURE__*/React.createElement(BoxCentered, {
    width: ['45px', '45px', '42px']
  }, /*#__PURE__*/React.createElement(IconFlex, {
    size: 24,
    widthOffset: 8,
    defaultColor: true
  }, /*#__PURE__*/React.createElement(IconCarLine, null))), /*#__PURE__*/React.createElement(Box, {
    width: ['73%', '73%', '480px']
  }, /*#__PURE__*/React.createElement(Box, null, monthDay$2(option.car.pickUp), " - ", monthDay$2(option.car.dropOff)), /*#__PURE__*/React.createElement(TripPieceTitle$1, null, option.car.vendorName, " \u2014 ", option.car.carType), option.message && /*#__PURE__*/React.createElement(Notice, {
    kind: "warning"
  }, option.message)), /*#__PURE__*/React.createElement(Box, {
    mt: 2,
    ml: "42px"
  }, option.success ? /*#__PURE__*/React.createElement(AirportCode, {
    code: "CANCELLED"
  }) : /*#__PURE__*/React.createElement(AirportCode, {
    code: "PENDING"
  }))));
};
const getFlightsForm = option => {
  return /*#__PURE__*/React.createElement(React.Fragment, {
    key: option.cancelId
  }, /*#__PURE__*/React.createElement(ItinItemWrapper$1, null, /*#__PURE__*/React.createElement(BoxCentered, {
    width: ['45px', '45px', '42px']
  }, /*#__PURE__*/React.createElement(IconFlex, {
    size: 24,
    widthOffset: 8,
    defaultColor: true
  }, /*#__PURE__*/React.createElement(IconPlaneLine, null))), /*#__PURE__*/React.createElement(Box, {
    width: ['73%', '73%', '480px']
  }, /*#__PURE__*/React.createElement("ul", null, option.air.map(flight => /*#__PURE__*/React.createElement("li", {
    key: flight.date
  }, /*#__PURE__*/React.createElement(Box, null, monthDay$2(flight.date)), /*#__PURE__*/React.createElement(TripPieceTitle$1, null, flight.fromCity, " to ", flight.toCity)))), option.message && /*#__PURE__*/React.createElement(Notice, {
    kind: "warning"
  }, option.message)), /*#__PURE__*/React.createElement(Box, {
    mt: 2,
    ml: "42px"
  }, option.success ? /*#__PURE__*/React.createElement(AirportCode, {
    code: "CANCELLED"
  }) : /*#__PURE__*/React.createElement(AirportCode, {
    code: "PENDING"
  }))));
};
const getHotelForm = option => {
  return /*#__PURE__*/React.createElement(React.Fragment, {
    key: option.cancelId
  }, /*#__PURE__*/React.createElement(ItinItemWrapper$1, null, /*#__PURE__*/React.createElement(BoxCentered, {
    width: ['45px', '45px', '42px']
  }, /*#__PURE__*/React.createElement(IconFlex, {
    size: 24,
    widthOffset: 8,
    defaultColor: true
  }, /*#__PURE__*/React.createElement(IconHotelLine, null))), /*#__PURE__*/React.createElement(Box, {
    width: ['73%', '73%', '480px']
  }, /*#__PURE__*/React.createElement(Box, null, option.hotel.hotelName), /*#__PURE__*/React.createElement(TripPieceTitle$1, null, monthDay$2(option.hotel.fromDate), " - ", monthDay$2(option.hotel.toDate)), option.message && /*#__PURE__*/React.createElement(Notice, {
    kind: "warning"
  }, option.message)), /*#__PURE__*/React.createElement(Box, {
    mt: 2,
    ml: "42px"
  }, option.success ? /*#__PURE__*/React.createElement(AirportCode, {
    code: "CANCELLED"
  }) : /*#__PURE__*/React.createElement(AirportCode, {
    code: "PENDING"
  }))));
};
const getOptionForm = option => {
  if (option.car) {
    return getCarForm(option);
  }
  if (option.air) {
    return getFlightsForm(option);
  }
  if (option.hotel) {
    return getHotelForm(option);
  }
  return null;
};
const CancelSuccessModal = _ref => {
  let {
    results,
    onClose
  } = _ref;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose,
    kind: "danger"
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Cancelled Trip Component(s)"), /*#__PURE__*/React.createElement(Modal.Body, null, results.status.map(stat => getOptionForm(stat))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    text: "Ok",
    onClick: onClose
  })));
};
CancelSuccessModal.propTypes = {
  results: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.object)
  }),
  onClose: PropTypes.func
};
CancelSuccessModal.defaultProps = {
  results: {
    status: []
  },
  onClose: () => {}
};

const CancelFailureModal = _ref => {
  let {
    results,
    onClose
  } = _ref;
  const data = results.response && results.response.data;
  const serverCode = data && data.Code;
  const serverMessage = data && data.Message;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose,
    kind: "danger"
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Uh Oh"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Box, {
    width: ['73%', '73%', '480px']
  }, serverCode && serverCode < 9000 ? /*#__PURE__*/React.createElement(P, null, serverMessage) : /*#__PURE__*/React.createElement(P, null, "We ran into a problem trying to cancel your booking."))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    text: "Ok",
    kind: "danger",
    onClick: onClose
  })));
};
CancelFailureModal.propTypes = {
  results: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.object)
  }),
  onClose: PropTypes.func
};
CancelFailureModal.defaultProps = {
  results: {
    status: []
  },
  onClose: () => {}
};

const Canceller = _ref => {
  let {
    prompts,
    children
  } = _ref;
  const {
    reload
  } = useContext(TripDetailsContext);
  const activator = config => {
    return prompts.cancel(config).then(res => {
      reload();
      return prompts.success(res);
    }, err => {
      if (err.response) {
        return prompts.error(err);
      }
      return true;
    });
  };
  return children(activator);
};
Canceller.propTypes = {
  prompts: PropTypes.shape({
    cancel: PropTypes.func,
    success: PropTypes.func
  })
};
Canceller.defaultProps = {
  prompts: {
    cancel: () => {},
    success: () => {}
  }
};
var Canceller$1 = PromptedComponent(Canceller, {
  cancel: (resolve, reject, cancelData) => /*#__PURE__*/React.createElement(CancelModalView, {
    onSubmit: resolve,
    onClose: reject,
    cancelOptions: cancelData.options,
    cancelId: cancelData.id
  }),
  success: (resolve, reject, results) => /*#__PURE__*/React.createElement(CancelSuccessModal, {
    onSubmit: resolve,
    onClose: resolve,
    results: results
  }),
  error: (resolve, reject, results) => /*#__PURE__*/React.createElement(CancelFailureModal, {
    onSubmit: resolve,
    onClose: resolve,
    results: results
  })
});

const HideFromPrintBox$1 = styled(Box)`
  @media print {
    display: none;
  }
`;
const ChangeOrCancelButton = _ref => {
  let {
    enableManageTrips,
    cancelOnlyMode,
    bookingNumber,
    item,
    cancelable,
    cancelOptions
  } = _ref;
  const {
    openWebUrl
  } = useAppApi();
  if (enableManageTrips) {
    return /*#__PURE__*/React.createElement(HideFromPrintBox$1, {
      pl: ['0', '1']
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      kind: cancelOnlyMode ? 'danger' : null,
      text: `${!cancelOnlyMode ? 'Change or ' : ''}Cancel`,
      onClick: () => {
        const manageJsr = {};
        manageJsr.bn = bookingNumber;
        manageJsr.componentId = item.componentId;
        openWebUrl(`/trips/change?jsr=${encodeObjToJsrString(manageJsr)}`);
      }
    }));
  }
  // This is only used on CheapAir
  if (cancelable && item.cancelId && !enableManageTrips) {
    return /*#__PURE__*/React.createElement(HideFromPrintBox$1, {
      pl: 1
    }, /*#__PURE__*/React.createElement(Canceller$1, null, activator => /*#__PURE__*/React.createElement(Button, {
      size: "small",
      kind: "danger",
      text: "Cancel",
      onClick: () => activator({
        id: item.cancelId,
        options: cancelOptions
      }).catch(() => {})
    })));
  }
  return null;
};
ChangeOrCancelButton.propTypes = {
  bookingNumber: PropTypes.number,
  enableManageTrips: PropTypes.bool,
  cancelOnlyMode: PropTypes.bool,
  cancelable: PropTypes.bool,
  item: PropTypes.shape({
    cancelId: PropTypes.string,
    componentId: PropTypes.string
  }),
  cancelOptions: PropTypes.arrayOf(PropTypes.shape({}))
};

const PlaneIconCentered = styled(IconPlaneAlt)`
  font-size: 26px;
  position: relative;
  top: 7px;
  margin-top: -7px;
  padding: 0 6px;
`;
const TrainIconCentered = styled(IconTrain)`
  font-size: 26px;
  position: relative;
  top: 7px;
  margin-top: -7px;
  padding: 0 6px;
`;
const ConfirmationNumber = styled(Flex)`
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const HideFromPrintBox = styled(Box)`
  @media print {
    display: none;
  }
`;
const CardHeaderButtonBadgesContainer = styled(Flex)`
  gap: 4px;
`;
const getLayoverType = code => {
  if (!code) return '';
  switch (code.toUpperCase()) {
    case 'C':
      return 'Connection';
    case 'S':
      return 'Stop';
    default:
      return '';
  }
};
const dateTabletAndLarger$3 = dateString => {
  return `${dayOfWeekShortFormatter(parseDate$1(dateString))}, ${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}, ${yearFourDigitFormatter(parseDate$1(dateString))}`;
};
const dateMobile$2 = dateString => {
  return `${dayOfWeekShortFormatter(parseDate$1(dateString))}, ${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}`;
};
const hasAmenities = segments => {
  return segments.some(segment => !isNilOrEmpty(segment.inflightAmenities));
};
const Flight = _ref => {
  let {
    item,
    cancelable,
    cancelOptions,
    bookingNumber,
    enableManageTrips,
    cancelOnlyMode
  } = _ref;
  const {
    readOnly
  } = useContext(TripDetailsContext);
  const {
    flight
  } = item;
  const {
    segments
  } = flight;
  const confirmationNum = flight.vendorRecordLocator || null;
  const {
    openWebUrl
  } = useAppApi();
  const flightStatusLink = `/trips/status.aspx?json=${JSON.stringify({
    segments: segments.map(seg => ({
      al: seg.airlineCode,
      flNum: seg.flightNumber,
      date: isoExtendedFormat(parseDate$1(seg.departs)),
      fromCode: seg.fromCode,
      toCode: seg.toCode
    }))
  })}`;
  const checkInBadge = currentItem => {
    const checkedIn = currentItem.flight.segments.some(seg => seg.isFlightCheckedIn);
    const boarded = currentItem.flight.segments.some(seg => seg.isFlightBoarded);
    const {
      checkInAvailable
    } = item;
    if (boarded) {
      return /*#__PURE__*/React.createElement(Box, {
        pl: 1
      }, /*#__PURE__*/React.createElement(Badge, null, "Boarded"));
    }
    if (checkedIn) {
      return /*#__PURE__*/React.createElement(Box, {
        pl: 1
      }, /*#__PURE__*/React.createElement(Badge, null, "Checked In"));
    }
    if (checkInAvailable) {
      return /*#__PURE__*/React.createElement(Box, {
        pl: 1
      }, /*#__PURE__*/React.createElement(Button, {
        size: "small",
        text: "Check In",
        onClick: () => {
          openWebUrl(item.checkInAvailable);
        }
      }));
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Card.SubHeader, {
    py: "2"
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: ['column', 'row'],
    justifyContent: "space-between",
    alignItems: ['start', 'center'],
    width: "100%"
  }, /*#__PURE__*/React.createElement(DateText, null, /*#__PURE__*/React.createElement(MatchMedia.XSmallOnly, null, dateMobile$2(segments[0].departs)), /*#__PURE__*/React.createElement(MatchMedia.Small, null, dateTabletAndLarger$3(segments[0].departs))), !readOnly && /*#__PURE__*/React.createElement(CardHeaderButtonBadgesContainer, {
    pt: ['1', '0']
  }, checkInBadge(item), item.statusAvailable && /*#__PURE__*/React.createElement(HideFromPrintBox, {
    pl: 1
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    text: "Status",
    onClick: () => {
      openWebUrl(flightStatusLink);
    }
  })), /*#__PURE__*/React.createElement(ChangeOrCancelButton, {
    enableManageTrips: enableManageTrips,
    bookingNumber: bookingNumber,
    item: item,
    cancelable: cancelable,
    cancelOptions: cancelOptions,
    cancelOnlyMode: cancelOnlyMode
  })))), /*#__PURE__*/React.createElement(Card.Header, null, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, /*#__PURE__*/React.createElement("b", null, segments[0].fromName), segments[0].isRail ? /*#__PURE__*/React.createElement(TrainIconCentered, null) : /*#__PURE__*/React.createElement(PlaneIconCentered, null), /*#__PURE__*/React.createElement("b", null, segments[segments.length - 1].toName)))), /*#__PURE__*/React.createElement(Box, null, segments.map((segment, index) => {
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: segment.segNum
    }, /*#__PURE__*/React.createElement("div", null, index === 0 ? !isNilOrEmpty(confirmationNum) && /*#__PURE__*/React.createElement(Card.SubHeader, null, /*#__PURE__*/React.createElement(ConfirmationNumber, null, /*#__PURE__*/React.createElement(TitleCase, null, "CONFIRMATION # "), /*#__PURE__*/React.createElement(P, {
      fontSize: "18px",
      darkColor: true
    }, /*#__PURE__*/React.createElement("b", null, confirmationNum)))) : /*#__PURE__*/React.createElement(Card.SubHeader, {
      enclosed: true
    }, /*#__PURE__*/React.createElement(Box, {
      m: "auto"
    }, /*#__PURE__*/React.createElement("b", null, getLayoverType(segment.connectionType), " in ", segment.fromName)))), /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(SegmentSchedule, {
      segment: segment
    })), /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(SegmentDetails, {
      segment: segment
    })), !isNilOrEmpty(segment.inflightAmenities) && /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(SegmentAmenities, {
      segment: segment
    })));
  }))), /*#__PURE__*/React.createElement(Box, {
    mt: 2,
    mb: [3, 3, '24px'],
    ml: [2, 2, 0],
    width: "95%"
  }, hasAmenities(segments) && /*#__PURE__*/React.createElement(P, null, "*We do our best to present accurate in flight service details, but amenities are subject to change and cannot be guaranteed.")));
};
Flight.propTypes = {
  bookingNumber: PropTypes.number,
  item: PropTypes.shape({
    flight: PropTypes.shape({
      vendorRecordLocator: PropTypes.string,
      segments: PropTypes.arrayOf(PropTypes.shape({
        vendorRecordLocator: PropTypes.string,
        fromName: PropTypes.string,
        toName: PropTypes.string,
        departs: PropTypes.string,
        inflightAmenities: PropTypes.arrayOf(PropTypes.number),
        isRail: PropTypes.bool
      }))
    }),
    cancelId: PropTypes.string,
    checkInAvailable: PropTypes.string,
    statusAvailable: PropTypes.bool,
    componentId: PropTypes.string
  }),
  cancelable: PropTypes.bool,
  cancelOptions: PropTypes.arrayOf(PropTypes.shape({})),
  enableManageTrips: PropTypes.bool,
  cancelOnlyMode: PropTypes.bool
};
Flight.defaultProps = {
  item: {
    flight: {
      segments: []
    },
    cancelId: '',
    checkInAvailable: null,
    statusAvailable: false
  },
  cancelable: false,
  cancelOptions: []
};

const dayMonthDay = dateString => {
  const date = parseDate$1(dateString);
  return `${dayOfWeekLongFormatter(date)}, ${monthLongFormatter(date)} ${dayOfMonthFormatter(date)}`;
};
const carEvent = (direction, car) => {
  const [date, details] = direction === 'pickUp' ? [car.pickUp, car.pickUpDetails] : [car.dropOff, car.dropOffDetails];
  const label = direction === 'pickUp' ? 'PICK UP' : 'DROP OFF';
  return /*#__PURE__*/React.createElement(Box, {
    width: 1 / 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, label), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "24px"
  }, hourMinuteFormat(parseDate$1(date))), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, meridianFormat(parseDate$1(date)))), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, dayMonthDay(date)), details.map(line => /*#__PURE__*/React.createElement(P, {
    key: line
  }, line)));
};
const CarSchedule = _ref => {
  let {
    car
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, null, carEvent('pickUp', car), carEvent('dropOff', car));
};
CarSchedule.propTypes = {
  car: PropTypes.shape({
    pickUp: PropTypes.string,
    dropOff: PropTypes.string,
    pickUpLocationName: PropTypes.string,
    dropOffLocationName: PropTypes.string,
    pickUpShuttle: PropTypes.string,
    dropOffShuttle: PropTypes.string
  })
};
CarSchedule.defaultProps = {
  car: PropTypes.shape({
    pickUp: '',
    dropOff: '',
    pickUpLocationName: '',
    dropOffLocationName: '',
    pickUpShuttle: '',
    dropOffShuttle: ''
  })
};

const PrintableBox$2 = styled(Box)`
  @media print {
    width: ${_ref => {
  let {
    printWidth
  } = _ref;
  return `${printWidth || '33%'}`;
}};
  }
`;
const PrintableRow$4 = styled(Flex)`
  @media print {
    flex-direction: row;
  }
`;
const layoutDetail$1 = (label, value) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleCase, null, label), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Box, {
    width: 1,
    mb: "24px"
  }, value));
};
const CarDetails = _ref2 => {
  let {
    car
  } = _ref2;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PrintableRow$4, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(PrintableBox$2, {
    width: [1, 1, 1 / 3]
  }, layoutDetail$1('CAR TYPE', /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px",
    mt: 1
  }, car.carType), /*#__PURE__*/React.createElement(P, null, car.makeModel)))), car.mileageDisplay && /*#__PURE__*/React.createElement(PrintableBox$2, {
    width: [1, 1, 1 / 3]
  }, layoutDetail$1('MILEAGE', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, car.mileageDisplay))), /*#__PURE__*/React.createElement(PrintableBox$2, {
    width: [1, 1, 1 / 3]
  }, layoutDetail$1('AUTOMATIC TRANSMISSION', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, car.automaticTransmission ? 'Yes' : 'No')))), /*#__PURE__*/React.createElement(PrintableRow$4, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(PrintableBox$2, {
    width: [1, 1, 1 / 3]
  }, layoutDetail$1('AIR CONDITIONING', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, car.withAC ? 'Yes' : 'No', " \xA0"))), /*#__PURE__*/React.createElement(PrintableBox$2, {
    width: [1, 1, 1 / 3]
  }, layoutDetail$1('DRIVER', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, car.displayName))), /*#__PURE__*/React.createElement(PrintableBox$2, {
    width: [1, 1, 1 / 3]
  }, layoutDetail$1('LOYALTY NUMBER', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, car.idNumber ? car.idNumber : '(None Provided)')))), car.withNavigation && /*#__PURE__*/React.createElement(PrintableRow$4, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(PrintableBox$2, {
    width: [1, 1, 1 / 3]
  }, layoutDetail$1('NAVIGATION UNIT', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, "Navigation unit reserved")))));
};
CarDetails.propTypes = {
  car: PropTypes.shape({
    carType: PropTypes.string,
    makeModel: PropTypes.string,
    mileageDisplay: PropTypes.string,
    withAC: PropTypes.bool,
    automaticTransmission: PropTypes.bool,
    displayName: PropTypes.string,
    idNumber: PropTypes.string
  })
};
CarDetails.defaultProps = {
  car: PropTypes.shape({
    carType: '',
    makeModel: '',
    mileageDisplay: '',
    withAC: false,
    automaticTransmission: false,
    displayName: '',
    idNumber: ''
  })
};

const PrintableRow$3 = styled(Flex)`
  @media print {
    flex-direction: row;
  }
`;
const PrintableCarLogo$1 = styled(Flex)`
  @media print {
    margin-bottom: 0;
    align-items: center;
  }
`;
const TotalEstimate$1 = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin-top: 2px;
`;
const dateTabletAndLarger$2 = dateString => {
  return `${dayOfWeekShortFormatter(parseDate$1(dateString))}, ${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}, ${yearFourDigitFormatter(parseDate$1(dateString))}`;
};
const dateMobile$1 = dateString => {
  return `${dayOfWeekShortFormatter(parseDate$1(dateString))}, ${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}`;
};
const isPayable = fopType => fopType !== 'G' && fopType !== 'D';
const Car = _ref => {
  let {
    item,
    cancelable,
    cancelOptions,
    bookingNumber,
    enableManageTrips,
    cancelOnlyMode
  } = _ref;
  const {
    readOnly
  } = useContext(TripDetailsContext);
  const {
    car
  } = item;
  return /*#__PURE__*/React.createElement(Card, {
    mb: 4
  }, /*#__PURE__*/React.createElement(Card.SubHeader, {
    py: "2"
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    alignItems: ['start', 'center'],
    width: "100%",
    flexDirection: ['column', 'row']
  }, /*#__PURE__*/React.createElement(DateText, null, /*#__PURE__*/React.createElement(MatchMedia.XSmallOnly, null, dateMobile$1(car.pickUp), " \u2014 ", dateMobile$1(car.dropOff)), /*#__PURE__*/React.createElement(MatchMedia.Small, null, dateTabletAndLarger$2(car.pickUp), " \u2014 ", dateTabletAndLarger$2(car.dropOff))), !readOnly && /*#__PURE__*/React.createElement(Flex, {
    pt: ['1', '0']
  }, /*#__PURE__*/React.createElement(ChangeOrCancelButton, {
    enableManageTrips: enableManageTrips,
    bookingNumber: bookingNumber,
    item: item,
    cancelable: cancelable,
    cancelOptions: cancelOptions,
    cancelOnlyMode: cancelOnlyMode
  })))), /*#__PURE__*/React.createElement(Card.Header, null, /*#__PURE__*/React.createElement(PrintableRow$3, {
    justifyContent: "space-between",
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, /*#__PURE__*/React.createElement("b", null, car.vendorName, " Car Rental"))))), /*#__PURE__*/React.createElement(Card.SubHeader, null, car.statusText ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleCase, null, "STATUS "), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px",
    darkColor: true
  }, /*#__PURE__*/React.createElement("b", null, car.statusText))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "CONFIRMATION # ")), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px",
    darkColor: true
  }, /*#__PURE__*/React.createElement("b", null, car.confirmationNumber)))), /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(PrintableRow$3, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(PrintableCarLogo$1, {
    mb: [3, 3, 0],
    flexDirection: "column",
    justifyContent: "center",
    alignItems: ['flex-start', 'flex-start', 'center'],
    width: "220px"
  }, car.vendorCode ? /*#__PURE__*/React.createElement(CarVendorLogo, {
    carCode: car.vendorCode,
    size: "120px"
  }) : /*#__PURE__*/React.createElement(Box, {
    width: "75px",
    m: "auto"
  }, /*#__PURE__*/React.createElement(IconLineCar, null)), /*#__PURE__*/React.createElement("strong", null, car.vendorName)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(TitleCase, null, "RATE"), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("h4", null, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, car.displayUnitRate)), /*#__PURE__*/React.createElement(TotalEstimate$1, null, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, "$", car.pricing.total, " ", car.pricing.currency, " TOTAL")), /*#__PURE__*/React.createElement(P, null, /*#__PURE__*/React.createElement("em", null, "(includes estimate of taxes & fees)"))))), /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(CarSchedule, {
    car: car
  })), /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(CarDetails, {
    car: car
  })), car.fop && isPayable(car.fop.fopType) && /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement("strong", null, /*#__PURE__*/React.createElement("em", null, "Rental rate payable upon pickup")))));
};
Car.propTypes = {
  bookingNumber: PropTypes.number,
  cancelable: PropTypes.bool,
  cancelOptions: PropTypes.arrayOf(PropTypes.shape({})),
  item: PropTypes.shape({
    componentId: PropTypes.string,
    cancelId: PropTypes.string,
    checkInAvailable: PropTypes.string,
    car: PropTypes.shape({
      vendorName: PropTypes.string,
      vendorCode: PropTypes.string,
      pickUp: PropTypes.string,
      dropOff: PropTypes.string,
      statusText: PropTypes.string,
      confirmationNumber: PropTypes.string,
      displayUnitRate: PropTypes.string,
      pricing: PropTypes.shape({
        total: PropTypes.number,
        currency: PropTypes.string
      }),
      fop: PropTypes.shape({
        fopType: PropTypes.string
      })
    })
  }),
  enableManageTrips: PropTypes.bool,
  cancelOnlyMode: PropTypes.bool
};
Car.defaultProps = {
  cancelable: false,
  cancelOptions: [],
  item: PropTypes.shape({
    cancelId: '',
    car: PropTypes.shape({
      vendorName: '',
      vendorCode: '',
      pickUp: '',
      dropOff: '',
      statusText: '',
      confirmationNumber: '',
      displayUnitRate: '',
      pricing: PropTypes.shape({
        total: 0,
        currency: ''
      }),
      fop: PropTypes.shape({
        fopType: ''
      })
    })
  })
};

const PrintableBox$1 = styled(Box)`
  @media print {
    width: ${_ref => {
  let {
    printWidth
  } = _ref;
  return `${printWidth || '100%'}`;
}};
  }
`;
const PrintableRow$2 = styled(Flex)`
  @media print {
    flex-direction: row;
  }
`;
const layoutDetail = (label, value) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    width: 1
  }, /*#__PURE__*/React.createElement(TitleCase, null, label)), /*#__PURE__*/React.createElement(Box, {
    width: 1,
    mb: "24px"
  }, value));
};

// NOTE: ASK STEVE ABOUT THESE MAPPINGS!
const addressMap = [['address1', 'address1'], ['street1', 'street1'], ['Address2', 'street2'], ['Address3', 'street3']];
const HotelDetails$1 = _ref2 => {
  var _hotel$rules;
  let {
    hotel
  } = _ref2;
  const iffyBox = _ref3 => {
    let [check, key] = _ref3;
    return hotel.hotelAddress[check] ? /*#__PURE__*/React.createElement(Box, {
      key: key
    }, hotel.hotelAddress[key]) : null;
  };
  const generateAddress = _ref4 => {
    let [check, key] = _ref4;
    return hotel.hotelAddress[check] ? hotel.hotelAddress[key] : null;
  };
  const mapQueryData = useMemo(() => {
    let combinedStreetAddress = addressMap.map(generateAddress).find(element => {
      return element;
    });
    combinedStreetAddress += ` ${hotel.hotelAddress.city}`;
    if (hotel.hotelAddress.state) combinedStreetAddress += `, ${hotel.hotelAddress.state}`;
    combinedStreetAddress += ` ${hotel.hotelAddress.zip}`;
    if (hotel.hotelAddress.country) combinedStreetAddress += ` ${hotel.hotelAddress.country}`;
    const delimitedString = `${hotel.hotelName} ${combinedStreetAddress}`.split(' ').join('+');
    return delimitedString;
  }, [hotel.hotelAddress, hotel.hotelName]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PrintableRow$2, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(PrintableBox$1, {
    width: [1, 1, 1 / 2],
    printWidth: "50%"
  }, layoutDetail('ADDRESS', /*#__PURE__*/React.createElement("a", {
    href: `https://maps.google.com?q=${mapQueryData}`,
    target: "_blank",
    rel: "noreferrer"
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, addressMap.map(iffyBox).find(element => {
    return element;
  }), hotel.hotelAddress.city, hotel.hotelAddress.state && /*#__PURE__*/React.createElement("span", null, ", ", hotel.hotelAddress.state), /*#__PURE__*/React.createElement("span", null, "\xA0", hotel.hotelAddress.zip), hotel.hotelAddress.country && /*#__PURE__*/React.createElement(Box, null, hotel.hotelAddress.country))))), /*#__PURE__*/React.createElement(PrintableBox$1, {
    width: [1, 1, 1 / 4],
    printWidth: "25%"
  }, layoutDetail('PHONE', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, hotel.hotelPhone ? hotel.hotelPhone : /*#__PURE__*/React.createElement("em", null, "(None Provided)")))), /*#__PURE__*/React.createElement(PrintableBox$1, {
    width: [1, 1, 1 / 4],
    printWidth: "25%"
  }, layoutDetail('OCCUPANTS', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, hotel.numAdults, " Adult", hotel.numAdults !== 1 ? 's' : '', hotel.numChildren !== 0 && hotel.numChildren && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("br", null), hotel.numChildren, " Child", hotel.numChildren !== 1 ? 'ren' : ''))))), /*#__PURE__*/React.createElement(PrintableRow$2, {
    flexDirection: ['column', 'column', 'row']
  }, hotel.displayName && /*#__PURE__*/React.createElement(PrintableBox$1, {
    width: [1, 1, 1 / 2],
    printWidth: "50%"
  }, layoutDetail('GUEST', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, hotel.displayName))), /*#__PURE__*/React.createElement(Box, {
    width: 1 / 2
  }, layoutDetail('LOYALTY #', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, hotel.loyaltyProgramNumber ? hotel.loyaltyProgramNumber : /*#__PURE__*/React.createElement("em", null, "(None Provided)"))))), hotel.shortDescription && /*#__PURE__*/React.createElement(Box, null, layoutDetail('ROOM TYPE', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, hotel.shortDescription))), ((_hotel$rules = hotel.rules) === null || _hotel$rules === void 0 ? void 0 : _hotel$rules.cancelPolicy) && /*#__PURE__*/React.createElement(Box, null, layoutDetail('CANCEL POLICY', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, hotel.rules.cancelPolicy))), hotel.paymentDescription && /*#__PURE__*/React.createElement(Box, null, layoutDetail('PAYMENT', /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, hotel.paymentDescription))));
};
HotelDetails$1.propTypes = {
  hotel: PropTypes.shape({
    hotelAddress: PropTypes.shape({
      address1: PropTypes.string,
      street1: PropTypes.string,
      street2: PropTypes.string,
      street3: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
      country: PropTypes.string
    }),
    shortDescription: PropTypes.string,
    paymentDescription: PropTypes.string,
    hotelName: PropTypes.string,
    hotelPhone: PropTypes.string,
    numAdults: PropTypes.number,
    numChildren: PropTypes.number,
    displayName: PropTypes.string,
    loyaltyProgramNumber: PropTypes.string,
    rules: PropTypes.shape({
      cancelPolicy: PropTypes.string
    }),
    fop: PropTypes.shape({
      fopType: PropTypes.string
    })
  })
};
HotelDetails$1.defaultProps = {
  hotel: PropTypes.shape({
    hotelAddress: PropTypes.shape({
      address1: '',
      street1: '',
      street2: '',
      street3: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    }),
    hotelName: '',
    hotelPhone: '',
    numAdults: 0,
    numChildren: 0,
    displayName: '',
    loyaltyProgramNumber: '',
    rules: PropTypes.shape({
      cancelPolicy: ''
    }),
    fop: PropTypes.shape({
      fopType: ''
    })
  })
};

const EMAIL_PATTERN =
// eslint-disable-next-line
/^[a-z0-9\u007F-\uffff!#$%&'*+/=?^_`{|}~-]+(?:.[a-z0-9\u007F-\uffff!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?.)+[a-z]{2,}$/gi;
const vCardWidth = 342;
const vCardHeight = 192;
const RadioStyled = styled(RadioButton)`
  padding: 0 0 5px 0;
`;
const RadioButtonWrapper = styled(Flex)`
  width: 150px;
  justify-content: space-between;
`;
const CardImagesCont = styled(Flex)`
  flex-direction: column;
  width: ${vCardWidth}px;
  gap: 12px;
  margin: 0 auto 16px auto;
`;
const ImageWrapper = styled.div`
  width: ${vCardWidth}px;
  min-height: ${vCardHeight}px;
  background-color: lightgray;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: ${vCardWidth}px;
    object-fit: contain;
  }
`;
const Hint = styled(Flex)`
  flex-direction: column;
  justify-content: flex-end;
  height: 34px;
  font-size: 13px;
  color: grey;
  margin: 0 0 6px 0;
  line-height: 16px;
`;
const FaxOrEmailInput = styled(Flex)`
  flex-direction: column;
  margin-top: 18px;
`;
const DangerNotice = styled(Flex)`
  height: 100%;
  align-items: center;
  justify-content: center;
`;
const DangerNoticeSmall = styled(Flex)`
  height: 14px;
  margin-top: 4px;
  font-size: 14px;
  color: ${getThemeProp('global.danger--color')};
`;
const SubmitAlertCont = styled(Flex)`
  width: 100%;
  align-items: flex-end;
  font-weight: 600;
`;
const SubmitSuccess = styled.div`
  color: ${getThemeProp('global.success--color')};
  margin-left: auto;
`;
const SubmitFail = styled.div`
  color: ${getThemeProp('global.danger--color')};
  margin-left: auto;
`;
const VCardImages = _ref => {
  let {
    pnrId
  } = _ref;
  const [isLoading, setLoading] = useState(true);
  const [imgData, setImgData] = useState(null);
  const [imgError, setImgError] = useState(false);
  const {
    getHotelVCardImages,
    getJsr
  } = useAppApi();
  useEffect(() => {
    const jsr = getJsr();
    const vCardQuery = `nameNum=${jsr.nameNum}&bookNum=${jsr.bn}&pnrId=${pnrId}`;
    getHotelVCardImages(vCardQuery).then(res => {
      setImgData(res.data);
      setLoading(false);
    }).catch(err => {
      console.log(err);
      setImgError(true);
      setLoading(false);
    });
  }, []);
  const imgIsUrl = Boolean((imgData === null || imgData === void 0 ? void 0 : imgData.cardFrontURL) || (imgData === null || imgData === void 0 ? void 0 : imgData.cardBackURL));
  return /*#__PURE__*/React.createElement(CardImagesCont, null, isLoading && /*#__PURE__*/React.createElement(InlineLoader, {
    withoutMinHeight: true
  }), imgError && /*#__PURE__*/React.createElement(DangerNotice, null, /*#__PURE__*/React.createElement(Notice, {
    kind: "danger"
  }, "Something went wrong. Please try again.")), imgData && (imgIsUrl ? /*#__PURE__*/React.createElement(React.Fragment, null, Boolean(imgData.cardFrontURL) && /*#__PURE__*/React.createElement(ImageWrapper, null, /*#__PURE__*/React.createElement(Image, {
    width: vCardWidth,
    alt: "Virtual Card Front",
    src: `${imgData.cardFrontURL}`
  })), Boolean(imgData.cardBackURL) && /*#__PURE__*/React.createElement(ImageWrapper, null, /*#__PURE__*/React.createElement(Image, {
    width: vCardWidth,
    alt: "Virtual Card Back",
    src: `${imgData.cardBackURL}`
  }))) : /*#__PURE__*/React.createElement(ImageWrapper, null, /*#__PURE__*/React.createElement(Image, {
    width: vCardWidth,
    alt: "Virtual Card Front Data",
    src: `data:image/png;base64,${imgData.cardFrontData}`
  }))));
};
VCardImages.propTypes = {
  pnrId: PropTypes.number
};
VCardImages.defaultProps = {
  pnrId: null
};
const HotelVirtualCard = _ref2 => {
  let {
    hotel
  } = _ref2;
  const hideResendSection = hotel.enableSendHotelEmail === false && hotel.enableSendHotelFax === false;
  const sendEmailOnly = hotel.enableSendHotelEmail === true && hotel.enableSendHotelFax === false;
  const sendFaxOnly = hotel.enableSendHotelEmail === false && hotel.enableSendHotelFax === true;
  const {
    getJsr
  } = useAppApi();
  const JSR = getJsr();
  const api = useContext(TripDetailsContext);
  const [isOpen, setIsOpen] = useState(false);
  const [selection, setSelection] = useState(sendFaxOnly ? 'Fax' : 'Email');
  const radioHandler = _ref3 => {
    let {
      target
    } = _ref3;
    return setSelection(target.value);
  };
  const [faxNumber, setFaxNumber] = useState('');
  const [faxValid, setFaxValid] = useState(true);
  const faxNumberFormatted = faxNumber.replace(/-|\s/g, '');
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(true);
  const [inProgress, setInProgress] = useState(false);
  const [postStatus, setPostStatus] = useState('default');
  const valueToSend = () => {
    if (selection === 'Fax') {
      return faxNumberFormatted;
    }
    if (selection === 'Email') {
      return email;
    }
    return '';
  };
  const openModal = () => {
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
    setPostStatus('default');
    setEmail('');
    setFaxNumber('');
    setEmailValid(true);
    setFaxValid(true);
  };
  const postSubmit = () => {
    setInProgress(true);
    const payload = {
      nameNum: JSR.nameNum,
      bookNum: JSR.bn,
      type: selection,
      to: valueToSend(),
      pnrId: hotel.pnrId
    };
    api.postVirtualCardInfo(payload).then(data => {
      setPostStatus('submitted');
      setInProgress(false);
      return data;
    }).catch(err => {
      console.log(err);
      setPostStatus('fail');
      setInProgress(false);
    });
  };
  const emailValidCheck = value => {
    if (value.match(EMAIL_PATTERN)) {
      setEmailValid(true);
      postSubmit();
    } else {
      setEmailValid(false);
    }
    return '';
  };
  const faxValidCheck = value => {
    if (value.match(/^\d+$/) && value.length > 9) {
      setFaxValid(true);
      postSubmit();
    } else {
      setFaxValid(false);
    }
    return '';
  };
  const handleSubmit = () => {
    if (selection === 'Email') {
      emailValidCheck(email);
    }
    if (selection === 'Fax') {
      faxValidCheck(faxNumberFormatted);
    }
  };
  function postStatusMsg(st) {
    switch (st) {
      case 'fail':
        return /*#__PURE__*/React.createElement(SubmitFail, {
          ml: "auto"
        }, "Something went wrong. Please try again.");
      case 'submitted':
        return /*#__PURE__*/React.createElement(SubmitSuccess, {
          ml: "auto"
        }, "Your request has been submitted, please allow a few minutes for processing.");
      case 'default':
        return null;
      default:
        return null;
    }
  }
  function footerButtons(st) {
    switch (st) {
      case 'submitted':
        return /*#__PURE__*/React.createElement(Button, {
          onClick: closeModal,
          text: "Close"
        });
      default:
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
          onClick: closeModal,
          text: "Cancel",
          kind: "danger",
          bordered: true
        }), /*#__PURE__*/React.createElement(Box, {
          ml: 2
        }, /*#__PURE__*/React.createElement(Button, {
          onClick: handleSubmit,
          text: "Send",
          isLoading: inProgress
        })));
    }
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    iconPrefix: /*#__PURE__*/React.createElement(IconCreditCard, null),
    text: "View Virtual Card",
    onClick: openModal
  }), isOpen && /*#__PURE__*/React.createElement(Modal, {
    onClose: closeModal
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Virtual Card"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(VCardImages, {
    pnrId: hotel.pnrId
  }), !hideResendSection ? /*#__PURE__*/React.createElement(Box, {
    width: [1, '335px'],
    margin: "auto"
  }, /*#__PURE__*/React.createElement(Box, {
    mb: 2
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, /*#__PURE__*/React.createElement("b", null, "Re-send to Hotel"))), /*#__PURE__*/React.createElement(RadioButtonWrapper, null, sendFaxOnly ? null : /*#__PURE__*/React.createElement(RadioStyled, {
    label: "Email",
    checked: selection === 'Email',
    name: "options",
    onChange: radioHandler,
    value: "Email",
    id: "2",
    key: 2
  }), sendEmailOnly ? null : /*#__PURE__*/React.createElement(RadioStyled, {
    label: "Fax",
    checked: selection === 'Fax',
    name: "options",
    onChange: radioHandler,
    value: "Fax",
    id: "1",
    key: 1
  })), /*#__PURE__*/React.createElement(FaxOrEmailInput, {
    width: [1, '335px']
  }, selection === 'Fax' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Hint, null, /*#__PURE__*/React.createElement("em", null, "Enter Fax Number with International Dial Code."), "Example: (US & Canada) 12139559000", /*#__PURE__*/React.createElement("br", null), "(UK, etc.) 441614453941"), /*#__PURE__*/React.createElement(TextInput, {
    value: faxNumber,
    onChange: e => {
      setFaxNumber(e.target.value);
      setPostStatus('default');
    },
    kind: faxValid ? 'primary' : 'danger'
  }), /*#__PURE__*/React.createElement(DangerNoticeSmall, null, faxValid ? null : 'This Fax number is not valid.')) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Hint, null, "Example: email@website.com"), /*#__PURE__*/React.createElement(TextInput, {
    value: email,
    onChange: e => {
      setEmail(e.target.value);
      setPostStatus('default');
    },
    kind: emailValid ? 'primary' : 'danger'
  }), /*#__PURE__*/React.createElement(DangerNoticeSmall, null, emailValid ? null : 'This email is not valid.')))) : null, /*#__PURE__*/React.createElement(SubmitAlertCont, null, postStatusMsg(postStatus))), !hideResendSection ? /*#__PURE__*/React.createElement(Modal.Footer, null, footerButtons(postStatus)) : null));
};
HotelVirtualCard.propTypes = {
  hotel: PropTypes.shape({
    enableSendHotelEmail: PropTypes.bool,
    enableSendHotelFax: PropTypes.bool,
    enableVirtualCardImage: PropTypes.bool,
    pnrId: PropTypes.number
  })
};
HotelVirtualCard.defaultProps = {
  hotel: PropTypes.shape({
    enableSendHotelEmail: false,
    enableSendHotelFax: false,
    enableVirtualCardImage: false
  })
};

const PrintableBox = styled(Box)`
  @media print {
    width: ${_ref => {
  let {
    printWidth
  } = _ref;
  return `${printWidth || '100%'}`;
}};
  }
`;
const PrintableRow$1 = styled(Flex)`
  @media print {
    flex-direction: row;
  }
`;
const TotalEstimate = styled.div`
  font-weight: 600;
  font-size: 15px;
  margin-top: 2px;
`;
const TaxAndFees = styled.div`
  font-size: 16px;
  text-transform: uppercase;
  margin: 2px 0 8px 0;
`;
const TaxAndFeesTooltip = styled.span`
  border-bottom: 1px dotted;
  cursor: pointer;
`;
const VCardButtonCont = styled(Flex)`
  width: 100%;
  justify-content: flex-end;
  margin-top: -34px;
`;
const dateTabletAndLarger$1 = dateString => {
  return `${dayOfWeekShortFormatter(parseDate$1(dateString))}, ${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}, ${yearFourDigitFormatter(parseDate$1(dateString))}`;
};
const dateMobile = dateString => {
  return `${dayOfWeekShortFormatter(parseDate$1(dateString))}, ${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}`;
};
const removeProtocol = url => {
  if (url) {
    return url.replace('http:', '');
  }
  return url;
};
const Hotel = _ref2 => {
  let {
    item,
    cancelable,
    cancelOptions,
    bookingNumber,
    enableManageTrips,
    cancelOnlyMode
  } = _ref2;
  const {
    readOnly
  } = useContext(TripDetailsContext);
  const {
    hotel
  } = item;
  return /*#__PURE__*/React.createElement(Card, {
    mb: 4
  }, /*#__PURE__*/React.createElement(Card.SubHeader, {
    py: "2"
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    alignItems: ['start', 'center'],
    width: "100%",
    flexDirection: ['column', 'row']
  }, /*#__PURE__*/React.createElement(DateText, null, /*#__PURE__*/React.createElement(MatchMedia.XSmallOnly, null, dateMobile(hotel.arrives), " \u2014 ", dateMobile(hotel.departs)), /*#__PURE__*/React.createElement(MatchMedia.Small, null, dateTabletAndLarger$1(hotel.arrives), " \u2014 ", dateTabletAndLarger$1(hotel.departs))), !readOnly && /*#__PURE__*/React.createElement(Flex, {
    pt: ['1', '0']
  }, /*#__PURE__*/React.createElement(ChangeOrCancelButton, {
    enableManageTrips: enableManageTrips,
    bookingNumber: bookingNumber,
    item: item,
    cancelable: cancelable,
    cancelOptions: cancelOptions,
    cancelOnlyMode: cancelOnlyMode
  })))), /*#__PURE__*/React.createElement(Card.Header, null, /*#__PURE__*/React.createElement(PrintableRow$1, {
    justifyContent: "space-between",
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, /*#__PURE__*/React.createElement("b", null, hotel.hotelName))))), hotel.confirmationNumber && /*#__PURE__*/React.createElement(Card.SubHeader, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "CONFIRMATION #")), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px",
    darkColor: true
  }, /*#__PURE__*/React.createElement("b", null, hotel.confirmationNumber)))), hotel.statusText && /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TitleCase, null, "STATUS"), /*#__PURE__*/React.createElement("span", null, hotel.statusText))), /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(PrintableRow$1, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(Box, {
    pb: [3, 3, 0],
    mr: 3
  }, /*#__PURE__*/React.createElement(HotelThumbnail, {
    src: removeProtocol(hotel.thumbNail),
    size: "large"
  })), /*#__PURE__*/React.createElement(PrintableBox, {
    width: [1, 1, 3 / 4],
    printWidth: "75%"
  }, /*#__PURE__*/React.createElement(TitleCase, null, "RATE"), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "24px"
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: hotel.averageNightlyRateLocalCurrency
  })), /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    lineHeight: 1.2,
    weight: "400",
    size: 18,
    mt: "6px"
  }, "\xA0", hotel.pricing.currency, "/ NIGHT")), /*#__PURE__*/React.createElement(TaxAndFees, null, hotel.additionalTaxAndFeeApply ? ', EXCLUDING TAX' : /*#__PURE__*/React.createElement(Tooltip
  // eslint-disable-next-line max-len
  , {
    content: "The taxes are tax recovery charges paid to vendors (e.g., hotels); for details, please see our Terms of Use. Service fees are retained as compensation in servicing your booking and may include fees charged by vendors."
  }, "+ $", hotel.taxAndFees, " ", /*#__PURE__*/React.createElement(TaxAndFeesTooltip, null, hotel.taxAndFeesLabel))), /*#__PURE__*/React.createElement(TotalEstimate, null, /*#__PURE__*/React.createElement(Currency, {
    amount: hotel.pricing.total
  }), "\xA0", hotel.pricing.currency, "\xA0", hotel.settleWithAmtrav ? 'TOTAL' : 'ESTIMATED TOTAL FOR STAY'), hotel.enableVirtualCardImage && !readOnly ? /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(VCardButtonCont, null, /*#__PURE__*/React.createElement(HotelVirtualCard, {
    hotel: hotel
  }))) : null))), hotel.enableVirtualCardImage && !readOnly ? /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(HotelVirtualCard, {
    hotel: hotel
  }))) : null, /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(HotelDetails$1, {
    hotel: hotel
  })));
};
Hotel.propTypes = {
  bookingNumber: PropTypes.number,
  cancelable: PropTypes.bool,
  cancelOptions: PropTypes.arrayOf(PropTypes.shape({})),
  enableManageTrips: PropTypes.bool,
  cancelOnlyMode: PropTypes.bool,
  item: PropTypes.shape({
    componentId: PropTypes.string,
    cancelId: PropTypes.string,
    hotel: PropTypes.shape({
      enableVirtualCardImage: PropTypes.bool,
      hotelName: PropTypes.string,
      arrives: PropTypes.string,
      departs: PropTypes.string,
      confirmationNumber: PropTypes.string,
      statusText: PropTypes.string,
      thumbNail: PropTypes.string,
      averageNightlyRateLocalCurrency: PropTypes.number,
      pricing: PropTypes.shape({
        currency: PropTypes.string,
        total: PropTypes.number
      }),
      additionalTaxAndFeeApply: PropTypes.bool,
      settleWithAmtrav: PropTypes.bool,
      taxAndFees: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      taxAndFeesLabel: PropTypes.string
    })
  })
};
Hotel.defaultProps = {
  cancelable: false,
  cancelOptions: [],
  item: PropTypes.shape({
    enableVirtualCardImage: false,
    cancelId: '',
    hotel: PropTypes.shape({
      hotelName: '',
      arrives: '',
      departs: '',
      confirmationNumber: '',
      statusText: '',
      thumbNail: '',
      averageNightlyRateLocalCurrency: 0,
      pricing: PropTypes.shape({
        currency: '',
        total: 0
      }),
      additionalTaxAndFeeApply: false,
      settleWithAmtrav: false
    })
  })
};

const GroundCar = () => {
  return /*#__PURE__*/React.createElement("svg", {
    style: {
      width: '100%',
      height: '100%'
    },
    viewBox: "0 0 48 19"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m7.49,7.44H3.12c-.13,0-.26-.1-.26-.26h0c0-.13.1-.26.26-.26h4.37c.13,0,.26.1.26.26h0c0,.13-.13.26-.26.26Zm1.27,1.09h0c0-.16-.13-.26-.26-.26h-4.47c-.16,0-.26.13-.26.26h0c0,.16.13.26.26.26h4.5c.13,0,.23-.13.23-.26Zm-2.58,1.42h0c0-.16-.13-.26-.26-.26H1.26c-.16,0-.26.13-.26.26h0c0,.16.13.26.26.26h4.65c.13,0,.23-.1.26-.26Zm2.3,1.4h0c0-.16-.13-.26-.26-.26H3.14c-.16,0-.26.13-.26.26h0c0,.16.13.26.26.26h5.07c.13,0,.26-.1.26-.26Zm31.79-.93c1.47,0,2.69,1.21,2.69,2.69s-1.21,2.69-2.69,2.69-2.69-1.21-2.69-2.69,1.19-2.69,2.69-2.69Zm0,4.37c.93,0,1.68-.75,1.68-1.68s-.75-1.68-1.68-1.68-1.68.75-1.68,1.68.75,1.68,1.68,1.68Zm-30-4.24l.26-.08c.03-.93.1-1.83.23-2.69.08-.49.28-.62.72-.7,1.19-.21,2.82-.41,4.52-.57,1.32-1.11,2.58-1.99,3.9-2.79.49-.31,1.03-.52,1.63-.57,1.42-.1,2.56-.16,4.57-.16,1.37,0,2.64.03,4.06.16.78.08,1.32.23,1.96.57,2.07,1.06,3.64,2.04,5.45,3.26,2.48.23,4.68.54,6.69.93,1.01.18,1.76,1.16,1.96,2.51.57.18.78.49.78,1.37,0,1.34-.39,2.69-1.11,2.69h-2.38c.7-2.71-.7-4.73-3.26-4.73s-3.98,2.04-3.26,4.73h-15.07c.7-2.71-.7-4.73-3.26-4.73s-3.95,1.99-3.26,4.68l-4.29-.26c-.62-.03-1.21-1.06-1.21-2.4,0-.67.08-1.11.34-1.21Zm17.75-2.84h7.52v-1.03c-1.32-.98-2.61-1.81-3.85-2.43-.47-.23-.96-.34-1.52-.41-.65-.1-1.47-.13-2.71-.16l.57,4.03Zm-6.59,0h5.25l-.57-4.06c-2.04,0-3.44.08-4.34.26-.28.05-.57.18-.83.36l.49,3.44Zm-2.07,0h1.4l-.41-2.92c-.26.28-.52.57-.72.85-.34.39-.65.88-.93,1.4l.67.67Zm-.7,2.71c1.47,0,2.69,1.21,2.69,2.69s-1.21,2.69-2.69,2.69-2.69-1.21-2.69-2.69,1.21-2.69,2.69-2.69Zm.03,4.37c.93,0,1.68-.75,1.68-1.68s-.75-1.68-1.68-1.68-1.68.75-1.68,1.68.75,1.68,1.68,1.68Z",
    fill: "#cdcbcc" // eslint-disable-line
  }));
};
const GroundLimo = () => {
  return /*#__PURE__*/React.createElement("svg", {
    style: {
      width: '100%',
      height: '100%'
    },
    viewBox: "0 0 48 19"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m6.79,7.74h-3.76c-.13,0-.21-.1-.21-.21h0c0-.13.1-.21.21-.21h3.76c.13,0,.21.1.21.21h0c0,.13-.1.21-.21.21Zm1.08.98h0c0-.1-.08-.21-.21-.21h-3.86c-.1,0-.21.08-.21.21h0c0,.1.08.21.21.21h3.86c.1,0,.21-.1.21-.21Zm-2.24,1.24h0c0-.1-.08-.21-.21-.21H1.4c-.1,0-.21.08-.21.21h0c0,.1.08.21.21.21h4.02c.1,0,.21-.1.21-.21Zm1.96,1.19h0c0-.1-.08-.21-.21-.21H3.03c-.1,0-.21.08-.21.21h0c0,.1.08.21.21.21h4.38c.1,0,.21-.1.18-.21Zm33.62-.77c1.29,0,2.32,1.06,2.32,2.32s-1.06,2.32-2.32,2.32-2.32-1.06-2.32-2.32,1.06-2.32,2.32-2.32Zm0,3.76c.8,0,1.44-.64,1.44-1.44s-.64-1.44-1.44-1.44-1.44.64-1.44,1.44.64,1.44,1.44,1.44Zm-25.45-3.76c1.29,0,2.32,1.06,2.32,2.32s-1.06,2.32-2.32,2.32-2.32-1.06-2.32-2.32,1.06-2.32,2.32-2.32Zm0,3.76c.8,0,1.44-.64,1.44-1.44s-.64-1.44-1.44-1.44-1.44.64-1.44,1.44.64,1.44,1.44,1.44Zm-7.21-3.63l.18-.08c.03-.8.08-1.6.21-2.32.05-.41.26-.52.62-.59,1.03-.18,2.42-.36,3.89-.49,1.13-.95,2.22-1.7,3.35-2.4.41-.26.88-.44,1.39-.49,1.24-.1,3.58-.13,7.68-.13,3.61,0,5.46.03,6.7.08.67.08,1.11.21,1.67.49,1.78.93,3.12,1.75,4.69,2.81,2.14.21,4.02.46,5.74.8.88.18,1.49,1,1.67,2.16.49.13.67.39.67,1.16,0,1.16-.33,2.32-.95,2.32h-2.04c.62-2.32-.59-4.04-2.78-4.04s-3.4,1.73-2.78,4.04h-19.89c.62-2.32-.59-4.04-2.78-4.04s-3.4,1.73-2.81,4.02l-3.71-.21c-.54-.03-1.03-.9-1.03-2.06,0-.59.08-.95.31-1.03Zm22.21-2.45h6.44v-.9c-1.16-.85-2.27-1.55-3.32-2.09-.39-.21-.82-.28-1.31-.36-.39-.05-1.11-.08-2.32-.1l.52,3.45Zm-6.98,0h5.77l-.49-3.45h-5.77l.49,3.45Zm-6.83,0h5.67l-.49-3.45c-2.04.05-3.19.1-3.71.21-.49.08-.9.39-1.24.75-.15.18-.31.36-.49.57l.26,1.93Z" // eslint-disable-line
    ,
    fill: "#cdcbcc"
  }));
};
const GroundSUV = () => {
  return /*#__PURE__*/React.createElement("svg", {
    style: {
      width: '100%',
      height: '100%'
    },
    viewBox: "0 0 48 19"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m8.03,7.87H3.3c-.16,0-.26-.13-.26-.26h0c0-.16.13-.26.26-.26h4.73c.16,0,.26.13.26.26h0c0,.13-.13.26-.26.26Zm1.09,1.45h-4.84c-.16,0-.26-.13-.26-.26h0c0-.16.13-.26.26-.26h4.86c.16,0,.26.13.26.26h0c0,.16-.13.26-.28.26Zm-2.82,1.55H1.26c-.16,0-.26-.13-.26-.26h0c0-.16.13-.26.26-.26h5.04c.16,0,.26.13.26.26h0c.03.16-.1.26-.26.26Zm2.51,1.53H3.3c-.16,0-.26-.13-.26-.26h0c0-.16.13-.26.26-.26h5.48c.16,0,.26.13.26.26h0c.03.13-.1.26-.23.26Zm5.02-8.09h0s-.7-.65-.31-.72,2.17-.1,2.17-.1h0c-.7.16-1.29.41-1.86.83Zm12.1-1.11c-1.06-.03-7.96-.03-9.98.26.08-.08.34-.23,1.06-.28.98-.08,8.71-.23,9.77-.13.62.05.96.16,1.14.23-.8-.03-1.55-.05-1.99-.08Zm14.71,13.78c1.76.21,3.36-1.03,3.59-2.79.21-1.76-1.03-3.36-2.79-3.59-1.76-.21-3.36,1.03-3.59,2.79-.21,1.78,1.03,3.39,2.79,3.59Zm-1.76-3.13h0Zm.03-.47h0Zm3.75,1.45l-.88-.88s-.05-.08-.05-.1v-.05c0-.05.03-.08.05-.1l1.01-.7c.13.31.21.67.16,1.03-.03.28-.13.57-.28.8Zm.18.18h0Zm-.08-2.53h0Zm-1.32.67s-.05.03-.08.03h-.05s-.03,0-.03-.03c-.05-.03-.08-.05-.1-.1l-.34-1.16c.16-.03.28-.03.44,0,.52.05.96.34,1.27.7l-1.11.57Zm-.62,2.56c-.08,0-.18-.03-.26-.05l.57-1.11s.05-.08.1-.08h.1s.05,0,.08.03l.98.75c-.47.34-1.01.52-1.58.47Zm-.28-2.38l-.03.03s-.08.05-.13.05l-1.22-.03c.13-.65.59-1.19,1.19-1.4l.21,1.24s0,.08-.03.1Zm-.03.98l-.41,1.16c-.57-.31-.93-.91-.96-1.58l1.24.18c.05,0,.08.03.1.08l.03.03v.13Zm2.69-.26h0Zm-26.22,2.92c1.76.21,3.36-1.03,3.59-2.79.21-1.76-1.03-3.36-2.79-3.59-1.76-.21-3.36,1.03-3.59,2.79s1.03,3.39,2.79,3.59Zm-1.76-3.13h0Zm.03-.47h0Zm3.75,1.45l-.88-.88c-.05-.03-.05-.05-.05-.1v-.05c0-.05.03-.08.05-.1l1.01-.7c.13.31.21.67.16,1.03-.03.28-.13.57-.28.8Zm.16.18h0Zm-.08-2.53h0Zm-1.32.67s-.05.03-.08.03h-.05s-.03,0-.03-.03c-.05-.03-.08-.05-.1-.1l-.34-1.16c.16-.03.28-.03.44,0,.52.05.96.34,1.27.7l-1.11.57Zm-.62,2.56c-.08,0-.18-.03-.26-.05l.57-1.11s.05-.08.1-.08h.1s.05,0,.08.03l.98.75c-.44.34-1.01.52-1.58.47Zm-.28-2.38l-.03.03s-.08.05-.13.05h-1.22c.13-.65.59-1.19,1.19-1.4l.21,1.24s0,.05-.03.08Zm-.03.98l-.41,1.16c-.57-.31-.93-.91-.96-1.58l1.24.18c.05,0,.08.03.1.08l.03.03s.03.08,0,.13Zm2.69-.26h0Zm13.65-7.47c-.03-.26.08-.85.1-.88s.21-.1.75-.03c.54.08.47,1.06.47,1.06h.05s.03,0,.03.03l.16.03c.05,0,.08.05.05.08-.05.16-.26.49-.83.47-.13-.13-.16-.16-.18-.18h.03l.34-.13h0l.36-.1h.03l-.36.1h0l-.34.13h-.08c-.1-.05-.44-.23-.49-.23-.08-.05-.1-.1-.08-.34Zm-23.06,5.59c.03.23.18,1.06.57,1.27.36.21,1.78.52,2.43.67.08.03.18-.03.21-.1l1.24-3.13c.31-.65.98-1.03,1.68-1.01l2.38.08c.78.03,1.45.54,1.71,1.27l1.03,3.21c.03.08.1.13.18.13l15.15.1c.05,0,.13-.03.16-.1l1.4-3.49c.31-.65.98-1.03,1.68-1.01l2.38.08c.78.03,1.45.54,1.71,1.27l1.06,3.23c.03.1.16.18.28.13l1.4-.44c.08-.03.13-.08.16-.16v-.03c.03-.1,0-.21-.08-.28l-.1-.08c-.08-.08-.1-.18-.05-.28l.31-.75s.03-.08.03-.13l-.1-1.42c0-.05-.03-.08-.05-.13-.1-.13-.34-.47-.47-.88-.13-.52-.34-1.09-.49-1.37-.16-.28-.62-.75-1.32-.93-.67-.18-6.83-.8-6.83-.8,0,0-.85-.05-1.81-.65-.96-.62-3.78-2.4-5.12-2.77-1.34-.36-3.88-.47-4.94-.52-1.06-.03-8.22-.03-10.08.28-1.81.28-3,1.58-5.02,3.65-.1.1-.16.26-.18.39-.03.34-.05,1.01,0,1.73,0,.23-.03.47-.13.67-.23.44-.26.96-.23,1.76,0,.28-.1.39-.16.41.03.08,0,.1.03.13Zm34.11-3.44c.31-.1,1.24.03,1.6.08,0,0,0,.03.03.03.16.28.34.8.49,1.32-.23-.05-.54-.1-.85-.16-.57-.1-.67-.26-.67-.26,0,0-.8-.98-.59-1.01ZM27.01,3.81c1.5.03,3.46.05,6.1,1.78v-.03c.05-.05.16-.1.47-.1.13,0,.28,0,.44.03.52.05.65.65.65,1.09h.05c.08,0,.16.05.18.13.05.08.05.16.03.23-.05.18-.28.57-.91.57h-.18l-.21-.21h-.03c-.16-.08-.28-.16-.36-.18v.1l-5.66-.21-.57-3.21Zm-6.23,1.53c0-.1,0-.18.05-.28l.44-1.19c1.47-.05,3.1-.08,4.27-.08h.31l.18,3.15-5.2-.18-.05-1.42Zm-6.52.93l2.04-1.91s.34-.28,1.32-.34c.41-.03,1.63-.08,3.08-.13l-1.22,2.82-5.17-.18c-.08,0-.13-.08-.13-.08.03-.05,0-.13.08-.18Zm-3.52,1.27c0-.13.05-.26.16-.36h1.89c.18,0,.28.21.18.36l-.91,1.11c-.05.08-.16.13-.23.13l-1.11.03c-.03-.57,0-1.01.03-1.27Z" // eslint-disable-line
    ,
    fill: "#cdcbcc"
  }));
};
const GroundVan = () => {
  return /*#__PURE__*/React.createElement("svg", {
    style: {
      width: '100%',
      height: '100%'
    },
    viewBox: "0 0 48 19"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m41.51,12.39c-1.33-.56-2.9.05-3.49,1.39-.18.41-.23.85-.21,1.26.08.95.67,1.82,1.59,2.21,1.33.56,2.9-.05,3.49-1.39.13-.28.18-.54.21-.82.1-1.08-.51-2.18-1.59-2.64Zm-1.8,3.88h0c-.05-.03-.08-.05-.13-.08h0c-.1-.05-.18-.13-.28-.23h0s-.05-.05-.08-.1c-.03-.03-.03-.05-.05-.05t-.05-.05h0s-.03-.05-.05-.08c-.03-.03-.03-.05-.05-.08h0s-.03-.05-.03-.05h0s-.03-.05-.05-.1h0s-.03-.05-.03-.1h0s0-.03-.03-.05v-.33l.26-.1h0v-.46s0-.03.03-.05v-.03s0-.03.03-.05h0s.03-.05.03-.05c0-.03.03-.03.03-.05v-.05s.03-.05.03-.05l-.18-.21s.03-.03.05-.05l.03-.03s.03-.03.03-.05h0l.03-.03h0l.03-.03h0l.03-.03h0l.23.13h0l.03-.03.03-.03.03-.03s.03,0,.03-.03l.03-.03h.03s.03-.03.05-.03h.03s.03,0,.03-.03h.03s.03,0,.03-.03h.03c.05-.03.08-.03.13-.05h0s.03,0,.05-.03h0c.05-.03.13-.03.18-.05h.05l.05-.26h.59s.03,0,.05.03h.1s.03.03.05.03.03.03.05.03h.03s.03,0,.03.03h.03s.03,0,.03.03h0s.03.03.05.03h.03s.03,0,.03.03c0,0,.03,0,.03.03,0,0,.03,0,.03.03,0,0,.03,0,.03.03h.03s.03.03.05.03h.03l.03.03h0l.03.03h0s.05.05.08.08h0l.03.03h0s.05.05.08.1h0s.03.05.05.08c.03.03.03.05.05.08h0l.03.03v.05s0,.03.03.03v.03s0,.03.03.03v.03s.03.03.03.05h0s0,.03.03.03h0v.03h0v.03h0s0,.05.03.05v.64h0v.03s0,.05-.03.08v.05s-.03.05-.03.08,0,.03-.03.05h0s0,.03-.03.05l-.28-.03c-.05.1-.1.21-.18.28h0s-.05.08-.08.1h0l-.03.03h0s-.03.03-.05.05h-.03l-.03.03h-.03s-.05.03-.08.05l.08.26h0s-.05.03-.08.03h0s-.05.03-.08.03h0s-.05.03-.08.03h0s-.05.03-.08.03l-.15-.23h0c-.05.03-.13.03-.18.03h-.51s-.03,0-.05-.03h0l-.15.21h-.03s-.03,0-.03-.03c-.03,0-.03-.03-.05-.03h0c-.03.26-.05.26-.05.26Zm-21.18-3.88c-1.33-.56-2.9.05-3.49,1.39-.18.41-.23.85-.21,1.26.08.95.67,1.82,1.59,2.21,1.33.56,2.9-.05,3.49-1.39.13-.28.18-.54.21-.82.1-1.08-.51-2.18-1.59-2.64Zm-2.23,2.44c0-.64.51-1.16,1.16-1.16s1.16.51,1.16,1.16-.51,1.16-1.16,1.16c-.64.03-1.16-.49-1.16-1.16Zm29.7-.13v-1.85c0-.15-.13-.28-.28-.28h-.08v-1.08h.05c.08,0,.13-.05.13-.13v-1.1c0-.08-.05-.13-.13-.13h-.05v-.03c0-.28-.13-.56-.33-.77l-1.39-1.26-5.26-4.77-.36-.31-.51-.46-.44-.41c-.08-.08-.15-.13-.23-.21-.51-.39-1.16-.62-1.82-.64-9.75-.39-19.3-.05-21.85.05-.39.03-.72.23-.9.56l-.05.1-.36.74-.21.41c-.08.15-.13.33-.18.49-.05.21-.08.41-.08.62v6.31h-.05c-.08,0-.15.08-.15.15v1.28c0,.08.08.15.15.15h.05v.44h-.03c-.15,0-.28.13-.28.28v2.1c0,.15.13.28.28.28h1.98c.15,0,.28-.08.36-.18h.13c.18,0,.33-.15.36-.33.03-.36.1-.72.23-1.08.31-.74.9-1.31,1.67-1.62.36-.15.74-.21,1.13-.21.41,0,.8.08,1.18.26.74.31,1.31.9,1.62,1.67.03.03.03.08.05.1v.03s.03.05.03.08,0,.03.03.05c0,.03.03.05.03.08v.03h0s.03.08.03.1v.03s.03.08.03.1v.03s0,.05.03.1v.26c0,.18.15.33.36.33h16.22c.18,0,.33-.15.36-.33v-.31s0-.08.03-.1v-.03c0-.05.03-.08.03-.13h0s0-.05.03-.08v-.03s0-.03.03-.05c0-.03.03-.05.03-.08s0-.03.03-.05c0-.03.03-.05.03-.08s0-.03.03-.05c.03-.05.03-.08.05-.13.49-1.16,1.62-1.82,2.77-1.82.39,0,.8.08,1.18.26.74.31,1.31.9,1.62,1.67.13.33.21.67.21,1,0,.18.15.33.36.33h.13c.08.1.21.18.36.18h1.39c.15,0,.28-.13.28-.28v-.28h0Zm-14.02-7.24h-4.36c-.21,0-.41-.15-.46-.36l-.23-1.03-.08-.28c-.05-.21-.23-.36-.46-.36h-.18v-1.54c0-.13.1-.23.23-.23h5.29c.13,0,.23.1.23.23v3.57h.03Zm1.67-3.49c0-.18.15-.31.31-.31h2.41c.21,0,.41.08.59.23l3.59,3.21h-.69c-.13,0-.23.08-.28.15-.03.03-.03.08-.03.1v.08h-4.39c-.21,0-.39-.13-.44-.33l-.31-1.21-.03-.13c-.05-.21-.23-.33-.44-.33h-.31v-1.46Zm-20,.69v-.77c0-.13.1-.23.23-.23h10.58c.13,0,.23.1.23.23v3.59h-.87c-.21,0-.41-.15-.46-.36l-.1-.41-.13-.59-.08-.28c-.05-.21-.23-.36-.46-.36h-.33c-.08,0-.13.05-.15.1,0,.03-.03.05,0,.08l.23,1.85h-2.75c-.21,0-.41-.15-.46-.36l-.1-.49-.13-.54-.08-.28c-.05-.21-.23-.36-.46-.36h-.33c-.05,0-.1.03-.13.08-.03.03-.03.08-.03.1l.08.74.13,1.1h-3c-.21,0-.41-.15-.46-.36l-.23-1.03-.08-.28c-.05-.21-.23-.36-.46-.36h-.23v-.82h.05Zm25.95,11.76c.05.03.08.05.13.05.44.18.95.21,1.39.03.46-.18.8-.51,1-.98.39-.87,0-1.9-.82-2.34-.05-.03-.08-.05-.13-.05-.92-.39-2,.03-2.39.95-.39.87-.03,1.9.82,2.34Zm.82-2.77c.64,0,1.16.51,1.16,1.16s-.51,1.16-1.16,1.16-1.16-.51-1.16-1.16.51-1.16,1.16-1.16ZM9.27,6.71H3.7c-.18,0-.31-.15-.31-.31h0c0-.18.15-.31.31-.31h5.57c.18,0,.31.15.31.31h0c0,.15-.15.31-.31.31Zm1.28,1.72h-5.7c-.18,0-.31-.15-.31-.31h0c0-.18.15-.31.31-.31h5.72c.18,0,.31.15.31.31h0c0,.15-.15.31-.33.31Zm-3.31,1.82H1.31c-.18,0-.31-.15-.31-.31h0c0-.18.15-.31.31-.31h5.93c.18,0,.31.15.31.31h0c0,.18-.13.31-.31.31Zm2.93,1.8H3.72c-.18,0-.31-.15-.31-.31h0c0-.18.15-.31.31-.31h6.47c.18,0,.31.15.31.31h0c0,.15-.15.31-.33.31Z" // eslint-disable-line
    ,
    fill: "#cdcbcc"
  }));
};
const vehicleTypes = ['Car', 'Van', 'Van (Shared)', 'Shuttle', 'SUV', 'Limo', 'Sedan'];
const vehicles = {
  Car: /*#__PURE__*/React.createElement(GroundCar, null),
  Limo: /*#__PURE__*/React.createElement(GroundLimo, null),
  Sedan: /*#__PURE__*/React.createElement(GroundCar, null),
  Shuttle: /*#__PURE__*/React.createElement(GroundVan, null),
  SUV: /*#__PURE__*/React.createElement(GroundSUV, null),
  Van: /*#__PURE__*/React.createElement(GroundVan, null),
  'Van (Shared)': /*#__PURE__*/React.createElement(GroundVan, null)
};
const GroundTransferImage = _ref => {
  let {
    vehicleType
  } = _ref;
  if (vehicleTypes.includes(vehicleType)) {
    return vehicles[vehicleType];
  }
  return /*#__PURE__*/React.createElement(GroundCar, null);
};
GroundTransferImage.propTypes = {
  vehicleType: PropTypes.string
};

const PrintableRow = styled(Flex)`
  @media print {
    flex-direction: row;
  }
`;
const PrintableCarLogo = styled(Flex)`
  @media print {
    margin-bottom: 0;
    align-items: center;
  }
`;
const TimeDate = styled(Flex)`
  gap: 8px;
`;
const PhoneNumber = styled.a`
  font-size: 18px;
  text-decoration: none;
  color: ${getThemeProp('global.link--color')};
  white-space: nowrap;
`;
const dateTabletAndLarger = dateString => {
  return `${dayOfWeekShortFormatter(parseDate$1(dateString))}, ${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}, ${yearFourDigitFormatter(parseDate$1(dateString))}`;
};
const GroundTransfer = _ref => {
  var _service$groundTransf, _service$groundTransf2, _service$groundTransf3;
  let {
    item
  } = _ref;
  const {
    service
  } = item;
  const showPhoneNumber = Boolean(service.contactPhone);
  const showPickUpNotes = ((_service$groundTransf = service.groundTransfer.pickUpNotes) === null || _service$groundTransf === void 0 ? void 0 : _service$groundTransf.length) > 0;
  const showFromAddress = ((_service$groundTransf2 = service.groundTransfer.fromAddress) === null || _service$groundTransf2 === void 0 ? void 0 : _service$groundTransf2.length) > 0;
  const showToAddress = ((_service$groundTransf3 = service.groundTransfer.toAddress) === null || _service$groundTransf3 === void 0 ? void 0 : _service$groundTransf3.length) > 0;
  return /*#__PURE__*/React.createElement(Card, {
    mb: 4
  }, /*#__PURE__*/React.createElement(Card.SubHeader, {
    py: "2"
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  }, /*#__PURE__*/React.createElement(DateText, null, dateTabletAndLarger(service.travelDate)))), /*#__PURE__*/React.createElement(Card.Header, null, /*#__PURE__*/React.createElement(PrintableRow, {
    justifyContent: "space-between",
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(P, {
    fontSize: "18px"
  }, /*#__PURE__*/React.createElement("b", null, service.supplier, " Ground Transfer"))))), /*#__PURE__*/React.createElement(Card.SubHeader, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "CONFIRMATION # ")), /*#__PURE__*/React.createElement(P, {
    fontSize: "18px",
    darkColor: true
  }, /*#__PURE__*/React.createElement("b", null, service.supplierConfirmationNum)))), /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(PrintableRow, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(PrintableCarLogo, {
    mb: [3, 3, 0],
    flexDirection: "column",
    alignItems: "center",
    width: "200px"
  }, /*#__PURE__*/React.createElement(Box, {
    width: "166px",
    alignSelf: "flex-start"
  }, /*#__PURE__*/React.createElement(GroundTransferImage, {
    vehicleType: service.groundTransfer.vehicleType
  })), /*#__PURE__*/React.createElement(Text$2, {
    mt: 1,
    size: 18,
    weight: "bold"
  }, service.groundTransfer.vehicleType)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(TitleCase, null, "RATE"), /*#__PURE__*/React.createElement(Box, {
    mb: "6px"
  }), /*#__PURE__*/React.createElement(Flex, {
    mb: 1,
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(Text$2, {
    mr: "6px",
    size: 18,
    weight: "bold"
  }, "$", service.baseFareUSD), /*#__PURE__*/React.createElement(Text$2, {
    size: 14,
    weight: "600"
  }, service.currency, " TOTAL")), /*#__PURE__*/React.createElement(P, null, /*#__PURE__*/React.createElement("em", null, "(includes estimate of taxes & fees)"))))), /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(TimeDate, {
    mb: 3,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text$2, {
    size: 18
  }, dateTabletAndLarger(service.travelDate)), /*#__PURE__*/React.createElement(DotSeparator, null), /*#__PURE__*/React.createElement(Text$2, {
    size: 18,
    weight: "bold"
  }, hourMinuteMeridianCondensed(parseDate$1(service.travelDate)))), /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    width: 1 / 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "PICK UP"), /*#__PURE__*/React.createElement(Box, {
    mb: 2
  }), showFromAddress ? service.groundTransfer.fromAddress.map(fa => /*#__PURE__*/React.createElement(Text$2, {
    mb: 1,
    size: 18,
    key: fa.replace(/[^0-9A-Z]+/gi, '')
  }, fa)) : /*#__PURE__*/React.createElement(Text$2, {
    mb: 1,
    size: 18
  }, service.origin)), /*#__PURE__*/React.createElement(Box, {
    width: 1 / 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "DROP OFF"), /*#__PURE__*/React.createElement(Box, {
    mb: 2
  }), showToAddress ? service.groundTransfer.toAddress.map(ta => /*#__PURE__*/React.createElement(Text$2, {
    mb: 1,
    size: 18,
    key: ta.replace(/[^0-9A-Z]+/gi, '')
  }, ta)) : /*#__PURE__*/React.createElement(Text$2, {
    mb: 1,
    size: 18
  }, service.destination))), showPickUpNotes && /*#__PURE__*/React.createElement(Text$2, {
    mt: 2,
    size: 15,
    weight: "bold",
    lineHeight: 1.2,
    italic: true
  }, "(", service.groundTransfer.pickUpNotes, ")")), showPhoneNumber && /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(TitleCase, null, "PHONE #"), /*#__PURE__*/React.createElement(Box, {
    mb: 2
  }), /*#__PURE__*/React.createElement(PhoneNumber, {
    href: `tel:${service.contactPhone}`
  }, phoneNumberFormatter(service.contactPhone))));
};
GroundTransfer.propTypes = {
  item: PropTypes.shape({
    service: PropTypes.shape({
      travelDate: PropTypes.string.isRequired,
      supplier: PropTypes.string.isRequired,
      supplierConfirmationNum: PropTypes.string.isRequired,
      baseFareUSD: PropTypes.number.isRequired,
      currency: PropTypes.string.isRequired,
      origin: PropTypes.string,
      destination: PropTypes.string,
      contactPhone: PropTypes.string,
      groundTransfer: PropTypes.shape({
        vehicleType: PropTypes.string.isRequired,
        fromAddress: PropTypes.arrayOf(PropTypes.string),
        toAddress: PropTypes.arrayOf(PropTypes.string),
        pickUpNotes: PropTypes.string
      })
    })
  })
};

const getItinComponent = (itin, index, cancelable, cancelOptions, bookingNumber, enableManageTrips, cancelOnlyMode) => {
  var _itin$service;
  if (itin.flight) {
    return /*#__PURE__*/React.createElement(Flight, {
      key: index,
      item: itin,
      cancelable: cancelable,
      cancelOptions: cancelOptions,
      bookingNumber: bookingNumber,
      enableManageTrips: enableManageTrips,
      cancelOnlyMode: cancelOnlyMode
    });
  }
  if (itin.car) {
    return /*#__PURE__*/React.createElement(Car, {
      key: index,
      item: itin,
      cancelable: cancelable,
      cancelOptions: cancelOptions,
      bookingNumber: bookingNumber,
      enableManageTrips: enableManageTrips,
      cancelOnlyMode: cancelOnlyMode
    });
  }
  if (itin.hotel) {
    return /*#__PURE__*/React.createElement(Hotel, {
      key: index,
      item: itin,
      cancelable: cancelable,
      cancelOptions: cancelOptions,
      bookingNumber: bookingNumber,
      enableManageTrips: enableManageTrips,
      cancelOnlyMode: cancelOnlyMode
    });
  }
  if ((_itin$service = itin.service) !== null && _itin$service !== void 0 && _itin$service.groundTransfer) {
    return /*#__PURE__*/React.createElement(GroundTransfer, {
      key: index,
      item: itin
    });
  }
  return null;
};
const Itinerary = _ref => {
  let {
    itinItems,
    attendee,
    cancelable,
    cancelOptions,
    bookingNumber,
    enableManageTrips = false,
    cancelOnlyMode = false
  } = _ref;
  const {
    readOnly
  } = useContext(TripDetailsContext);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading2, {
    mt: [SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size48],
    mb: SPACE.size16
  }, "Itinerary"), attendee && !readOnly ? /*#__PURE__*/React.createElement(AttendeeInfo, {
    attendee: attendee
  }) : null, itinItems.map((itin, index) => getItinComponent(itin, index, cancelable, cancelOptions, bookingNumber, enableManageTrips, cancelOnlyMode)));
};
Itinerary.propTypes = {
  cancelable: PropTypes.bool,
  attendee: attendeeShape,
  cancelOptions: PropTypes.arrayOf(PropTypes.shape({})),
  itinItems: PropTypes.arrayOf(PropTypes.shape({})),
  bookingNumber: PropTypes.number,
  enableManageTrips: PropTypes.bool,
  cancelOnlyMode: PropTypes.bool
};
Itinerary.defaultProps = {
  cancelable: false,
  cancelOptions: [],
  itinItems: []
};

const Charges$2 = _ref => {
  let {
    charges
  } = _ref;
  const {
    passengers,
    additionalChargesCredits,
    payments,
    total,
    balanceDue
  } = charges;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading2, {
    mt: [SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size48],
    mb: SPACE.size16
  }, "Charges"), /*#__PURE__*/React.createElement(Table, {
    outerBorder: true,
    fontSize16: true
  }, /*#__PURE__*/React.createElement(Table.Head, {
    alignHeadersRight: true
  }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Details"), /*#__PURE__*/React.createElement("th", null, "Amount"))), /*#__PURE__*/React.createElement(Table.Body, null, passengers.map(passenger => /*#__PURE__*/React.createElement(React.Fragment, {
    key: passenger.nameNum
  }, /*#__PURE__*/React.createElement("tr", {
    key: passenger.nameNum
  }, /*#__PURE__*/React.createElement("td", {
    colSpan: "2"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "TRAVELER")), /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    weight: "bold"
  }, passenger.displayName)))), !isNilOrEmpty(passenger.tickets) && passenger.tickets.map(ticket => /*#__PURE__*/React.createElement(Table.Row, {
    key: `${ticket.ticketNumber}`,
    white: true
  }, /*#__PURE__*/React.createElement("td", null, ticket.routing && /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "ROUTING")), /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, ticket.routing)), ticket.ticketNumber && /*#__PURE__*/React.createElement(Flex, {
    alignItems: "flex-end"
  }, /*#__PURE__*/React.createElement(Box, {
    mr: 2,
    mt: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "TICKET #")), /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, ticket.ticketNumber))), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: ticket.amount
  }))))))), additionalChargesCredits.map(charge => /*#__PURE__*/React.createElement(Table.Row, {
    key: randomString(),
    white: true
  }, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "1"
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement("em", null, charge.item))), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: charge.price
  }))))), payments && !isNilOrEmpty(total) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table.Row, {
    white: true
  }, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "1",
    alignRight: true
  }, /*#__PURE__*/React.createElement(TitleCase, {
    medium: true
  }, "SUBTOTAL")), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement("em", null, /*#__PURE__*/React.createElement(Currency, {
    amount: total
  })))), payments.map(payment => /*#__PURE__*/React.createElement(Table.Row, {
    key: payment.description,
    white: true
  }, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "1",
    alignRight: true,
    redText: true
  }, /*#__PURE__*/React.createElement("em", {
    style: {
      wordBreak: 'break-all'
    }
  }, payment.description)), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    redText: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement("em", null, /*#__PURE__*/React.createElement(Currency, {
    amount: payment.amount
  }))))))), payments ? /*#__PURE__*/React.createElement(Table.Foot, null, /*#__PURE__*/React.createElement(Table.Row, null, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "1",
    alignRight: true
  }, /*#__PURE__*/React.createElement(TitleCase, {
    medium: true
  }, "BALANCE DUE")), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: balanceDue
  }))))) : /*#__PURE__*/React.createElement(Table.Foot, {
    yellowInvoice: true
  }, /*#__PURE__*/React.createElement(Table.Row, null, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "1",
    alignRight: true
  }, /*#__PURE__*/React.createElement(TitleCase, {
    medium: true
  }, "TOTAL")), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: total
  })))))), /*#__PURE__*/React.createElement(Box, {
    mt: 2,
    ml: 2
  }, /*#__PURE__*/React.createElement("h6", null, /*#__PURE__*/React.createElement("em", null, /*#__PURE__*/React.createElement("b", null, "Note:"), " All prices are in U.S. dollars."))));
};
Charges$2.propTypes = {
  charges: PropTypes.shape({
    passengers: PropTypes.arrayOf(PropTypes.shape({
      nameNum: PropTypes.number,
      displayName: PropTypes.string
    })),
    additionalChargesCredits: PropTypes.arrayOf(PropTypes.shape({
      price: PropTypes.number
    })),
    payments: PropTypes.arrayOf(PropTypes.shape({
      description: PropTypes.string,
      amount: PropTypes.number
    })),
    total: PropTypes.number,
    airTotal: PropTypes.number,
    balanceDue: PropTypes.number
  })
};
Charges$2.defaultProps = {
  charges: PropTypes.shape({
    passengers: PropTypes.arrayOf(PropTypes.shape({
      nameNum: 0,
      displayName: ''
    })),
    additionalChargesCredits: PropTypes.arrayOf(PropTypes.shape({
      price: 0
    })),
    payments: PropTypes.arrayOf(PropTypes.shape({
      description: '',
      amount: 0
    })),
    total: 0,
    airTotal: 0,
    balanceDue: 0
  })
};

const FeesTh = styled.th`
  width: 16%;
`;
const Charges$1 = _ref => {
  let {
    charges
  } = _ref;
  const {
    passengers,
    additionalChargesCredits,
    payments,
    total,
    balanceDue
  } = charges;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading2, {
    mt: [SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size32, SPACE.size48],
    mb: SPACE.size16
  }, "Charges"), /*#__PURE__*/React.createElement(Table, {
    outerBorder: true,
    fontSize16: true
  }, /*#__PURE__*/React.createElement(Table.Head, {
    alignHeadersRight: true
  }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Details"), /*#__PURE__*/React.createElement("th", null, "Base"), /*#__PURE__*/React.createElement(FeesTh, null, "Taxes & Fees"), /*#__PURE__*/React.createElement("th", null, "Amount"))), /*#__PURE__*/React.createElement(Table.Body, null, passengers.map(passenger => /*#__PURE__*/React.createElement(React.Fragment, {
    key: passenger.nameNum
  }, /*#__PURE__*/React.createElement("tr", {
    key: passenger.nameNum
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "TRAVELER")), /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, passenger.displayName))), /*#__PURE__*/React.createElement("td", null, "\xA0"), /*#__PURE__*/React.createElement("td", null, "\xA0"), /*#__PURE__*/React.createElement("td", null, "\xA0")), !isNilOrEmpty(passenger.tickets) && passenger.tickets.map((ticket, i) => /*#__PURE__*/React.createElement(Table.Row, {
    key: `${ticket.ticketNumber}--${i}`,
    white: true
  }, /*#__PURE__*/React.createElement("td", null, ticket.routing && /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    mr: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "ROUTING")), /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, ticket.routing)), ticket.ticketNumber && /*#__PURE__*/React.createElement(Flex, {
    alignItems: "flex-end"
  }, /*#__PURE__*/React.createElement(Box, {
    mr: 2,
    mt: 2
  }, /*#__PURE__*/React.createElement(TitleCase, null, "TICKET #")), /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, ticket.ticketNumber))), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: ticket.baseFare
  }))), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: ticket.tax
  }))), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: ticket.amount
  }))))))), additionalChargesCredits.map(charge => /*#__PURE__*/React.createElement(Table.Row, {
    key: randomString(),
    white: true
  }, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "3"
  }, /*#__PURE__*/React.createElement(Text$2, {
    italic: true,
    primary: true
  }, charge.item)), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: charge.price
  }))))), payments && !isNilOrEmpty(total) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table.Row, {
    white: true
  }, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "3",
    alignRight: true
  }, /*#__PURE__*/React.createElement(TitleCase, {
    medium: true
  }, "SUBTOTAL")), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement("em", null, /*#__PURE__*/React.createElement(Currency, {
    amount: total
  })))), payments.map(payment => /*#__PURE__*/React.createElement(Table.Row, {
    key: payment.description + payment.amount,
    white: true
  }, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "3",
    alignRight: true,
    redText: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, payment.description)), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    redText: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: payment.amount
  }))))))), payments ? /*#__PURE__*/React.createElement(Table.Foot, {
    yellowInvoice: true
  }, /*#__PURE__*/React.createElement(Table.Row, null, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "3",
    alignRight: true
  }, /*#__PURE__*/React.createElement(TitleCase, {
    medium: true
  }, "BALANCE DUE")), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true,
    weight: "bold"
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: balanceDue
  }))))) : /*#__PURE__*/React.createElement(Table.Foot, {
    yellowInvoice: true
  }, /*#__PURE__*/React.createElement(Table.Row, null, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: "3",
    alignRight: true
  }, /*#__PURE__*/React.createElement(TitleCase, {
    medium: true
  }, "TOTAL")), /*#__PURE__*/React.createElement(Table.Cell, {
    alignRight: true,
    borderLeft: true
  }, /*#__PURE__*/React.createElement("strong", null, /*#__PURE__*/React.createElement(Currency, {
    amount: total
  })))))), /*#__PURE__*/React.createElement(Box, {
    mt: 2
  }, /*#__PURE__*/React.createElement("h6", null, /*#__PURE__*/React.createElement("em", null, /*#__PURE__*/React.createElement("b", null, "Note:"), " All prices are in U.S. dollars."))));
};
Charges$1.propTypes = {
  charges: PropTypes.shape({
    passengers: PropTypes.arrayOf(PropTypes.shape({
      nameNum: PropTypes.number,
      displayName: PropTypes.string
    })),
    additionalChargesCredits: PropTypes.arrayOf(PropTypes.shape({
      price: PropTypes.number
    })),
    payments: PropTypes.arrayOf(PropTypes.shape({
      description: PropTypes.string,
      amount: PropTypes.number
    })),
    total: PropTypes.number,
    airTotal: PropTypes.number,
    balanceDue: PropTypes.number
  })
};
Charges$1.defaultProps = {
  charges: PropTypes.shape({
    passengers: PropTypes.arrayOf(PropTypes.shape({
      nameNum: 0,
      displayName: ''
    })),
    additionalChargesCredits: PropTypes.arrayOf(PropTypes.shape({
      price: 0
    })),
    payments: PropTypes.arrayOf(PropTypes.shape({
      description: '',
      amount: 0
    })),
    total: 0,
    airTotal: 0,
    balanceDue: 0
  })
};

const Charges = props => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Charges$2, props)), /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Charges$1, props)));
Charges.displayName = 'TripDetailsCharges';

const Notes = _ref => {
  let {
    notes
  } = _ref;
  if (!notes) return null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h2", null, "Notes"), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement("ul", null, notes.map(note => /*#__PURE__*/React.createElement("li", null, note)))));
};
Notes.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.string)
};
Notes.defaultProps = {
  notes: []
};

const InlineItem = styled.li`
  display: inline;

  &:first-of-type {
    &:before {
      content: '';
      padding: 0;
    }
  }

  &:before {
    content: ' • ';
    padding: 0 1px;
  }
`;
const InlineBulletList = _ref => {
  let {
    list
  } = _ref;
  return /*#__PURE__*/React.createElement("ul", null, list.map((item, index) =>
  /*#__PURE__*/
  // eslint-disable-next-line react/no-array-index-key
  React.createElement(InlineItem, {
    key: index
  }, item)));
};
InlineBulletList.propTypes = {
  /**
   * Text list
   */
  list: PropTypes.arrayOf(PropTypes.string)
};
InlineBulletList.defaultProps = {
  list: []
};

const getStarStr = stars => {
  let starStr = '';
  if (typeof stars !== 'undefined' && stars !== null) {
    const starArr = stars.toString().split('.');
    starArr.forEach(val => {
      starStr += val;
    });
    if (starArr.length === 1) {
      starStr += '0';
    }
  } else {
    starStr = '00';
  }
  return starStr;
};
const HotelRatingRegion = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 2px;
`;
const StarRating = styled.img`
  height: 12px;
`;
const GuestRatingTotal = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  font-size: 10px;
  font-weight: 500;
  color: white;
  background-color: ${getThemeProp('global.info--color')};
  border-radius: 2px;
  padding-top: 2px;
  padding-bottom: 2px;
  margin-left: 8px;
`;
const GuestReviewCount = styled.div`
  color: ${getThemeProp('global.info--color')};
  margin-left: 8px;
`;
const HotelRating = _ref => {
  let {
    rating,
    count,
    stars
  } = _ref;
  return rating > 0 ? /*#__PURE__*/React.createElement(HotelRatingRegion, null, /*#__PURE__*/React.createElement(StarRating, {
    src: `https://cdn.amtrav.com/hotels/stars-${getStarStr(stars)}.svg`
  }), /*#__PURE__*/React.createElement(GuestRatingTotal, null, rating), /*#__PURE__*/React.createElement(GuestReviewCount, null, count, " Reviews")) : null;
};
HotelRating.propTypes = {
  rating: PropTypes.number,
  count: PropTypes.number,
  stars: PropTypes.number
};
HotelRating.defaultProps = {
  rating: 0,
  count: 0,
  stars: 0
};

/* eslint-disable react/forbid-prop-types */
const CurrencyStyled = styled.span`
  position: relative;
  padding-left: 0.45em;
  display: inline-block;
  line-height: 1;
`;
const CurrencyDollarSign = styled.span`
  top: 0.125em;
  left: 0;
  font-size: 0.7em;
  position: absolute;
`;
const getBigHotelImage = url => url.replace('_e', '_z');
const getRoomUrl = (htl, hotelsRequest, getJsr) => {
  if (htl && typeof htl.PropID !== 'undefined' && typeof htl.VendorHotelID !== 'undefined' && typeof htl.VendorSource !== 'undefined') {
    const payload = {
      checkin: hotelsRequest.checkin,
      checkout: hotelsRequest.checkout,
      rms: hotelsRequest.rms,
      searchId: -1,
      chainCode: '',
      bn: typeof getJsr().bn !== 'undefined' ? getJsr().bn : null,
      nameNum: -1,
      isPersonal: false,
      shopRequest: {
        HSS: 'H',
        checkin: hotelsRequest.checkin,
        checkout: hotelsRequest.checkout,
        locId: hotelsRequest.locId,
        rms: hotelsRequest.rms
      }
    };
    payload.VendorHotelID = htl.VendorHotelID;
    payload.PropID = htl.PropID;
    switch (htl.VendorSource) {
      case 1:
        payload.sourceSys = '1V';
        break;
      case 2:
        payload.sourceSys = 'EA';
        break;
      case 8:
        payload.sourceSys = 'ER';
        break;
      default:
        payload.sourceSys = 0;
        break;
    }
    return `/hotels/rooms?jsr=${encodeToJSR(payload)}`;
  }
  return '/hotels';
};
const SeeRoomsRegion = styled.div`
  color: white;
  background: ${getThemeProp('global.success--color')};
  padding: 16px;
  font-weight: 500;
  font-size: 16px;
  position: absolute;
  text-align: center;
  width: 100%;
  bottom: 0;
  height: 48px;
  transform: translateY(48px);
  z-index: 0;
  transition: all 0.3s;
`;
const HotelPoster$1 = styled.div`
  width: 100%;
  height: 150px;
  background-size: 110%;
  background-position: 50% 100%;
  background-repeat: no-repeat;
  border-radius: 5px 5px 0 0;
  transition: all 0.3s;
`;
const HotelCard = styled.div`
  border-radius: 5px;
  border: 1px solid #ddd;
  margin: 0 0 16px;
  position: relative;
  user-select: none;
  box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.1);
  background-size: 100%;
  overflow: hidden;
`;
const HotelOption = styled.div`
  min-height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: white;
  position: relative;
  z-index: 1;
  transition: all 0.3s;
  bottom: 0px;

  @media (min-width: 660px) {
    height: 87px;
  }
`;
const HotelDetails = styled.div`
  width: calc(100% - 100px);
`;
const HotelPriceRegion = styled.div`
  text-align: right;
  width: 100px;
  padding: 8px 0 0;
`;
const HotelAmount = styled.span`
  font-size: 26px;

  ${CurrencyStyled} {
    color: ${getThemeProp('global.success--color')};
    font-weight: bold;
  }
`;
const HotelName = styled.div`
  color: ${getThemeProp('global.link--color')};
  font-weight: 500;
  padding: 0 0 4px 0;
  font-size: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const HotelAddress = styled.div`
  color: #777;
  font-size: 11px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const PromoBanner = styled.div`
  position: absolute;
  z-index: 2;
  background: ${getThemeProp('global.success--color')};
  color: white;
  padding: 8px 16px 8px 24px;
  right: -7px;
  top: 112px;
  transition: all 0.3s;
  font-weight: 500;
  box-shadow: 0 6px 5px -6px rgba(0, 0, 0, 0.3);
  clip-path: polygon(0 0, 100% 0%, 100% 130%, 8px 100%);

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    right: 0px;
    border-top: 8px solid ${props => tinycolor(getThemeProp('global.success--color')(props)).darken(6).toString()};
    border-bottom: 0px solid transparent;
    border-right: 7px solid transparent;
    top: 30px;
  }
`;
const HotelLink = styled(Box)`
  max-width: 88vw;
  position: relative;
  display: block;
  text-decoration: none;
  color: unset;
  cursor: pointer;
  width: 100%;

  @media (min-width: 660px) {
    width: calc((100% / 2) - 8px);
  }

  @media (min-width: 1020px) {
    width: calc((100% / 3) - 8px);
  }

  @media (hover: hover) {
    &:hover {
      color: unset;

      ${SeeRoomsRegion} {
        transform: translateY(0px);
      }

      ${PromoBanner} {
        top: 79px;
      }

      ${HotelOption} {
        bottom: 48px;
      }

      ${HotelPoster$1} {
        background-size: 120%;
        background-position: 50% calc(100% - 24px);
      }
    }
  }
`;
const HotelValueAdds = styled(Box)`
  font-size: 11px;
  font-weight: 500;
  color: ${getThemeProp('global.success--color')};
`;
const AveragePerNightLabel = styled.div`
  padding: 2px 0 8px;
  font-size: 10px;
`;
const OldPrice = styled.div`
  text-decoration: line-through;
  font-size: 16px;
  color: #777;
`;
const HotelRecommendationCard = _ref => {
  let {
    hotel,
    hotelsRequest
  } = _ref;
  const {
    Thumbnail,
    Name,
    AverageNightlyRate,
    AverageBaseRate,
    Address1,
    Rooms,
    StarRating,
    ReviewRate,
    ReviewCount
  } = hotel;
  const firstRoom = Rooms && Rooms[0];
  const firstValueAdds = firstRoom && firstRoom.ValueAdds;
  const {
    getJsr,
    openWebUrl
  } = useAppApi();
  return /*#__PURE__*/React.createElement(HotelLink, {
    as: "a",
    onClick: () => {
      openWebUrl(getRoomUrl(hotel, hotelsRequest, getJsr));
    }
  }, firstRoom.VendorPromoText ? /*#__PURE__*/React.createElement(PromoBanner, null, firstRoom.VendorPromoText) : null, /*#__PURE__*/React.createElement(HotelCard, null, /*#__PURE__*/React.createElement(HotelPoster$1, {
    style: {
      backgroundImage: `url(${getBigHotelImage(Thumbnail)})`
    }
  }), /*#__PURE__*/React.createElement(HotelOption, null, /*#__PURE__*/React.createElement(HotelDetails, null, /*#__PURE__*/React.createElement(HotelName, null, Name), /*#__PURE__*/React.createElement(HotelAddress, null, Address1), /*#__PURE__*/React.createElement(Flex, {
    pt: 2,
    flexDirection: ['column', 'row']
  }, /*#__PURE__*/React.createElement(HotelRating, {
    rating: ReviewRate,
    count: ReviewCount,
    stars: StarRating
  })), firstValueAdds && /*#__PURE__*/React.createElement(HotelValueAdds, {
    pt: 1
  }, /*#__PURE__*/React.createElement(InlineBulletList, {
    list: firstValueAdds.map(i => i.Description)
  }))), /*#__PURE__*/React.createElement(HotelPriceRegion, null, /*#__PURE__*/React.createElement(HotelAmount, null, /*#__PURE__*/React.createElement(CurrencyStyled, null, /*#__PURE__*/React.createElement(CurrencyDollarSign, null, "$"), AverageNightlyRate.toFixed(0)), /*#__PURE__*/React.createElement(AveragePerNightLabel, null, "avg. per night"), AverageBaseRate !== AverageNightlyRate && /*#__PURE__*/React.createElement(OldPrice, null, "$", AverageBaseRate.toFixed(0))))), /*#__PURE__*/React.createElement(SeeRoomsRegion, null, "See Rooms")));
};
HotelRecommendationCard.propTypes = {
  hotel: PropTypes.object.isRequired,
  hotelsRequest: PropTypes.object.isRequired,
  Thumbnail: PropTypes.string,
  Name: PropTypes.string,
  AverageNightlyRate: PropTypes.number.isRequired,
  Address1: PropTypes.string,
  Rooms: PropTypes.array.isRequired,
  StarRating: PropTypes.number,
  ReviewRate: PropTypes.number,
  ReviewCount: PropTypes.number
};
HotelRecommendationCard.defaultProps = {
  Thumbnail: '',
  Name: '',
  Address1: '',
  ReviewRate: 0,
  ReviewCount: 0,
  StarRating: 0
};

const HotelCardsWrapper = styled(Flex)`
  max-width: 1020px;
  margin: 0 auto;
  flex-wrap: wrap;
  justify-content: space-between;
`;
const DestinationTitle = styled(Box)`
  font-family: ${getThemeProp('global.headings--fontFamily')};
  font-size: 1.25rem;
  color: ${getThemeProp('global.headings--color')};

  ${media.Medium`
  font-size: 1.5rem;
  `}
`;
const TravelersTitle = styled(Box)`
  font-family: ${getThemeProp('global.headings--fontFamily')};
  font-size: 1.125rem;
`;
const NewsBanner = styled(Box)`
  background-color: ${getThemeProp('global.success--color')};
  background-image: url('https://cdn.amtrav.com/trip-details/hotel-recommendations/travel-collection-icons.svg');
  background-size: 35%;
  background-position: -2% 11%;
  color: white;
  margin-top: -1px;
  font-size: 0.875rem;
  font-weight: 500;
  font-style: italic;
  position: relative;
  line-height: 1.3;

  ${media.Medium`
    font-size: 1rem;
  `}
`;
const getHotelResultsUrl = hotelsRequest => {
  const requestObj = {
    HSS: 'H',
    bn: hotelsRequest.bn,
    checkin: hotelsRequest.checkin,
    checkout: hotelsRequest.checkout,
    locId: hotelsRequest.locId,
    rms: hotelsRequest.rms
  };
  return `/hotels/results?jsr=${encodeToJSR(requestObj)}`;
};
const getTravelerCountSummary = travelersStr => {
  const count = (travelersStr.split(',')).reduce(curr => parseInt(curr, 10) + 1);
  return parseInt(count, 10) === 1 ? `${count} Traveler` : `${count} Travelers`;
};
const weekdayMonthDay = date => {
  return `${dayOfWeekShortFormatter(date)}, ${monthShortFormatter(date)} ${dayOfMonthFormatter(date)}`;
};
const HotelRecommendations = _ref => {
  let {
    hotels,
    hotelsRequest,
    modalTitle,
    onClose
  } = _ref;
  const {
    openWebUrl
  } = useAppApi();
  // trap if no hotel recommendations exist
  if ((hotels && hotels.Data || []).length <= 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: () => {
      onClose();
    }
  }, /*#__PURE__*/React.createElement(Modal.Header, null, modalTitle), /*#__PURE__*/React.createElement(NewsBanner, {
    px: 3,
    py: [2, 2, 3]
  }, "Great news! You qualify for special hotel deals because you booked a flight."), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    mb: "3",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(DestinationTitle, {
    width: ['100%', 'auto']
  }, hotels.SearchKey.shortDisplay, " (", weekdayMonthDay(parseDate$1(hotelsRequest.checkin)), " -", ' ', weekdayMonthDay(parseDate$1(hotelsRequest.checkout)), ")"), /*#__PURE__*/React.createElement(TravelersTitle, {
    pt: [2, 0]
  }, getTravelerCountSummary(hotelsRequest.rms))), /*#__PURE__*/React.createElement(HotelCardsWrapper, null, hotels.Data.slice(0, 6).map(hotel => /*#__PURE__*/React.createElement(HotelRecommendationCard, {
    key: hotel.VendorHotelID,
    hotel: hotel,
    hotelsRequest: hotelsRequest
  }))))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      openWebUrl(getHotelResultsUrl(hotelsRequest));
    },
    kind: "secondary",
    bordered: true,
    text: "See All Hotel Options"
  })));
};
const HotelRecommendationsPropTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  hotels: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  hotelsRequest: PropTypes.object,
  modalTitle: PropTypes.string,
  onClose: PropTypes.func
};
const HotelRecommendationsDefaultProps = {
  hotels: {},
  hotelsRequest: {},
  modalTitle: 'Hotel Options',
  onClose: () => {}
};
HotelRecommendations.propTypes = HotelRecommendationsPropTypes;
HotelRecommendations.defaultProps = HotelRecommendationsDefaultProps;

const HotelPoster = styled.div`
  background-size: 110%;
  background-position: 50% 100%;
  background-repeat: no-repeat;
  position: absolute;
  right: 0;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  border: 3px solid white;
  display: none;

  ${media.Small`
    display: block;
    width: 80px;
    height: 48px;
  `}

  ${media.Medium`
    width: 130px;
    height: 75px;
  `}
`;
const HotelPosterContainer = styled.div`
  ${HotelPoster}:nth-child(1) {
    top: 60%;
    right: 30%;

    ${media.Medium`
      top: 22%;
      right: -1%;
    `}
    z-index: 1;
    transform: rotate(10deg);
  }
  ${HotelPoster}:nth-child(2) {
    top: 72%;
    right: 40%;

    ${media.Medium`
      top: 26%;
      right: 17%;
    `}
    transform: rotate(16deg);
  }
  ${HotelPoster}:nth-child(3) {
    transform: rotate(9deg);
    right: 9%;
    bottom: 3%;

    ${media.Medium`
      transform: rotate(-9deg);
      bottom: -1%;
      right: -2%;
    `};
  }
  ${HotelPoster}:nth-child(4) {
    transform: rotate(-11deg);
    top: 70%;
    right: 20%;

    ${media.Medium`
      top: 2%;
      right: 10%;
    `}
  }
  ${HotelPoster}:nth-child(5) {
    transform: rotate(-15deg);
    top: 76%;
    right: -3%;

    ${media.Medium`
      top: 0;
      right: -10%;
    `}
  }
  ${HotelPoster}:nth-child(6) {
    bottom: 8%;
    right: 1%;
    transform: rotate(-4deg);

    ${media.Medium`
      bottom: 5%;
      right: 22%;
      transform: rotate(-8deg);
    `}
  }
`;
const ButtonTextStyled = styled.span`
  display: inline-block;
  font-weight: normal;
  text-decoration: none;
  text-transform: none;
  color: inherit;

  ${_ref => {
  let {
    isLoading
  } = _ref;
  return isLoading && css`
      visibility: hidden;
    `;
}};
`;
const CtaBox = styled(Box)`
  position: relative;
  overflow: hidden;
  padding: 16px 8px;
  background: linear-gradient(
    90deg,
    ${getThemeProp('global.dark--color')} 0%,
    ${getThemeProp('global.dark--color')} 35%,
    ${props => tinycolor.mix(getThemeProp('global.dark--color')(props), 'white', 40).toString()} 100%
  );

  ${media.Small`
    padding: 16px;
  `}

  ${media.Medium`
    padding: 24px;
    border-radius: ${getThemeProp('global.borderRadius--size')};
  `}

  ${ButtonTextStyled} {
    font-weight: bold;
    font-size: 1.2rem;
  }
`;
const CtaSubheading = styled(Box)`
  font-size: 0.9rem;
  letter-spacing: 0.0625rem;
  color: goldenrod;
`;
const CtaHeading = styled.h1`
  font-family: ${getThemeProp('global.headings--fontFamily')};
  color: white;
  font-size: 1.5rem;

  ${media.Medium`
    font-size: 1.625rem;
    width: 58%;
  `}
`;
const HotelRecommendationsCTA = _ref2 => {
  let {
    hotels,
    hotelsRequest
  } = _ref2;
  const [showRecommendations, setShowRecommendations] = useState(false);

  // trap if no hotel recommendations exist
  if ((hotels && hotels.Data || []).length <= 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, showRecommendations ? /*#__PURE__*/React.createElement(HotelRecommendations, {
    modalTitle: "Special Hotel Deals",
    hotels: hotels,
    hotelsRequest: hotelsRequest,
    onClose: () => setShowRecommendations(false)
  }) : null, /*#__PURE__*/React.createElement(CtaBox, {
    mb: 4
  }, /*#__PURE__*/React.createElement(HotelPosterContainer, null, hotels.Data.slice(0, 6).map(hotel => /*#__PURE__*/React.createElement(HotelPoster, {
    key: hotel.PropID,
    style: {
      backgroundImage: `url(${getBigHotelImage(hotel.Thumbnail)})`
    }
  }))), /*#__PURE__*/React.createElement(CtaSubheading, {
    pb: 2
  }, "Exclusive Hotel Offer"), /*#__PURE__*/React.createElement(CtaHeading, null, "Because You Booked a Flight You Qualify for Special Hotel Deals"), /*#__PURE__*/React.createElement(Button, {
    onClick: () => setShowRecommendations(true),
    text: "See Hotel Deals"
  })));
};
HotelRecommendationsCTA.propTypes = HotelRecommendationsPropTypes;
HotelRecommendationsCTA.defaultProps = HotelRecommendationsDefaultProps;

const CtaButtonStyled = styled.button`
  height: 40px;
  width: 100%;
  font-size: 16px;
  font-family: inherit;
  font-weight: bold;
  color: ${getThemeProp('global.link--color')};
  border-radius: ${getThemeProp('button.borderRadius--size')};
  border-color: ${props => tinycolor.mix(getThemeProp('global.link--color')(props), 'white', 75).toString()};
  border-width: ${getThemeProp('button.borderWidth--standard')};
  border-style: solid;
  padding: 0;
  cursor: pointer;
  background-color: ${getThemeProp('global.light--color')};

  &:focus {
    outline: none;
  }
`;
const Text$1 = styled(Box)`
  text-align: left;
  margin-left: 10px;
`;
const Prefix = styled(Flex)`
  width: 60px;
  justify-content: center;
  font-size: 26px;
`;
const Suffix = styled(Flex)`
  width: 40px;
  justify-content: center;
`;
const RightContentWrapper = styled(Flex)`
  width: 100%;
  height: 36px;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  border-radius: ${getThemeProp('button.borderRadius--size')};
`;
const CtaButton = _ref => {
  let {
    onClick,
    text,
    iconPrefix,
    iconSuffix
  } = _ref;
  return /*#__PURE__*/React.createElement(CtaButtonStyled, {
    onClick: onClick
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Prefix, null, iconPrefix), /*#__PURE__*/React.createElement(RightContentWrapper, null, /*#__PURE__*/React.createElement(Text$1, null, text), iconSuffix ? /*#__PURE__*/React.createElement(Suffix, null, iconSuffix) : null)));
};
CtaButton.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClick: PropTypes.func,
  iconSuffix: PropTypes.element,
  iconPrefix: PropTypes.element.isRequired
};
CtaButton.defaultProps = {
  text: '',
  onClick: () => {},
  iconSuffix: null
};

const BoxGrow = styled(Box)`
  display: grid;
`;
const AddOn = _ref => {
  let {
    control
  } = _ref;
  return /*#__PURE__*/React.createElement(BoxGrow, null, control);
};
AddOn.propTypes = {
  control: PropTypes.node
};
AddOn.defaultProps = {
  control: null
};

const AddOnOptionsContStyled$1 = styled(Flex)`
  flex-direction: column;
  max-width: 318px;
  padding: 8px;
`;

// styled <button> is used here instead of <option>
// because option elements are difficult to style
const PopoverOption$1 = styled.button`
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  font-size: 16px;
  font-family: ${getThemeProp('global.textBody--fontFamily')};
  color: ${getTokenValue(THEME_TOKENS.base.color.action)};
  text-align: left;
  line-height: 18px;
  background: white;
  border-radius: 8px;

  &:hover {
    background-color: ${getTokenValue(THEME_TOKENS.textInput.color.backgroundHover)};
  }

  &:first-child {
    display: none;
  }

  &:nth-of-type(2) {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom: 0;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;
const commonPropTypes$1 = {
  disabled: PropTypes.bool,
  focus: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  message: PropTypes.string,
  uiStyle: PropTypes.shape({})
};
const AddOnOptions$1 = props => {
  const [toggleState, setToggleState] = useState(true);
  const {
    options,
    caption,
    onClick,
    iconPrefix
  } = props;
  return /*#__PURE__*/React.createElement(Popover, {
    withPadding: false,
    onShow: () => setToggleState(false),
    onHide: () => setToggleState(true),
    content: /*#__PURE__*/React.createElement(AddOnOptionsContStyled$1, null, options.map(option => {
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: option.id
      }, /*#__PURE__*/React.createElement(PopoverOption$1, {
        onClick: onClick,
        value: option.id
      }, option.text));
    }))
  }, /*#__PURE__*/React.createElement(CtaButton, {
    text: caption,
    iconPrefix: iconPrefix,
    iconSuffix: toggleState ? /*#__PURE__*/React.createElement(IconCaretDown, null) : /*#__PURE__*/React.createElement(IconCaretUp, null)
  }));
};
AddOnOptions$1.propTypes = {
  ...commonPropTypes$1
};
AddOnOptions$1.defaultProps = {
  options: []
};

const shortDateNumbers = dateString => {
  const date = parseDate$1(dateString);
  return `${monthOfYearFormatter(date)}/${dayOfMonthFormatter(date)}/${yearTwoDigitFormatter(date)}`;
};
const getHotelsSelectorUrl = (hotel, bn, nameNum, isPersonal, viewSettings) => {
  let url = '/hotels/default.aspx';
  const jsr = {
    bn,
    nameNum,
    isPersonal,
    checkIn: hotel.arrivalDate,
    checkin: hotel.arrivalDate,
    checkOut: hotel.departDate,
    checkout: hotel.departDate
  };
  if (hotel.allowModify) {
    jsr.destinationLocId = hotel.locId;
    if (!isNilOrEmpty(viewSettings)) {
      if (!isNilOrEmpty(viewSettings.hotelDefaultChain)) {
        jsr.chainCode = viewSettings.hotelDefaultChain;
      }
      if (!isNilOrEmpty(viewSettings.hotelDefaultStarRate)) {
        jsr.sf = viewSettings.hotelDefaultStarRate;
      }
    }
  } else {
    url = '/hotels/results.aspx';
    jsr.HSS = 'H';
    jsr.locId = hotel.locId;
    jsr.rms = '1';
    if (!isNilOrEmpty(viewSettings)) {
      if (!isNilOrEmpty(viewSettings.hotelDefaultChain)) {
        if (viewSettings.hotelDefaultChain === 'PREF') {
          jsr.pf = true;
        } else {
          jsr.chainCode = viewSettings.hotelDefaultChain;
          jsr.schainCode = viewSettings.hotelDefaultChain; // service call only
        }
      }
      if (!isNilOrEmpty(viewSettings.hotelDefaultStarRate)) {
        jsr.sf = viewSettings.hotelDefaultStarRate;
        jsr.ssf = viewSettings.hotelDefaultStarRate; // service call only
      }
    }
  }
  url += `?jsr=${encodeObjToJsrString(jsr)}`;
  return url;
};
const HotelAddOn = _ref => {
  let {
    hotels,
    bn,
    nameNum,
    viewSettings,
    isPersonal
  } = _ref;
  const {
    bookingModPendingAlert,
    openWebUrl
  } = useAppApi();
  const handleCarSelect = event => {
    const url = event.target.value;
    bookingModPendingAlert({
      bn
    }).then(() => {
      openWebUrl(url);
    });
  };
  const carSelector = hotels.options ? /*#__PURE__*/React.createElement(AddOnOptions$1, {
    options: [{
      id: '',
      text: hotels.caption
    }].concat(hotels.options.map(hotel => ({
      text: hotel.display + (hotel.arrivalDate ? ` ${shortDateNumbers(hotel.arrivalDate)} - ${shortDateNumbers(hotel.departDate)}` : ''),
      id: getHotelsSelectorUrl(hotel, bn, nameNum, isPersonal, viewSettings)
    }))),
    caption: hotels.caption,
    onClick: handleCarSelect,
    iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Hotel"
    })
  }) : /*#__PURE__*/React.createElement(CtaButton, {
    onClick: () => {
      openWebUrl(`/hotels/default.aspx?jsr=${encodeObjToJsrString({
        bn,
        nameNum,
        isPersonal
      })}`);
    },
    text: hotels.caption,
    iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Hotel"
    })
  });
  return /*#__PURE__*/React.createElement(AddOn, {
    control: carSelector
  });
};
HotelAddOn.propTypes = {
  hotels: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({
      display: PropTypes.string,
      arrivalDate: PropTypes.string,
      departDate: PropTypes.string
    })),
    caption: PropTypes.string
  }),
  bn: PropTypes.string,
  nameNum: PropTypes.number,
  isPersonal: PropTypes.bool,
  viewSettings: PropTypes.shape({
    hotelDefaultChain: PropTypes.string,
    hotelDefaultStarRate: PropTypes.number
  })
};
HotelAddOn.defaultProps = {
  hotels: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({
      display: '',
      arrivalDate: '',
      departDate: ''
    })),
    caption: ''
  }),
  bn: '',
  nameNum: 0,
  isPersonal: false,
  viewSettings: PropTypes.shape({
    hotelDefaultChain: '',
    hotelDefaultStarRate: 0
  })
};
var HotelAddOn$1 = withTheme(HotelAddOn);

const monthDayYearCompact = dateString => {
  const date = parseDate$1(dateString);
  return `${monthOfYearFormatter(date)}/${dayOfMonthFormatter(date)}/${yearTwoDigitFormatter(date)}`;
};
const getCarsSelectorUrl = (car, bn, nameNum, isPersonal, viewSettings) => {
  let url = '/cars/default.aspx';
  const jsr = {
    bn,
    nameNum,
    isPersonal,
    // NOTE: The api expects slightly different capitalizations depending on
    // whether the car can be modified or not. I'm just including both (Matt)
    pickupLocId: car.location,
    pickUpLocID: car.location,
    dropOffLocId: car.dropOffLocation,
    dropOffLocID: car.dropOffLocation
  };
  if (car.allowModify) {
    jsr.pickupDate = isoExtendedFormat(parseDate$1(car.fromDate));
    jsr.pickupTime = `${militaryHourFormatter(parseDate$1(car.fromDate))}:00`;
    jsr.dropoffDate = isoExtendedFormat(parseDate$1(car.toDate));
    jsr.dropoffTime = `${militaryHourFormatter(parseDate$1(car.toDate))}:00`;
    if (!isNilOrEmpty(viewSettings)) {
      if (!isNilOrEmpty(viewSettings.carDefaultType)) {
        jsr.carType = viewSettings.carDefaultType;
      }
      if (!isNilOrEmpty(viewSettings.carDefaultVendor)) {
        jsr.preferredVendor = viewSettings.carDefaultVendor;
      }
    }
  } else {
    url = '/cars/results.aspx';
    jsr.pickUp = car.fromDate;
    jsr.dropOff = car.toDate;
    jsr.nextFlight = car.nextFlight;
    jsr.previousFlight = car.previousFlight;
    if (!isNilOrEmpty(viewSettings)) {
      jsr.sfilters = {};
      if (!isNilOrEmpty(viewSettings.carDefaultType)) {
        jsr.sfilters.carType = viewSettings.carDefaultType;
      }
      if (!isNilOrEmpty(viewSettings.carDefaultVendor)) {
        jsr.sfilters.vf = viewSettings.carDefaultVendor;
      }
    }
  }
  url += `?jsr=${encodeObjToJsrString(jsr)}`;
  return url;
};
const CarAddOn = _ref => {
  let {
    cars,
    bn,
    nameNum,
    viewSettings,
    isPersonal
  } = _ref;
  const {
    bookingModPendingAlert,
    openWebUrl
  } = useAppApi();
  const handleCarSelect = event => {
    const url = event.target.value;
    bookingModPendingAlert({
      bn
    }).then(() => {
      openWebUrl(url);
    });
  };
  const carSelector = cars.options ? /*#__PURE__*/React.createElement(AddOnOptions$1, {
    options: [{
      id: '',
      text: cars.caption
    }].concat(cars.options.map(car => ({
      text: car.display + (car.fromDate ? ` ${monthDayYearCompact(car.fromDate)} - ${monthDayYearCompact(car.toDate)}` : ''),
      id: getCarsSelectorUrl(car, bn, nameNum, isPersonal, viewSettings)
    }))),
    caption: cars.caption,
    onClick: handleCarSelect,
    iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Car"
    })
  }) : /*#__PURE__*/React.createElement(CtaButton, {
    onClick: () => {
      openWebUrl(`/cars/default.aspx?jsr=${encodeObjToJsrString({
        bn,
        nameNum,
        isPersonal
      })}`);
    },
    text: cars.caption,
    iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Car"
    })
  });
  return /*#__PURE__*/React.createElement(AddOn, {
    control: carSelector
  });
};
CarAddOn.propTypes = {
  cars: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({
      display: PropTypes.string,
      fromDate: PropTypes.string,
      toDate: PropTypes.string
    })),
    caption: PropTypes.string
  }),
  bn: PropTypes.string,
  isPersonal: PropTypes.bool,
  nameNum: PropTypes.number,
  viewSettings: PropTypes.shape({
    carDefaultType: PropTypes.string,
    carDefaultVendor: PropTypes.string
  })
};
CarAddOn.defaultProps = {
  cars: [],
  bn: '',
  isPersonal: false,
  nameNum: 0,
  viewSettings: PropTypes.shape({
    carDefaultType: '',
    carDefaultVendor: ''
  })
};
var CarAddOn$1 = withTheme(CarAddOn);

const Text = styled(Box)`
  line-height: 1.4;
  font-family: ${getThemeProp('global.textBody--fontFamily')};

  h2 {
    margin-top: 1rem;
  }

  ul {
    list-style: square;
    padding: 0 1rem 0 2.5rem;
    margin: auto;

    li {
      line-height: 1.4;
    }
  }
`;
const PriceDetailsRegion = styled(Flex)`
  text-align: center;
  font-weight: bold;
`;
const LargePrice = styled(Box)`
  font-size: 1.825rem;
  font-weight: bold;
  font-family: ${getThemeProp('global.headings--fontFamily')};
`;
const LargePriceHigher = styled(LargePrice)`
  color: ${getThemeProp('global.danger--color')};
`;
const LargePriceLower = styled(LargePrice)`
  color: ${getThemeProp('global.success--color')};
`;
const PriceLabel = styled.span`
  font-style: italic;
  font-size: 1rem;
  font-family: ${getThemeProp('global.headings--fontFamily')};
`;
const PastPriceRegion = styled(Box)`
  color: ${getThemeProp('global.disabled--color')};
`;
const ChangeDirection = styled(Box)`
  font-size: 1rem;
`;
const Disclaimer = styled(Flex)`
  font-style: italic;
  font-size: 0.75rem;
  justify-content: flex-end;
  align-items: flex-start;
`;
const PriceCard = styled(Card)`
  background: ${props => tinycolor.mix(getThemeProp('global.success--color')(props), 'white', 85).toString()};
`;
const getPdpStatusText = status => {
  switch (status) {
    case 'U':
      return 'The fare is higher now, so no credit is due. Good job buying when you did!';
    case 'D':
      return "Congratulations! You're getting a payback. Cha-Ching!";
    case 'S':
      return 'Fare has stayed the same. No credit is due.';
    case 'X':
      return 'Fare has gone down but the $100 limit has already been exceeded.';
    case 'P':
      return 'Fare has gone down. The total decrease exceeds the $100 limit. A partial credit have been issued.';
    default:
      return '';
  }
};
const PdpPriceDetails = _ref => {
  let {
    baseFare,
    currentFare,
    fareChange,
    status
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PriceCard, null, /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(PriceDetailsRegion, {
    flexDirection: ['column', 'row'],
    width: "100%",
    justifyContent: "space-between",
    alignItems: ['center', 'flex-end']
  }, /*#__PURE__*/React.createElement(PastPriceRegion, null, /*#__PURE__*/React.createElement(PriceLabel, null, "Previous price was: "), /*#__PURE__*/React.createElement(LargePrice, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: baseFare
  }), "*")), /*#__PURE__*/React.createElement(Box, {
    py: [4, 0]
  }, /*#__PURE__*/React.createElement(PriceLabel, null, "Today's price is: "), /*#__PURE__*/React.createElement(LargePrice, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: currentFare
  }), "*")), /*#__PURE__*/React.createElement(Box, null, fareChange < 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PriceLabel, null, "Change: "), /*#__PURE__*/React.createElement(LargePriceLower, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(ChangeDirection, {
    pr: 2
  }, /*#__PURE__*/React.createElement(IconTriangle, {
    rotate: 180
  })), /*#__PURE__*/React.createElement(Currency, {
    amount: Math.abs(fareChange)
  }), "*"))), fareChange > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PriceLabel, null, "Change: "), /*#__PURE__*/React.createElement(LargePriceHigher, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(ChangeDirection, {
    pr: 2
  }, /*#__PURE__*/React.createElement(IconTriangle, null)), /*#__PURE__*/React.createElement(Currency, {
    amount: Math.abs(fareChange)
  }), "*"))), fareChange === 0 && /*#__PURE__*/React.createElement(LargePrice, {
    pt: 2
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, "No Change"))))), /*#__PURE__*/React.createElement(Card.Footer, null, /*#__PURE__*/React.createElement(Text, null, getPdpStatusText(status)))), /*#__PURE__*/React.createElement(Disclaimer, {
    pt: 2
  }, "* Average fare per person"));
};
PdpPriceDetails.propTypes = {
  baseFare: PropTypes.number.isRequired,
  currentFare: PropTypes.number.isRequired,
  fareChange: PropTypes.number.isRequired,
  status: PropTypes.string
};
PdpPriceDetails.defaultProps = {
  status: ''
};

const friendlyDate = dateString => {
  const date = parseDate$1(dateString);
  return `${monthShortFormatter(date)} ${dayOfMonthFormatter(date)}, ${yearFourDigitFormatter(date)}`;
};
const PdpCreditTablePropTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  credits: PropTypes.array.isRequired
};
const PdpCreditTableFull = _ref => {
  let {
    credits
  } = _ref;
  return /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement(Table.Head, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Name"), /*#__PURE__*/React.createElement("th", null, "Code"), /*#__PURE__*/React.createElement("th", null, "Amount"), /*#__PURE__*/React.createElement("th", null, "Valid for Redemptions"))), /*#__PURE__*/React.createElement(Table.Body, null, credits.map(_ref2 => {
    let {
      name,
      code,
      amount,
      validFromDate,
      validToDate
    } = _ref2;
    return /*#__PURE__*/React.createElement("tr", {
      key: `full${code}`
    }, /*#__PURE__*/React.createElement("td", null, name), /*#__PURE__*/React.createElement("td", null, code), /*#__PURE__*/React.createElement("td", null, amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    })), /*#__PURE__*/React.createElement("td", null, friendlyDate(validFromDate), " \u2013 ", friendlyDate(validToDate)));
  })));
};
PdpCreditTableFull.propTypes = PdpCreditTablePropTypes;
const PdpCreditTableSmall = _ref3 => {
  let {
    credits
  } = _ref3;
  return /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement(Table.Body, null, credits.map(_ref4 => {
    let {
      name,
      code,
      amount,
      validFromDate,
      validToDate
    } = _ref4;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: `small${code}`
    }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, "Name")), /*#__PURE__*/React.createElement("td", null, name)), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, "Code")), /*#__PURE__*/React.createElement("td", null, code)), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, "Amount")), /*#__PURE__*/React.createElement("td", null, amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, "Valid")), /*#__PURE__*/React.createElement("td", null, friendlyDate(validFromDate), " \u2013 ", friendlyDate(validToDate))));
  })));
};
PdpCreditTableSmall.propTypes = PdpCreditTablePropTypes;
const PdpCreditTable = props => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.XSmallOnly, null, matches => matches ? /*#__PURE__*/React.createElement(PdpCreditTableSmall, props) : null), /*#__PURE__*/React.createElement(MatchMedia.Small, null, matches => matches ? /*#__PURE__*/React.createElement(PdpCreditTableFull, props) : null));
PdpCreditTable.propTypes = PdpCreditTablePropTypes;

/* eslint-disable react/forbid-prop-types */
const H2Styled = styled.h2`
  color: ${getThemeProp('global.dark--color')};
  font-size: 1.5rem;
  font-family: ${getThemeProp('global.headings--fontFamily')};
`;
const H2 = _ref => {
  let {
    children,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    pl: [2, 2, 0],
    pt: [3, 3, 0]
  }, /*#__PURE__*/React.createElement(H2Styled, rest, children));
};
const PdpNotice = _ref2 => {
  let {
    status
  } = _ref2;
  if (status === 'D' || status === 'P') {
    return /*#__PURE__*/React.createElement(Notice, {
      kind: "success"
    }, "Congratulations! You're getting a pay back!");
  }
  if (status === 'M') {
    return /*#__PURE__*/React.createElement(Notice, {
      kind: "success"
    }, "You have already earned the maximum Price Drop Payback. Congratulations!");
  }
  return null;
};
const PpdModal = _ref3 => {
  let {
    pdpData,
    onClose
  } = _ref3;
  const {
    status,
    baseFare,
    currentFare,
    fareChange
  } = pdpData;
  const hasNewCredits = (pdpData.newCredits && pdpData.newCredits[0]) !== undefined;
  const hasPrevCredits = (pdpData.previouslyIssuedCredits && pdpData.previouslyIssuedCredits[0]) !== undefined;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Price Drop Payback"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(PdpNotice, {
    status: status
  }), status !== 'M' && /*#__PURE__*/React.createElement(PdpPriceDetails, {
    status: status,
    baseFare: baseFare,
    currentFare: currentFare,
    fareChange: fareChange
  }), hasNewCredits && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(H2, null, "New Credits"), /*#__PURE__*/React.createElement(PdpCreditTable, {
    credits: pdpData.newCredits
  })), hasPrevCredits && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(H2, null, "Previously Issued Credits"), /*#__PURE__*/React.createElement(PdpCreditTable, {
    credits: pdpData.previouslyIssuedCredits
  }))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    text: "Close",
    onClick: onClose
  })));
};
PpdModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  pdpData: PropTypes.object.isRequired
};
PdpNotice.propTypes = {
  status: PropTypes.string
};
PdpNotice.defaultProps = {
  status: ''
};

const PdpErrorModal = _ref => {
  let {
    message,
    onClose
  } = _ref;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Price Drop Payback"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Box, {
    style: {
      maxWidth: '680px'
    }
  }, /*#__PURE__*/React.createElement(Notice, {
    kind: "danger"
  }, message))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    text: "Close",
    onClick: onClose
  })));
};
PdpErrorModal.propTypes = {
  message: PropTypes.string,
  onClose: PropTypes.func
};
PdpErrorModal.defaultProps = {
  message: '',
  onClose: () => {}
};

const PdpButton = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdpData, setPdpData] = useState(null);
  const {
    getPdpCheck
  } = useAppApi();
  const clearError = () => setError('');
  const clearPdp = () => setPdpData(null);
  const {
    details: {
      bn
    }
  } = useContext(TripDetailsContext);
  const handleCheckPdp = async () => {
    setLoading(true);
    try {
      const {
        data
      } = await getPdpCheck({
        bn
      });
      setPdpData(data.pdpStatus);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, pdpData && /*#__PURE__*/React.createElement(PpdModal, {
    onClose: clearPdp,
    pdpData: pdpData
  }), error && /*#__PURE__*/React.createElement(PdpErrorModal, {
    onClose: clearError,
    message: error
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: handleCheckPdp,
    titleCase: true,
    fullWidth: true,
    text: "Check PDP",
    isLoading: loading
  }));
};
const PdpCheck = () => /*#__PURE__*/React.createElement(AddOn, {
  imageUrl: "https://cdn.amtrav.com/trip-details/sidebar/pdp.svg",
  control: /*#__PURE__*/React.createElement(PdpButton, null)
});

const getFlightsSelectorUrl = (flight, bn, nameNum, isPersonal) => {
  let url = '/flights/default.aspx';
  const jsr = {
    bn,
    nameNum,
    isPersonal,
    legs: [{
      fromLocId: flight.fromLocId,
      toLocId: flight.toLocId
    }]
  };
  url += `?jsr=${encodeObjToJsrString(jsr)}`;
  return url;
};
const FlightAddOn = _ref => {
  let {
    flights,
    bn,
    nameNum,
    viewSettings,
    isPersonal
  } = _ref;
  const {
    bookingModPendingAlert,
    openWebUrl
  } = useAppApi();
  const handleFlightSelect = event => {
    const url = event.target.value;
    bookingModPendingAlert({
      bn
    }).then(() => {
      openWebUrl(url);
    });
  };
  const flightSelector = flights.options ? /*#__PURE__*/React.createElement(AddOnOptions$1, {
    options: [{
      id: '',
      text: flights.caption
    }].concat(flights.options.map(flight => ({
      text: flight.display,
      id: getFlightsSelectorUrl(flight, bn, nameNum, isPersonal)
    }))),
    caption: flights.caption,
    onClick: handleFlightSelect,
    iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Hotel"
    })
  }) : /*#__PURE__*/React.createElement(CtaButton, {
    onClick: () => {
      openWebUrl(`/flights/default.aspx?jsr=${encodeObjToJsrString({
        bn,
        nameNum,
        isPersonal
      })}`);
    },
    text: flights.caption,
    iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Plane"
    })
  });
  return /*#__PURE__*/React.createElement(AddOn, {
    control: flightSelector
  });
};
FlightAddOn.propTypes = {
  flights: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({
      display: PropTypes.string,
      fromDate: PropTypes.string,
      toDate: PropTypes.string
    })),
    caption: PropTypes.string
  }),
  bn: PropTypes.string,
  isPersonal: PropTypes.bool,
  nameNum: PropTypes.number,
  viewSettings: PropTypes.shape({
    flightDefaultType: PropTypes.string,
    flightDefaultVendor: PropTypes.string
  })
};
FlightAddOn.defaultProps = {
  flights: [],
  bn: '',
  isPersonal: false,
  nameNum: 0,
  viewSettings: PropTypes.shape({
    flightDefaultType: '',
    flightDefaultVendor: ''
  })
};
var FlightAddOn$1 = withTheme(FlightAddOn);

const getSeatsButton = (hasSeats, bn, al, lastName, src, opener) => {
  const seatsTemplate = `/seats/?bn=${bn}&lastName=${lastName}&al=${al}&src=t`;

  /**
   * United NDC bookings get seats assigned automatically so as a special exception all 'UA' flights coming from the
   * purchase path will always show "Select Seats"
   * */
  if (al === 'UA' && src === 'p') {
    return /*#__PURE__*/React.createElement(CtaButton, {
      onClick: () => {
        opener(seatsTemplate);
      },
      text: "Select Seats",
      iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
        icon: "PlaneSeat"
      })
    });
  }
  switch (hasSeats) {
    case 'Y':
      return /*#__PURE__*/React.createElement(CtaButton, {
        onClick: () => {
          opener(seatsTemplate);
        },
        text: "Change Seats",
        iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
          icon: "PlaneSeat"
        })
      });
    case 'N':
      return /*#__PURE__*/React.createElement(CtaButton, {
        onClick: () => {
          opener(seatsTemplate);
        },
        text: "Select Seats",
        iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
          icon: "PlaneSeat"
        })
      });
    case 'S':
      return /*#__PURE__*/React.createElement(CtaButton, {
        onClick: () => {
          opener(seatsTemplate);
        },
        text: "Add or Change Seats",
        iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
          icon: "PlaneSeat"
        })
      });
    default:
      return null;
  }
};
const getInsuranceButton = (bn, insurance, opener) => {
  const baseFare = insurance && insurance.totalPrice || 0;
  const url = `/purchase/default.aspx?jsr=${encodeObjToJsrString({
    bn,
    itinItems: [{
      service: {
        serviceType: 'I',
        baseFare,
        supplier: 'Travel Guard',
        supplierNum: 2041502,
        description: 'Trip Protection by Travel Guard',
        status: 'NN'
      }
    }]
  })}`;
  return /*#__PURE__*/React.createElement(CtaButton, {
    text: "Insure Your Trip",
    onClick: () => {
      opener(url);
    },
    iconPrefix: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Luggage"
    })
  });
};
const AddOns = _ref => {
  let {
    details,
    viewSettings
  } = _ref;
  const {
    bn,
    addOns
  } = details;
  const al = getFirstAirlineCode({
    details
  });
  const nameNum = getFirstNameNum({
    details
  });
  const {
    getJsr,
    openWebUrl
  } = useAppApi();
  const jsr = getJsr(window.location.search);
  const isPersonal = details === null || details === void 0 ? void 0 : details.isPersonal;
  return /*#__PURE__*/React.createElement(React.Fragment, null, addOns.seats && /*#__PURE__*/React.createElement(AddOn, {
    control: getSeatsButton(addOns.seats.alreadyHasSeats, bn, al, jsr.lastName, jsr.src, openWebUrl)
  }), (addOns === null || addOns === void 0 ? void 0 : addOns.flights) && /*#__PURE__*/React.createElement(FlightAddOn$1, {
    flights: addOns.flights,
    isPersonal: isPersonal,
    bn: bn,
    nameNum: nameNum,
    viewSettings: viewSettings || {}
  }), addOns.hotels && /*#__PURE__*/React.createElement(HotelAddOn$1, {
    hotels: addOns.hotels,
    isPersonal: isPersonal,
    bn: bn,
    nameNum: nameNum,
    viewSettings: viewSettings || {}
  }), addOns.cars && /*#__PURE__*/React.createElement(CarAddOn$1, {
    cars: addOns.cars,
    isPersonal: isPersonal,
    bn: bn,
    nameNum: nameNum,
    viewSettings: viewSettings || {}
  }), addOns.insurance && /*#__PURE__*/React.createElement(AddOn, {
    control: addOns.insurance.alreadyPurchased ? /*#__PURE__*/React.createElement("p", null, "Your Trip Is Insured") : getInsuranceButton(bn, addOns.insurance, openWebUrl)
  }), addOns.pdpCheck && /*#__PURE__*/React.createElement(PdpCheck, null));
};
AddOns.propTypes = {
  details: PropTypes.shape({
    addOns: PropTypes.shape({
      seats: PropTypes.shape({
        alreadyHasSeats: PropTypes.string
      }),
      hotels: PropTypes.object,
      cars: PropTypes.object,
      flights: PropTypes.object,
      insurance: PropTypes.object
    }),
    bn: PropTypes.string,
    al: PropTypes.string,
    jsr: PropTypes.shape({
      lastName: PropTypes.string
    }),
    nameNum: PropTypes.number
  }),
  // viewSettings is just passed through and is opaque at this level
  // eslint-disable-next-line react/forbid-prop-types
  viewSettings: PropTypes.object
};
AddOns.defaultProps = {
  details: {
    addOns: {},
    bn: '',
    al: '',
    jsr: {
      lastName: ''
    },
    nameNum: 0
  },
  viewSettings: {}
};
var AddOns$1 = withTheme(AddOns);

const AddOnsContainer = styled(Flex)`
  gap: ${SPACE.size12};
  justify-content: space-between;
  flex-direction: column;
`;
const AddOnsModal = _ref => {
  let {
    details,
    viewSettings,
    onClose
  } = _ref;
  const {
    seats,
    hotels,
    cars
  } = details.addOns;
  if (!seats && !hotels && !cars) {
    return false;
  }
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Your trip is booked."), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(AddOnsContainer, null, /*#__PURE__*/React.createElement(AddOns$1, {
    details: details,
    viewSettings: viewSettings
  }))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose,
    text: "Close"
  })));
};
AddOnsModal.propTypes = {
  details: PropTypes.shape({
    addOns: PropTypes.object,
    itinItems: PropTypes.array
  }),
  // eslint-disable-next-line react/forbid-prop-types
  viewSettings: PropTypes.object,
  onClose: PropTypes.func
};
AddOnsModal.defaultProps = {
  details: {
    addOns: {},
    itinItems: []
  },
  viewSettings: {},
  onClose: () => {}
};

const ChangeAppliedMessage = () => {
  const params = new URLSearchParams(document.location.search);
  const hasChangeTripMessage = params.has('ctmsg');
  if (hasChangeTripMessage) {
    const encodedTripDetailsNotice = params.get('ctmsg');
    const messageStr = decodeMessageString(encodedTripDetailsNotice);
    const validMessageStr = messageStr !== 'null' || messageStr !== 'undefined';
    if (messageStr.length > 0 && validMessageStr) {
      return /*#__PURE__*/React.createElement(Box, {
        mb: [2, 2, '24px']
      }, /*#__PURE__*/React.createElement(Notice, {
        kind: "info"
      }, messageStr));
    }
  }
  return null;
};

const GlobalStyle = createGlobalStyle`
  body {
    @media print {
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    }

    @page {
      size: 8.5in 11in;
    }
  }
`;
const NoPrint = styled(Box)`
  @media print {
    display: none;
  }
`;
const FullWidthPrint = styled(Box)`
  @media print {
    width: 100%;
  }
`;
const TripDetailsLayout = _ref => {
  let {
    details,
    attendee,
    viewSettings,
    hotels,
    hotelsRequest,
    accountData,
    refetchData
  } = _ref;
  const [modalOpen, setModalOpen] = useState(true);
  const {
    getJsr
  } = useAppApi();
  const JSR = getJsr();
  console.log('Using appApi', JSR);
  const visitFromPurchaseFlow = !isNilOrEmpty(JSR) && JSR.src === 'p';
  // prevents both modals from opening at once
  const showHotelRecommendations = hotels && Object.keys(hotels).length && visitFromPurchaseFlow;
  const showAddonModal = JSR.ao && !showHotelRecommendations;
  if (isNilOrEmpty(details)) return /*#__PURE__*/React.createElement("div", null, "Loading...");
  return (
    /*#__PURE__*/
    // this div needs to remain here for display: block
    React.createElement("div", null, /*#__PURE__*/React.createElement(Flex, {
      flexDirection: ['column', 'column', 'row']
    }, /*#__PURE__*/React.createElement(GlobalStyle, null), modalOpen && showHotelRecommendations ? /*#__PURE__*/React.createElement(HotelRecommendations, {
      modalTitle: "Your Flight is Booked",
      hotels: hotels,
      hotelsRequest: hotelsRequest,
      onClose: () => setModalOpen(false)
    }) : null, modalOpen && showAddonModal ? /*#__PURE__*/React.createElement(AddOnsModal, {
      details: details,
      viewSettings: viewSettings,
      onClose: () => setModalOpen(false)
    }) : null, /*#__PURE__*/React.createElement(FullWidthPrint, {
      width: 1
    }, /*#__PURE__*/React.createElement(ChangeAppliedMessage, null), /*#__PURE__*/React.createElement(TripSummary, {
      details: details,
      refetchData: refetchData,
      companyBillingFields: accountData ? accountData.billingReferences : null
    }), /*#__PURE__*/React.createElement(NoPrint, null, /*#__PURE__*/React.createElement(HotelRecommendationsCTA, {
      hotels: hotels,
      hotelsRequest: hotelsRequest
    })), /*#__PURE__*/React.createElement(Travelers, {
      travelers: details.travelers
    }), !isNilOrEmpty(details.itinItems) && /*#__PURE__*/React.createElement(Itinerary, {
      itinItems: details.itinItems,
      attendee: attendee,
      cancelable: details.cancelable,
      cancelOptions: details.cancelOptions,
      bookingNumber: parseInt(details.bn, 10),
      enableManageTrips: details.enableManageTrips,
      cancelOnlyMode: details.enableCancelOnlyMode
    }), details.amtravPricing && /*#__PURE__*/React.createElement(Charges, {
      charges: details.amtravPricing
    }), /*#__PURE__*/React.createElement(Notes, {
      notes: details.notes
    }))))
  );
};
TripDetailsLayout.propTypes = {
  // We really only care about the fact that view settings is non empty
  // eslint-disable-next-line react/forbid-prop-types
  viewSettings: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  hotels: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  hotelsRequest: PropTypes.object,
  details: PropTypes.shape({
    cancelable: PropTypes.bool,
    cancelOptions: PropTypes.arrayOf(PropTypes.shape({})),
    amtravPricing: PropTypes.shape({}),
    notes: PropTypes.shape({}),
    travelers: PropTypes.arrayOf(PropTypes.shape({})),
    itinItems: PropTypes.arrayOf(PropTypes.shape({})),
    bookingNumber: PropTypes.string,
    bn: PropTypes.string,
    enableManageTrips: PropTypes.bool,
    enableCancelOnlyMode: PropTypes.bool
  }),
  meeting: PropTypes.shape({}),
  attendee: PropTypes.shape({}),
  refetchData: PropTypes.func,
  accountData: PropTypes.shape({
    billingReferences: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      mandatoryPNR: PropTypes.bool,
      staticPerPax: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string)
    }))
  })
};
TripDetailsLayout.defaultProps = {
  viewSettings: {},
  hotels: {},
  hotelsRequest: {},
  details: {
    cancelable: false,
    cancelOptions: [],
    amtravPricing: {},
    notes: {},
    travelers: [],
    itinItems: []
  }
};

const BulletList = styled.ul`
  padding-left: 16px !important;
  li {
    width: 100%;
    list-style: none;
    position: relative;
    line-height: 18px;
    padding: 0 0 0 16px;
    margin-bottom: 8px;
    color: black;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 7px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background-color: black;
    }

    &:last-of-type {
      margin-bottom: 0;

      ${media.Medium`
        width: 70%;
      `};
    }
  }
`;

const AltToggleWrapper = styled(Flex)`
  justify-content: flex-end;
  margin-top: 16px;

  ${media.Medium`
      /* position: absolute; */
      right: 16px;
      bottom: 16px;
      margin-top: unset;
  `};
`;
const AltToggleText = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  border-style: solid;
  border-color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  border-width: ${_ref => {
  let {
    noAltOptions
  } = _ref;
  return noAltOptions ? '0' : '0 0 1px 0';
}};
  cursor: ${_ref2 => {
  let {
    noAltOptions
  } = _ref2;
  return noAltOptions ? 'unset' : 'pointer';
}};
`;
const AltToggleIcon = styled.div`
  height: 18px;
  font-size: 18px;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  transform: ${_ref3 => {
  let {
    isOpen
  } = _ref3;
  return isOpen ? 'translateY(2px)' : 'none';
}};
  margin-left: 8px;
`;

styled(Box)`
  position: relative;
  width: 100%;
  background-color: ${props => tinycolor.mix(getThemeProp('global.warning--color')(props), 'white', 85).toString()};
  padding: 16px;
  box-shadow: ${getThemeProp('card.box-shadow')};
`;
const OutOfPolicyHeader = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => getThemeProp('global.warning--color')(props)};
  color: white;
`;
styled(OutOfPolicyHeader)`
  height: unset;
  line-height: 17px;
  padding: 8px 16px;
`;
styled(Flex)`
  width: 60%;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1.5px;
  line-height: 19px;
  text-transform: uppercase;
  padding: 12px 16px;
  margin-bottom: -2px;

  ${media.Medium`
    width: auto;
  `};
`;
styled.div`
  font-size: 24px;
  margin-right: 8px;
`;
styled(Flex)`
  width: 40%;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: bold;
  color: white;
  padding: 16px;

  ${media.Medium`
    font-size: 16px;
    width: 170px;
  `};
`;

const AltItineraryContainer = styled(Flex)`
  width: 100%;
  border-style: solid;
  border-width: 0 1px 1px 1px;
  border-color: ${props => tinycolor(getThemeProp('global.info--color')(props)).lighten(50).toString()};
  background-color: white;
  padding: 12px 16px;
`;
styled(Box)`
  width: 100%;
  border-style: solid;
  border-width: 1px;
  border-color: ${props => tinycolor(getThemeProp('global.info--color')(props)).lighten(50).toString()};
  background-color: white;
`;
const AltItineraryStrip = styled(Flex)`
  align-items: center;
  width: 100%;
  border-style: solid;
  border-width: 0 0 1px 0;
  border-color: ${props => tinycolor(getThemeProp('global.info--color')(props)).lighten(50).toString()};
  padding: 12px 16px;

  &:last-of-type {
    border-width: 0;
  }
`;
const AltItineraryHeader = styled(Flex)`
  align-items: flex-end;
  padding: 8px 16px;
  background-color: ${props => tinycolor(getThemeProp('global.info--color')(props)).lighten(50).toString()};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const ItineraryNumber = styled(TitleCase)`
  font-weight: 800;
`;

const REASON_CODES = {
  A: 'No in policy options are available',
  S: 'In policy options are not convenient',
  $: 'Better pricing available with out of policy option',
  V: 'Wanted or needed to use this supplier',
  R: 'In policy options had impractical restrictions',
  X: 'I have a travel credit that works for this option, but not in-policy ones',
  O: 'Other/multiple reasons'
};
const ApprovalPolicyInfo = _ref => {
  let {
    titlePrefix,
    total,
    policyBreakDetails,
    reasonCode,
    reasonNotes,
    alternateLength,
    onAlternateToggle,
    alternateOpen
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    kind: "warning"
  }, /*#__PURE__*/React.createElement(Card.Header, null, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    pr: 2
  }, /*#__PURE__*/React.createElement(IconLine, {
    icon: "AlertTriangle"
  })), titlePrefix, " Out of Policy"), /*#__PURE__*/React.createElement("div", null, "Total", /*#__PURE__*/React.createElement(Currency, {
    amount: total
  })))), /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(BulletList, null, policyBreakDetails.map(detail => /*#__PURE__*/React.createElement("li", {
    key: randomString()
  }, detail)))), /*#__PURE__*/React.createElement(Card.BodySection, null, /*#__PURE__*/React.createElement(Text$2, {
    pb: 2,
    primary: true
  }, "The following reason(s) were given for breaking policy:"), (reasonCode || reasonNotes) && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(BulletList, null, REASON_CODES[reasonCode] ? /*#__PURE__*/React.createElement("li", null, REASON_CODES[reasonCode]) : null, reasonNotes ? /*#__PURE__*/React.createElement("li", null, reasonNotes) : null)))), /*#__PURE__*/React.createElement(Box, {
    p: 2
  }, alternateLength > 0 ? /*#__PURE__*/React.createElement(AltToggleWrapper, {
    alignItems: "center",
    onClick: onAlternateToggle
  }, /*#__PURE__*/React.createElement(AltToggleText, null, alternateLength, " lower priced alternative", alternateLength !== 1 ? 's' : ''), /*#__PURE__*/React.createElement(AltToggleIcon, {
    isOpen: alternateOpen
  }, alternateOpen ? /*#__PURE__*/React.createElement(IconAngleDown, {
    rotate: 180
  }) : /*#__PURE__*/React.createElement(IconAngleDown, null))) : /*#__PURE__*/React.createElement(AltToggleWrapper, null, /*#__PURE__*/React.createElement(AltToggleText, {
    noAltOptions: true
  }, "No in-policy options available"))));
};
ApprovalPolicyInfo.propTypes = {
  titlePrefix: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  policyBreakDetails: PropTypes.arrayOf(PropTypes.string).isRequired,
  reasonCode: PropTypes.string.isRequired,
  reasonNotes: PropTypes.string.isRequired,
  alternateLength: PropTypes.number.isRequired,
  onAlternateToggle: PropTypes.func.isRequired,
  alternateOpen: PropTypes.bool.isRequired
};

styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => getThemeProp(`global.success--color`)(props)};
  color: white;
  margin-bottom: 16px;
`;
styled(Flex)`
  align-items: center;
  font-size: 16px;
  line-height: 19px;
  font-weight: 600;
  color: white;
  padding: 12px 16px;
  margin-bottom: -2px;
`;
styled.div`
  font-size: 20px;
  margin-right: 8px;
`;
const InPolicyBanner = _ref => {
  let {
    title = ''
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    pb: 3
  }, /*#__PURE__*/React.createElement(Notice, {
    kind: "success"
  }, title));
};
InPolicyBanner.propTypes = {
  title: PropTypes.string
};

const AirSegment = _ref => {
  let {
    flight
  } = _ref;
  const {
    airlineName,
    brandName,
    fromName,
    fromCode,
    toName,
    toCode,
    departs,
    arrives,
    numStops
  } = flight;
  const departDate = parseDate$1(departs);
  const arriveDate = parseDate$1(arrives);
  const departMonth = monthShortFormatter(departDate);
  const departDay = dayOfMonthLongFormatter(departDate);
  const departTime = hourMinuteMeridianCondensed(departDate);
  const arriveTime = hourMinuteMeridianCondensed(arriveDate);
  return /*#__PURE__*/React.createElement(Box, {
    width: 1
  }, /*#__PURE__*/React.createElement(AltItineraryContainer, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Box, {
    mb: 2
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement("strong", null, airlineName), " (", brandName, ")")), /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, null, fromName, " ", /*#__PURE__*/React.createElement("strong", null, "(", fromCode, ")")), /*#__PURE__*/React.createElement(Box, {
    px: 2
  }, "\u2192"), /*#__PURE__*/React.createElement(Box, null, toName, " ", /*#__PURE__*/React.createElement("strong", null, "(", toCode, ")")))), /*#__PURE__*/React.createElement(AltItineraryContainer, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Flex, null, departMonth, " ", departDay, /*#__PURE__*/React.createElement(Box, {
    px: 2
  }, "|"), departTime, " - ", arriveTime), /*#__PURE__*/React.createElement(Box, null, numStops, " stop", numStops !== 1 ? 's' : '')));
};
const flightProp = PropTypes.shape({
  airlineName: PropTypes.string,
  airlineCode: PropTypes.string,
  numStops: PropTypes.number,
  departs: PropTypes.string,
  arrives: PropTypes.string,
  fromCode: PropTypes.string,
  fromName: PropTypes.string,
  toCode: PropTypes.string,
  toName: PropTypes.string,
  brandName: PropTypes.string // seems to be the cabin?
});
AirSegment.propTypes = {
  flight: flightProp
};
const AlternateAirList = _ref2 => {
  let {
    alternateAirItins
  } = _ref2;
  if (alternateAirItins) {
    return /*#__PURE__*/React.createElement(Card, null, alternateAirItins.map((itin, index) => /*#__PURE__*/React.createElement("div", {
      key: randomString()
    }, /*#__PURE__*/React.createElement(AltItineraryHeader, null, /*#__PURE__*/React.createElement(ItineraryNumber, null, `Alternate Itinerary ${index + 1}`), /*#__PURE__*/React.createElement(Box, {
      ml: "24px"
    }, /*#__PURE__*/React.createElement("strong", null, /*#__PURE__*/React.createElement(Currency, {
      amount: itin.price
    })))), /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column"
    }, itin.flights.map(f => /*#__PURE__*/React.createElement(AirSegment, {
      flight: f,
      key: `${f.fromCode.replace(/[ /]/g, '-')}-${f.toCode.replace(/[ /]/g, '-')}`
    }))))));
  }
  return null;
};
AlternateAirList.propTypes = {
  alternateAirItins: PropTypes.arrayOf(PropTypes.shape({
    flights: PropTypes.arrayOf(flightProp)
  }))
};
const detailsProp$1 = PropTypes.shape({
  airCorporateControls: PropTypes.shape({
    approvalStatus: PropTypes.string,
    approvedBy: PropTypes.string,
    inPolicy: PropTypes.string,
    policyBreakDetails: PropTypes.arrayOf(PropTypes.string),
    reasonCode: PropTypes.string,
    reasonNotes: PropTypes.string,
    alternateAirItins: PropTypes.arrayOf(PropTypes.shape({
      price: PropTypes.number,
      flights: PropTypes.arrayOf(PropTypes.shape({
        fromCode: PropTypes.string,
        toCode: PropTypes.string
      }))
    }))
  }),
  amtravPricing: PropTypes.shape({
    airTotal: PropTypes.number
  })
});
const AirApprovalInfo = _ref3 => {
  let {
    details
  } = _ref3;
  const [listOpen, setListOpen] = useState(false);
  const {
    reasonCode,
    reasonNotes,
    alternateAirItins,
    policyBreakDetails,
    approvedBy,
    inPolicy
  } = details.airCorporateControls;
  const isInPolicy = inPolicy === 'Y';
  const price = details.amtravPricing && details.amtravPricing.airTotal;
  return !isInPolicy ? /*#__PURE__*/React.createElement(Box, {
    mb: "24px"
  }, /*#__PURE__*/React.createElement(ApprovalPolicyInfo, {
    titlePrefix: "Flights",
    total: price,
    policyBreakDetails: policyBreakDetails,
    reasonCode: reasonCode,
    reasonNotes: reasonNotes,
    alternateLength: (alternateAirItins || []).length,
    alternateOpen: listOpen,
    onAlternateToggle: () => setListOpen(!listOpen),
    approved: false,
    approvedBy: approvedBy,
    inPolicy: isInPolicy
  }), !isInPolicy && listOpen && /*#__PURE__*/React.createElement(AlternateAirList, {
    alternateAirItins: alternateAirItins
  })) : /*#__PURE__*/React.createElement(InPolicyBanner, {
    title: "Flights In Policy"
  });
};
AirApprovalInfo.propTypes = {
  details: detailsProp$1.isRequired
};
AirApprovalInfo.defaultProps = {};

const AlternateHotelsList = _ref => {
  let {
    alternateHotels
  } = _ref;
  if (!alternateHotels) return null;
  return /*#__PURE__*/React.createElement(Card, null, alternateHotels.map(hotel => /*#__PURE__*/React.createElement(AltItineraryStrip, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text$2, {
    mb: 2,
    mr: 3,
    primary: true,
    weight: "semiBold"
  }, hotel.hotelName), /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Text$2, null, hotel.starRating, "-Star Hotel"), /*#__PURE__*/React.createElement(Text$2, {
    mx: 2
  }, "\xB7"), /*#__PURE__*/React.createElement(Text$2, null, `${hotel.distance.toFixed(2)} miles away`))), /*#__PURE__*/React.createElement(Flex, {
    width: "100px",
    flexDirection: "column",
    alignItems: "flex-end"
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: hotel.averageNightlyRate
  }))))));
};
const ApprovalHotelInfo = _ref2 => {
  let {
    hotel
  } = _ref2;
  const [listOpen, setListOpen] = useState(false);
  const {
    reasonCode,
    reasonNotes,
    alternateHotels,
    policyBreakDetails,
    approvedBy,
    inPolicy
  } = hotel.corporateControls;
  const isInPolicy = inPolicy === 'Y';
  const showBannerDetails = hotel.hotelName !== '' || undefined;
  return !isInPolicy ? /*#__PURE__*/React.createElement(Box, {
    mb: 3
  }, /*#__PURE__*/React.createElement(ApprovalPolicyInfo, {
    titlePrefix: "Hotel",
    total: hotel.pricing.totalUSD,
    policyBreakDetails: showBannerDetails ? [hotel.hotelName, ...policyBreakDetails] : policyBreakDetails,
    reasonCode: reasonCode,
    reasonNotes: reasonNotes,
    alternateLength: (alternateHotels || []).length,
    alternateOpen: listOpen,
    onAlternateToggle: () => setListOpen(!listOpen),
    approved: false,
    approvedBy: approvedBy,
    inPolicy: isInPolicy
  }), !isInPolicy && listOpen && /*#__PURE__*/React.createElement(AlternateHotelsList, {
    alternateHotels: alternateHotels
  })) : /*#__PURE__*/React.createElement(InPolicyBanner, {
    title: showBannerDetails ? `Hotel In Policy - ${hotel.hotelName}` : 'Hotel In Policy'
  });
};
const carProps$1 = PropTypes.shape({
  // needs more props...
  pricing: PropTypes.shape({
    totalUSD: PropTypes.string
  }),
  vendorName: PropTypes.string,
  carType: PropTypes.string
});
ApprovalHotelInfo.propTypes = {
  hotel: PropTypes.shape({
    hotelName: PropTypes.string,
    pricing: PropTypes.shape({
      totalUSD: PropTypes.number
    }),
    corporateControls: PropTypes.shape({
      alternateHotels: PropTypes.arrayOf(carProps$1),
      approvedBy: PropTypes.string,
      approvalStatus: PropTypes.string,
      inPolicy: PropTypes.string,
      policyBreak: PropTypes.string,
      policyBreakDetails: PropTypes.arrayOf(PropTypes.string),
      reasonCode: PropTypes.string,
      reasonNotes: PropTypes.string
    })
  })
};
AlternateHotelsList.propTypes = {
  alternateHotels: PropTypes.arrayOf(carProps$1)
};

const AlternateCarsList = _ref => {
  let {
    alternateCars
  } = _ref;
  if (!alternateCars) return null;
  return /*#__PURE__*/React.createElement(Card, null, alternateCars.map(car => /*#__PURE__*/React.createElement(AltItineraryStrip, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Box, {
    mr: 3
  }, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement("strong", null, car.vendorName))), /*#__PURE__*/React.createElement(Box, null, car.carType)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text$2, {
    primary: true
  }, /*#__PURE__*/React.createElement(Currency, {
    amount: car.pricing.totalUSD
  }))))));
};
const ApprovalCarInfo = _ref2 => {
  let {
    car
  } = _ref2;
  const [listOpen, setListOpen] = useState(false);
  const {
    reasonCode,
    reasonNotes,
    alternateCars,
    policyBreakDetails,
    approvedBy,
    inPolicy
  } = car.corporateControls;
  const isInPolicy = inPolicy === 'Y';
  const showBannerDetails = car.vendorName !== '' || undefined;
  return !isInPolicy ? /*#__PURE__*/React.createElement(Box, {
    mb: 3
  }, /*#__PURE__*/React.createElement(ApprovalPolicyInfo, {
    titlePrefix: "Car Rental",
    total: car.pricing.totalUSD,
    policyBreakDetails: policyBreakDetails,
    reasonCode: reasonCode,
    reasonNotes: reasonNotes,
    alternateLength: (alternateCars || []).length,
    alternateOpen: listOpen,
    onAlternateToggle: () => setListOpen(!listOpen),
    approved: false,
    approvedBy: approvedBy,
    inPolicy: isInPolicy
  }), listOpen && /*#__PURE__*/React.createElement(AlternateCarsList, {
    alternateCars: alternateCars
  })) : /*#__PURE__*/React.createElement(InPolicyBanner, {
    title: showBannerDetails ? `Car Rental In Policy - ${car.vendorName} (${car.carType})` : 'Car Rental In Policy'
  });
};
const carProps = PropTypes.shape({
  // needs more props...
  pricing: PropTypes.shape({
    totalUSD: PropTypes.number
  }),
  vendorName: PropTypes.string,
  carType: PropTypes.string
});
ApprovalCarInfo.propTypes = {
  car: PropTypes.shape({
    vendorName: PropTypes.string,
    carType: PropTypes.string,
    pricing: PropTypes.shape({
      totalUSD: PropTypes.number
    }),
    corporateControls: PropTypes.shape({
      alternateCars: PropTypes.arrayOf(carProps),
      approvedBy: PropTypes.string,
      approvalStatus: PropTypes.string,
      inPolicy: PropTypes.string,
      policyBreak: PropTypes.string,
      policyBreakDetails: PropTypes.arrayOf(PropTypes.string),
      reasonCode: PropTypes.string,
      reasonNotes: PropTypes.string
    })
  })
};
AlternateCarsList.propTypes = {
  alternateCars: PropTypes.arrayOf(carProps)
};

const ApprovalInfo = _ref => {
  var _details$airCorporate;
  let {
    details
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    mt: [3, 3, 0]
  }, ((_details$airCorporate = details.airCorporateControls) === null || _details$airCorporate === void 0 ? void 0 : _details$airCorporate.inPolicy) === '' || details.airCorporateControls === undefined ? null : /*#__PURE__*/React.createElement(AirApprovalInfo, {
    details: details
  }), details.itinItems.filter(item => item.hotel).map(_ref2 => {
    let {
      hotel
    } = _ref2;
    return /*#__PURE__*/React.createElement("div", {
      key: randomString()
    }, hotel.corporateControls.inPolicy === '' ? null : /*#__PURE__*/React.createElement(ApprovalHotelInfo, {
      hotel: hotel,
      key: randomString()
    }));
  }), details.itinItems.filter(item => item.car).map(_ref3 => {
    let {
      car
    } = _ref3;
    return /*#__PURE__*/React.createElement("div", {
      key: randomString()
    }, car.corporateControls.inPolicy === '' ? null : /*#__PURE__*/React.createElement(ApprovalCarInfo, {
      car: car,
      key: randomString()
    }));
  }));
};
const detailsProp = PropTypes.shape({
  itinItems: PropTypes.arrayOf(PropTypes.shape({
    car: PropTypes.shape({}),
    hotel: PropTypes.shape({})
  })),
  airCorporateControls: PropTypes.shape({
    policyBreakDetails: PropTypes.arrayOf(PropTypes.string),
    inPolicy: PropTypes.string,
    reasonCode: PropTypes.string,
    reasonNotes: PropTypes.string,
    alternateAirItins: PropTypes.arrayOf(PropTypes.shape({
      price: PropTypes.number,
      flights: PropTypes.arrayOf(PropTypes.shape({
        fromCode: PropTypes.string,
        toCode: PropTypes.string
      }))
    }))
  }),
  amtravPricing: PropTypes.shape({
    airTotal: PropTypes.number
  })
});
ApprovalInfo.propTypes = {
  details: detailsProp.isRequired
};
ApprovalInfo.defaultProps = {};

const tripApprovalProps = {
  details: PropTypes.shape({
    bn: PropTypes.string,
    needsApproval: PropTypes.bool,
    cancelable: PropTypes.bool,
    cancelOptions: PropTypes.arrayOf(PropTypes.shape({})),
    amtravPricing: PropTypes.shape({}),
    notes: PropTypes.shape({}),
    travelers: PropTypes.arrayOf(PropTypes.shape({})),
    itinItems: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  accountData: PropTypes.shape({
    billingReferences: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      mandatoryPNR: PropTypes.bool,
      staticPerPax: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string)
    }))
  }),
  refetchData: PropTypes.func
};
const TripApprovalContent = _ref => {
  let {
    details,
    accountData,
    refetchData
  } = _ref;
  const {
    reload
  } = useContext(TripDetailsContext);
  const {
    add
  } = useToasts();
  const {
    bookingApproveOrReject
  } = useAppApi();
  const delayResolve = async payload => {
    try {
      await bookingApproveOrReject(payload);
      const msg = payload.action === ApprovalAccept ? 'The trip is now approved.' : 'The trip has been rejected.';
      await reload();
      add(/*#__PURE__*/React.createElement(ToastNotice, {
        kind: "success",
        text: msg,
        icon: /*#__PURE__*/React.createElement(IconCheckCircle, null)
      }));
    } catch (err) {
      add(/*#__PURE__*/React.createElement(ToastNotice, {
        kind: "danger",
        text: err.message,
        icon: /*#__PURE__*/React.createElement(IconCircleX, null)
      }));
    }
  };
  const messages = [];
  if (isNilOrEmpty(details)) return /*#__PURE__*/React.createElement("div", null, "Loading...");
  return /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    width: [1, 1, 1, 3 / 4],
    pb: SPACE.size128
  }, /*#__PURE__*/React.createElement(Box, null, messages.length !== 0 ? /*#__PURE__*/React.createElement(Box, null, "Alerts") : null, /*#__PURE__*/React.createElement(TripSummary, {
    details: details,
    companyBillingFields: accountData ? accountData.billingReferences : null,
    refetchData: refetchData
  }), /*#__PURE__*/React.createElement(Travelers, {
    travelers: details.travelers
  }), /*#__PURE__*/React.createElement(ApprovalInfo, {
    details: details
  }), !isNilOrEmpty(details.itinItems) && /*#__PURE__*/React.createElement(Itinerary, {
    itinItems: details.itinItems,
    cancelable: details.cancelable,
    cancelOptions: details.cancelOptions
  }), details.amtravPricing && /*#__PURE__*/React.createElement(Charges, {
    charges: details.amtravPricing
  }), /*#__PURE__*/React.createElement(Notes, {
    notes: details.notes
  })), details.needsApproval && /*#__PURE__*/React.createElement(ApprovalTray, {
    list: [details.bn],
    onApprove: delayResolve,
    onReject: delayResolve
  }));
};
TripApprovalContent.propTypes = {
  ...tripApprovalProps
};
TripApprovalContent.defaultProps = {
  details: {
    bn: '',
    needsApproval: false,
    cancelable: false,
    cancelOptions: [],
    amtravPricing: {},
    notes: {},
    travelers: [],
    itinItems: []
  }
};
const TripApprovalLayout = _ref2 => {
  let {
    details,
    accountData,
    refetchData
  } = _ref2;
  return /*#__PURE__*/React.createElement(ToastProvider, null, /*#__PURE__*/React.createElement(TripApprovalContent, {
    details: details,
    accountData: accountData,
    refetchData: refetchData
  }));
};
TripApprovalLayout.propTypes = {
  ...tripApprovalProps
};

const Emailer = _ref => {
  let {
    prompts,
    children
  } = _ref;
  const {
    getJsr
  } = useAppApi();
  const {
    details: {
      bn,
      showForceAddToCalendarOption
    }
  } = useContext(TripDetailsContext);
  const activator = config => {
    return prompts.email({
      bn,
      showForceAddToCalendarOption,
      lastName: getJsr().lastName,
      ...config
    }).then(prompts.success);
  };
  return children(activator);
};
Emailer.propTypes = {
  prompts: PropTypes.shape({
    email: PropTypes.func,
    success: PropTypes.func
  })
};
Emailer.defaultProps = {
  prompts: {
    email: () => {},
    success: () => {}
  }
};
const validateEmail = () => true;
const EmailModal = _ref2 => {
  let {
    onSubmit,
    onClose,
    bn,
    lastName,
    showForceAddToCalendarOption
  } = _ref2;
  const [submitting, setSubmitting] = useState(false);
  const [addItineraryToCal, setAddItineraryToCal] = useState(false);
  const {
    postEmailItinService
  } = useAppApi();
  const handleSubmission = values => {
    setSubmitting(true);
    const emailPayload = {
      bn,
      lastName,
      emails: values.email
    };
    if (showForceAddToCalendarOption) {
      emailPayload.forceAddToCalendar = addItineraryToCal;
    }
    return postEmailItinService(emailPayload).then(res => {
      setSubmitting(false);
      onSubmit(res);
    }, () => {
      setSubmitting(false);
    });
  };
  return /*#__PURE__*/React.createElement(Formik, {
    initialValues: {
      email: ''
    },
    validate: validateEmail,
    onSubmit: handleSubmission
  }, _ref3 => {
    let {
      values,
      submitForm,
      handleBlur,
      handleChange,
      isValid
    } = _ref3;
    return /*#__PURE__*/React.createElement(Modal, {
      onClose: onClose
    }, /*#__PURE__*/React.createElement(Modal.Header, null, "Email Itinerary"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(TextInput, {
      iconLineName: "Mail",
      name: "email",
      value: values.email,
      onBlur: handleBlur,
      onChange: handleChange
    }), showForceAddToCalendarOption ? /*#__PURE__*/React.createElement(Flex, {
      pt: 3,
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Checkbox, {
      label: "Add itinerary to recipients calendar",
      checked: addItineraryToCal,
      onChange: () => {
        setAddItineraryToCal(!addItineraryToCal);
      }
    }), /*#__PURE__*/React.createElement(Flex, {
      pl: 1
    }, /*#__PURE__*/React.createElement(InfoTooltip, {
      content: "If you check this box, we will attach a calendar invitation for each currently booked travel component. If your trip changes you will need to resend new invitations."
    }))) : null), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
      text: "Submit",
      onClick: submitForm,
      disabled: !isValid,
      isLoading: submitting
    })));
  });
};
EmailModal.propTypes = {
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  bn: PropTypes.string,
  lastName: PropTypes.string,
  showForceAddToCalendarOption: PropTypes.bool
};
EmailModal.defaultProps = {
  onSubmit: () => {},
  onClose: () => {},
  bn: '',
  lastName: '',
  showForceAddToCalendarOption: false
};
var Emailer$1 = PromptedComponent(Emailer, {
  email: (resolve, reject, config) => /*#__PURE__*/React.createElement(EmailModal, {
    onSubmit: resolve,
    onClose: reject,
    bn: config.bn,
    lastName: config.lastName,
    showForceAddToCalendarOption: config.showForceAddToCalendarOption
  }),
  success: resolve => /*#__PURE__*/React.createElement(Modal, {
    onClose: resolve
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Email Itinerary"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Notice, {
    icon: true,
    kind: "success"
  }, "It's on its way!")), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    text: "Ok",
    onClick: resolve
  })))
});

const AddOnOptionsContStyled = styled(Flex)`
  width: 180px;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
  margin: -16px;
`;
const PopoverOption = styled.button`
  display: flex;
  font-size: 18px;
  padding: 6px;
  border: none;
  cursor: pointer;
  font-family: ${getThemeProp('global.textBody--fontFamily')};
  text-align: left;
  background: white;

  &:first-child {
    display: none;
  }

  &:hover {
    background-color: whitesmoke;
  }
`;
const PopoverDivider = styled.hr`
  width: 100%;
  margin: 0;
  border-style: solid;
  border-width: 1px 0 0 0;
  border-color: whitesmoke;

  &:nth-child(2),
  &:last-child {
    display: none;
  }
`;
const commonPropTypes = {
  disabled: PropTypes.bool,
  focus: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  message: PropTypes.string,
  uiStyle: PropTypes.shape({})
};
const AddOnOptions = props => {
  const IconSwitch = id => {
    switch (id) {
      case 'google':
        return /*#__PURE__*/React.createElement(IconGoogle, null);
      case 'apple':
        return /*#__PURE__*/React.createElement(IconApple, null);
      case 'outlook':
        return /*#__PURE__*/React.createElement(IconOutlook, null);
      case 'outlookweb':
        return /*#__PURE__*/React.createElement(IconOutlookOnline, null);
      case 'yahoo':
        return /*#__PURE__*/React.createElement(IconYahoo, null);
      default:
        return null;
    }
  };
  const {
    options,
    onClick
  } = props;
  return /*#__PURE__*/React.createElement(Popover, {
    triggerDisplayProperty: "block",
    content: /*#__PURE__*/React.createElement(AddOnOptionsContStyled, null, options.map(option => {
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: option.id
      }, /*#__PURE__*/React.createElement(PopoverOption, {
        onClick: () => onClick(option.id),
        value: option.id
      }, IconSwitch(option.id), "\xA0\xA0", option.text), /*#__PURE__*/React.createElement(PopoverDivider, null));
    }))
  }, /*#__PURE__*/React.createElement(Button, {
    text: "Add to Calendar",
    bordered: true,
    fullWidth: true
  }));
};
AddOnOptions.propTypes = {
  ...commonPropTypes
};
AddOnOptions.defaultProps = {
  options: []
};

const weekdayMonthDayYearTime = dateString => {
  return `${weekdayMonthDayYear(parseDate$1(dateString))} ${hourMinuteMeridianCondensed(parseDate$1(dateString))}`;
};
const monthDay$1 = dateString => {
  return `${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}`;
};
const urlDate = dateString => {
  const date = parseDate$1(dateString);
  return `${dayOfMonthLongFormatter(date)}${monthShortFormatter(date)}${yearTwoDigitFormatter(date)}`;
};
const getCarDesc = item => {
  const {
    car
  } = item;
  return `Car Rental Details:

Pick Up: ${car.pickUpLocationName} on ${weekdayMonthDayYearTime(car.pickUp)}
${car.pickUpDetails.join('\n')}

Drop Off: ${car.dropOffLocationName} on ${weekdayMonthDayYearTime(car.dropOff)}
${car.dropOffDetails.join('\n')}

${car.carType}

${car.confirmationNumber ? `Car Confirmation #${car.confirmationNumber}` : ''}
`;
};
const getFlightDesc = (item, details) => {
  const {
    flight
  } = item;
  const firstTraveler = details.travelers[0];
  const names = details.travelers.map(trav => trav.displayName).join(', ');
  return `Flight Details:

${names ? `Travelers: ${names}` : ''}
${item.flight.segments.map(seg => `
${seg.airlineName} Flight #${seg.flightNumber}

Departs: ${seg.fromName} (${seg.fromCode}) on ${weekdayMonthDayYearTime(seg.departs)},
Arrives: ${seg.toName} (${seg.toCode}) on ${weekdayMonthDayYearTime(seg.arrives)}`).join('\n')}

${flight.vendorRecordLocator ? `Confirmation #${flight.vendorRecordLocator}` : ''}
${flight.checkInLinkOffered ? `
Online Check In: ${getCheckInUrl(flight.airlineCode, flight.vendorRecordLocator || '', firstTraveler.nameParts.firstName, firstTraveler.nameParts.lastName, flight.fromCode, flight.departs)}` : ''}`;
};
const getHotelDesc = item => {
  const addr = item.hotel.hotelAddress;
  return `Hotel Details:

Check In: ${weekdayMonthDayYear(parseDate$1(item.hotel.arrives))}
Check Out: ${weekdayMonthDayYear(parseDate$1(item.hotel.departs))}

${item.hotel.hotelName}
${addr.street1 ? `${addr.street1}
` : ''}${addr.street2 ? `${addr.street2}
` : ''}${[addr.city, addr.state, addr.country].join(', ')}${addr.hotelPhone ? `\n${addr.hotelPhone}` : ''}
${item.hotel.hotelPhone ? `Phone: ${item.hotel.hotelPhone}` : ''}

${item.hotel.confirmationNumber ? `Hotel Confirmation #${item.hotel.confirmationNumber}` : ''}`;
};
const getFlightTitle = flight => {
  const {
    airlineName,
    segments,
    fromCode,
    toCode
  } = flight;
  const firstSeg = segments[0];
  const lastSeg = segments[segments.length - 1];
  return `${airlineName} #${firstSeg.flightNumber} ${fromCode} - ${toCode} Departs ${hourMinuteMeridianCondensed(parseDate$1(firstSeg.departs))} ${firstSeg.fromTerminal ? `(Terminal ${firstSeg.fromTerminal})` : ''} Arrives ${hourMinuteMeridianCondensed(parseDate$1(lastSeg.arrives))} ${lastSeg.toTerminal ? `(Terminal ${lastSeg.toTerminal})` : ''} ${flight.vendorRecordLocator ? `Conf #${flight.vendorRecordLocator}` : ''}`;
};
const getHotelLocation = hotel => {
  const addr = hotel.hotelAddress;
  const nonStreet = [addr.city, addr.state, addr.country];
  const extraComma = nonStreet.some(e => e) ? ', ' : '';
  return [addr.street1, addr.street2].join(' ') + extraComma + nonStreet.join(', ');
};
const getItinDesc = (item, details) => {
  if (item.flight) return getFlightDesc(item, details);
  if (item.car) return getCarDesc(item);
  if (item.hotel) return getHotelDesc(item);
  return null;
};
const getCheckInUrl = (code, locator, first, last, fromCode, departs) => {
  return `https://www.amtrav.com/checkin?airline=${code}&recloc=${locator}` + `&firstname=${first}&lastname=${last}&from=${fromCode}&date=${urlDate(departs).toUpperCase()}`;
};
const baseItin = itin => ({
  id: itin.componentId,
  start: itin.calendarStartTime,
  startTime: itin.calendarStartTime,
  utcStart: itin.calendarStartTimeUtc,
  end: itin.calendarEndTime,
  endTime: itin.calendarEndTime,
  utcEnd: itin.calendarEndTimeUtc,
  timezone: itin.ianaTimeZone
});
const normalizeFlight = (itin, details) => {
  return {
    ...baseItin(itin),
    title: getFlightTitle(itin.flight),
    description: `Booking #${details.bn}
${getFlightDesc(itin, details)}
View Trip: ${window.location.href}`,
    location: `${itin.flight.fromName} ${itin.flight.fromCode}`
  };
};
const normalizeCar = (itin, details) => {
  return {
    ...baseItin(itin),
    title: `Car Rental - ${itin.car.vendorName}`,
    description: `Booking #${details.bn}
${getCarDesc(itin)}
View Trip: ${window.location.href}`,
    location: itin.car.pickUpLocationName
  };
};
const normalizeHotel = (itin, details) => {
  return {
    ...baseItin(itin),
    allDay: true,
    title: itin.hotel.hotelName,
    description: `Booking #${details.bn}
${getHotelDesc(itin)}
View Trip: ${window.location.href}`,
    location: getHotelLocation(itin.hotel)
  };
};
const normalizeTrip = details => {
  const {
    tripTitle,
    tripStartTime,
    tripStartTimeUtc,
    tripEndTime,
    tripEndTimeUtc,
    tripTimeZone,
    itinItems,
    bn
  } = details;
  return {
    id: bn,
    start: tripStartTime,
    startTime: tripStartTime,
    utcStart: tripStartTimeUtc,
    end: tripEndTime,
    endTime: tripEndTime,
    utcEnd: tripEndTimeUtc,
    timezone: tripTimeZone,
    title: `${tripTitle} Trip: ${monthDay$1(tripStartTime)} - ${monthDay$1(tripEndTime)}`,
    description: itinItems.map(item => getItinDesc(item, details)).join('\n\n')
  };
};
const normalizeEvent = (itin, details) => {
  if (!itin) return normalizeTrip(details);
  if (itin.flight) return normalizeFlight(itin, details);
  if (itin.car) return normalizeCar(itin, details);
  if (itin.hotel) return normalizeHotel(itin, details);
  return null;
};

// import { downloadBlob } from '@amtrav/webservice';

const timeZonedApiFormat = (datestring, timeZone) => {
  const date = parseFromTimeZone(datestring, {
    timeZone
  });
  return `${isoExtendedFormat(date)}T${leadingZeroHourMinuteSecondFormatter(date)}${timeZoneOffsetSeparatedFormatter(date)}`;
};
const apiFormat = datestring => {
  const date = parseDate$1(datestring);
  return `${isoBasicFormat(date)}T${apiHourMinuteSecondFormatter(date)}`;
};
const dateOnlyFormat = (datestring, addDay) => {
  const myDate = parseDate$1(datestring);
  return isoBasicFormat(new Date(myDate.getTime() + (addDay ? 24 * 60 * 60 * 1000 : 0)));
};
const makeParamString = params => {
  return Object.entries(params).filter(pair => !isNilOrEmpty(pair[1])) // don't include any null values
  .map(_ref => {
    let [key, val] = _ref;
    return `${key}=${encodeURIComponent(val)}`;
  }) // map them to key=value strings
  .join('&'); // concatenate them all together with &s
};
const getGoogleUrl = event => {
  const dates = event.allDay ? `${dateOnlyFormat(event.start)}/${dateOnlyFormat(event.end, true)}` : `${apiFormat(event.start)}/${apiFormat(event.end)}`;
  const params = {
    dates,
    ctz: event.timezone,
    location: event.location,
    text: event.title,
    details: event.description
  };
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&${makeParamString(params)}`;
};
const getYahooUrl = event => {
  const {
    start,
    end,
    location,
    title,
    description
  } = event;
  const hours = differenceInHours(new Date(end), new Date(start)).toString().padStart(2, '0');
  const minutes = (differenceInMinutes(new Date(end), new Date(start)) - differenceInHours(new Date(end), new Date(start)) * 60).toString().padStart(2, '0');
  const duration = `${hours}${minutes}`;
  return `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(title)}&st=${event.allDay ? dateOnlyFormat(start) : apiFormat(start)}&dur=${event.allDay ? 'allday' : duration}&desc=${encodeURIComponent(description)}&in_loc=${encodeURIComponent(location)}`;
};
const getOutlookUrl = event => {
  const {
    start,
    end,
    timezone,
    location,
    title
  } = event;
  const params = {
    startdt: timeZonedApiFormat(start, timezone),
    enddt: timeZonedApiFormat(end, timezone),
    subject: title,
    location,
    uid: '12345'
  };
  if (event.allDay) {
    params.allday = true;
  }
  return `https://outlook.live.com/owa/?allday=false&path=${encodeURIComponent('/calendar/view/Month')}&rru=addevent&${makeParamString(params)}`;
};
const buildFile = event => {
  const {
    utcStart,
    utcEnd,
    location,
    description,
    title,
    id
  } = event;
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:Amtrav', 'BEGIN:VEVENT', `UID:${id}`, `URL:${document.URL}`, 'METHOD:PUBLISH', `DTSTART:${event.allDay ? dateOnlyFormat(utcStart) : `${apiFormat(utcStart)}Z`}`, `DTEND:${event.allDay ? dateOnlyFormat(utcEnd, true) : `${apiFormat(utcEnd)}Z`}`, `SUMMARY:${title}`, `DESCRIPTION:${description.replace(/\n/g, '\\n')}`, location ? `LOCATION:${location}` : '', 'END:VEVENT', 'END:VCALENDAR'].join('\n');
};
const sendEvent = (event, type, webLinker, downloadBlob) => {
  switch (type) {
    case 'google':
      webLinker(getGoogleUrl(event), '_blank');
      break;
    case 'yahoo':
      webLinker(getYahooUrl(event), '_blank');
      break;
    case 'outlookweb':
      webLinker(getOutlookUrl(event), '_blank');
      break;
    case 'apple':
      downloadBlob(new Blob([buildFile(event)], {
        type: 'text/calendar;charset=utf-8'
      }), {
        fileName: 'event.ics'
      });
      break;
    case 'outlook':
      downloadBlob(new Blob([buildFile(event)], {
        type: 'text/calendar;charset=utf-8'
      }), {
        fileName: 'event.ics'
      });
      break;
    default:
      return null;
  }
  return null;
};

const ItinItemWrapper = styled(Flex)`
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  border-width: 1px;
  border-style: solid;
  border-color: ${getThemeProp('global.border--color')};
  border-radius: ${getThemeProp('global.borderRadius--size')};
  padding: 10px;
  margin: 16px 0;
`;
const TripPieceTitle = styled.div`
  font-weight: bold;
  margin-top: 4px;
`;
const monthDay = dateString => {
  return `${monthLongFormatter(parseDate$1(dateString))} ${dayOfMonthFormatter(parseDate$1(dateString))}`;
};
const eventOptions = [{
  id: '',
  text: 'Add to Calendar'
}, {
  id: 'google',
  text: 'Google',
  type: 'link'
}, {
  id: 'apple',
  text: 'Apple',
  type: 'download'
}, {
  id: 'outlook',
  text: 'Outlook',
  type: 'download'
}, {
  id: 'outlookweb',
  text: 'Outlook Online',
  type: 'link'
}, {
  id: 'yahoo',
  text: 'Yahoo',
  type: 'link'
}];
const ItinTile = _ref => {
  let {
    icon,
    time,
    label,
    header,
    onClick
  } = _ref;
  return /*#__PURE__*/React.createElement(ItinItemWrapper, null, /*#__PURE__*/React.createElement(Flex, {
    width: [1, 1, '60%'],
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    width: "15%"
  }, /*#__PURE__*/React.createElement(IconFlex, {
    size: 24,
    defaultColor: true
  }, icon)), /*#__PURE__*/React.createElement(Box, {
    width: "85%"
  }, /*#__PURE__*/React.createElement(Box, {
    mb: 1
  }, /*#__PURE__*/React.createElement(TitleCase, null, header)), /*#__PURE__*/React.createElement(P, {
    fontSize: "16px"
  }, time), /*#__PURE__*/React.createElement(TripPieceTitle, null, label))), /*#__PURE__*/React.createElement(Box, {
    width: [1, 1, 3 / 8],
    mt: [3, 3, 0]
  }, /*#__PURE__*/React.createElement(AddOnOptions, {
    options: eventOptions,
    onClick: onClick
  })));
};
ItinTile.propTypes = {
  icon: PropTypes.node.isRequired,
  time: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  header: PropTypes.string,
  onClick: PropTypes.func.isRequired
};
const getFlightTile = (_ref2, onClick) => {
  let {
    departs,
    fromName,
    toName
  } = _ref2;
  return /*#__PURE__*/React.createElement(ItinTile, {
    icon: /*#__PURE__*/React.createElement(IconPlaneLine, null),
    time: monthDay(departs),
    label: `${fromName} to ${toName}`,
    onClick: onClick
  });
};
const getCarTile = (_ref3, onClick) => {
  let {
    pickUp,
    dropOff,
    vendorName,
    carType
  } = _ref3;
  return /*#__PURE__*/React.createElement(ItinTile, {
    icon: /*#__PURE__*/React.createElement(IconCarLine, null),
    time: `${monthDay(pickUp)} - ${monthDay(dropOff)}`,
    label: /*#__PURE__*/React.createElement(React.Fragment, null, vendorName, " \u2014 ", carType),
    onClick: onClick
  });
};
const getHotelTile = (_ref4, onClick) => {
  let {
    arrives,
    departs,
    hotelName
  } = _ref4;
  return /*#__PURE__*/React.createElement(ItinTile, {
    icon: /*#__PURE__*/React.createElement(IconHotelLine, null),
    time: `${monthDay(arrives)} - ${monthDay(departs)}`,
    label: `${hotelName}`,
    onClick: onClick
  });
};
const getItinTile = (itin, onClick) => {
  if (itin.flight) return getFlightTile(itin.flight, onClick);
  if (itin.car) return getCarTile(itin.car, onClick);
  if (itin.hotel) return getHotelTile(itin.hotel, onClick);
  return null;
};
const AddToCalModal = _ref5 => {
  let {
    details,
    isOurApp,
    onClose
  } = _ref5;
  const {
    tripTitle,
    tripStartTime,
    tripEndTime,
    itinItems
  } = details;
  const {
    downloadBlob,
    openWebUrl
  } = useAppApi();
  const doCalendar = (eventType, itin) => {
    const calendarEvent = normalizeEvent(itin, details);
    if (isOurApp) {
      var _window;
      if ((_window = window) !== null && _window !== void 0 && (_window = _window.ReactNativeWebView) !== null && _window !== void 0 && _window.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'addToCalendar',
          payload: calendarEvent
        }));
      }
    } else {
      sendEvent(calendarEvent, eventType, openWebUrl, downloadBlob);
    }
  };
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Add itinerary item(s) to calendar"), /*#__PURE__*/React.createElement(Modal.Body, null, itinItems.length > 0 && /*#__PURE__*/React.createElement(P, null, "Add Individual Trip Components"), itinItems.map(itin => getItinTile(itin, eventType => {
    doCalendar(eventType, itin);
  })), itinItems.length > 0 && /*#__PURE__*/React.createElement(ItinTile, {
    icon: /*#__PURE__*/React.createElement(IconCalendarLine, null),
    time: `${monthDay(tripStartTime)} - ${monthDay(tripEndTime)}`,
    header: "BLOCK ENTIRE TRIP",
    label: `${tripTitle} Trip`,
    onClick: eventType => doCalendar(eventType)
  })));
};
AddToCalModal.propTypes = {
  details: PropTypes.shape({
    tripTitle: PropTypes.string,
    tripStartTime: PropTypes.string,
    tripStartTimeUtc: PropTypes.string,
    tripEndTime: PropTypes.string,
    tripEndTimeUtc: PropTypes.string,
    tripTimeZone: PropTypes.string,
    itinItems: PropTypes.arrayOf(PropTypes.shape({})),
    bn: PropTypes.string
  }),
  isOurApp: PropTypes.bool,
  onClose: PropTypes.func
};
AddToCalModal.defaultProps = {
  details: {
    tripTitle: '',
    tripStartTime: '',
    tripStartTimeUtc: '',
    tripEndTime: '',
    tripEndTimeUtc: '',
    tripTimeZone: '',
    itinItems: [],
    bn: ''
  },
  isOurApp: false,
  onClose: () => {}
};

const AddToCal = _ref => {
  let {
    prompts,
    children
  } = _ref;
  const {
    details,
    isOurApp
  } = useContext(TripDetailsContext);
  const activator = config => {
    return prompts.cal({
      details,
      isOurApp,
      ...config
    });
  };
  return children(activator);
};
AddToCal.propTypes = {
  prompts: PropTypes.shape({
    cal: PropTypes.func
  })
};
AddToCal.defaultProps = {
  prompts: {
    cal: () => {}
  }
};
var AddToCal$1 = PromptedComponent(AddToCal, {
  cal: (resolve, reject, calData) => /*#__PURE__*/React.createElement(AddToCalModal, {
    onSubmit: resolve,
    onClose: resolve,
    details: calData.details,
    isOurApp: calData.isOurApp
  })
});

const CancelIcon = styled(IconCircleX)`
  font-size: 28px;
`;
const ActionButtons = styled(Flex)`
  gap: ${SPACE.size12};
`;
const CardBodySectionOptionalBorder = styled(Card.BodySection)`
  ${_ref => {
  let {
    hideBorder
  } = _ref;
  return hideBorder && css`
      border-top: none;
    `;
}};
`;
const StyledControl = styled(Box)`
  padding: 16px 0 16px 8px;
  cursor: pointer;
  color: ${getThemeProp('global.link--color')};
  b {
    color: ${getThemeProp('global.link--color')};
  }

  aside {
    height: 29px;
    width: 37px;
    font-size: 26px;
    text-align: center;
  }

  ${_ref2 => {
  let {
    uiStyle
  } = _ref2;
  return uiStyle && (uiStyle.kind === 'danger' ? css`
          color: ${getThemeProp('global.danger--color')};
          b {
            color: ${getThemeProp('global.danger--color')};
          }
        ` : '');
}}
`;
const noop = () => {};
const Control = _ref3 => {
  let {
    label,
    icon,
    onClick,
    uiStyle
  } = _ref3;
  return /*#__PURE__*/React.createElement(StyledControl, {
    onClick: onClick,
    uiStyle: uiStyle
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, icon, /*#__PURE__*/React.createElement(Box, {
    ml: 2
  }, /*#__PURE__*/React.createElement(P, {
    fontSize: "16px"
  }, /*#__PURE__*/React.createElement("b", null, label)))));
};
Control.propTypes = {
  label: PropTypes.string,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  uiStyle: PropTypes.object
};
Control.defaultProps = {
  label: '',
  icon: null,
  onClick: noop,
  uiStyle: {}
};
const Controls = _ref4 => {
  let {
    bookingNumber = null,
    enableManageTrips = false,
    cancelOnlyMode = false,
    addons = null
  } = _ref4;
  const {
    details: {
      cancelable,
      cancelOptions,
      lastName,
      addOns
    }
  } = useContext(TripDetailsContext);
  const {
    openChangeTripForm
  } = useAppApi();
  const printItinerary = () => {
    window.location = `/print-confirmation?bn=${bookingNumber}&lastName=${lastName}`;
  };
  const hasAddons = (addOns === null || addOns === void 0 ? void 0 : addOns.flights) || (addOns === null || addOns === void 0 ? void 0 : addOns.hotels) || (addOns === null || addOns === void 0 ? void 0 : addOns.cars);
  return /*#__PURE__*/React.createElement(Flex, {
    mb: 2,
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(ActionButtons, {
    flexDirection: ['column', 'row']
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1 0 auto"
  }, /*#__PURE__*/React.createElement(Emailer$1, null, activator => /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    text: "Send",
    bordered: true,
    prefixIconLineName: "Mail",
    onClick: activator
  }))), /*#__PURE__*/React.createElement(Box, {
    flex: "1 0 auto"
  }, /*#__PURE__*/React.createElement(AddToCal$1, null, activator => /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    text: "Add to Calendar",
    bordered: true,
    prefixIconLineName: "Calendar",
    onClick: activator
  }))), /*#__PURE__*/React.createElement(Box, {
    flex: "1 0 auto"
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    text: "Print",
    bordered: true,
    prefixIconLineName: "Printer",
    onClick: printItinerary
  }))), hasAddons || enableManageTrips ? /*#__PURE__*/React.createElement(Card, {
    mt: SPACE.size32
  }, /*#__PURE__*/React.createElement(Card.SubHeader, null, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Heading3, null, "Manage this trip"), /*#__PURE__*/React.createElement(Text$2, {
    pt: SPACE.size4,
    colorToken: getTokenValue(THEME_TOKENS.base.color.secondaryText)
  }, "Change the details of this trip", enableManageTrips ? ' or cancel it' : '', "."))), hasAddons ? /*#__PURE__*/React.createElement(Card.Body, null, /*#__PURE__*/React.createElement(ActionButtons, {
    flexDirection: "column"
  }, addons)) : null, enableManageTrips ? /*#__PURE__*/React.createElement(CardBodySectionOptionalBorder, {
    hideBorder: !hasAddons
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    text: cancelOnlyMode ? 'Cancel Itinerary' : 'Change or Cancel Trip',
    prefixIconLineName: !cancelOnlyMode ? '' : 'Pencil',
    onClick: () => {
      openChangeTripForm(bookingNumber);
    },
    bordered: true,
    kind: "danger"
  })) : null) : null, cancelable && !enableManageTrips && /*#__PURE__*/React.createElement(Canceller$1, null, activator => /*#__PURE__*/React.createElement(Control, {
    label: "Cancel Itinerary",
    icon: /*#__PURE__*/React.createElement(CancelIcon, null),
    onClick: () => activator({
      options: cancelOptions
    }).catch(noop),
    uiStyle: {
      kind: 'danger'
    }
  })));
};
Controls.propTypes = {
  bookingNumber: PropTypes.number,
  enableManageTrips: PropTypes.bool,
  cancelOnlyMode: PropTypes.bool,
  addons: PropTypes.node
};

const Sidebar = _ref => {
  let {
    details,
    viewSettings,
    showAddons
  } = _ref;
  return !isNilOrEmpty(details.itinItems) && /*#__PURE__*/React.createElement(Controls, {
    bookingNumber: parseInt(details.bn, 10),
    enableManageTrips: details.enableManageTrips,
    cancelOnlyMode: details.enableCancelOnlyMode,
    addons: showAddons ? /*#__PURE__*/React.createElement(AddOns$1, {
      details: details,
      viewSettings: viewSettings
    }) : null
  });
};
Sidebar.propTypes = {
  details: PropTypes.shape({
    addOns: PropTypes.shape({}),
    itinItems: PropTypes.arrayOf(PropTypes.shape({})),
    bn: PropTypes.string,
    enableManageTrips: PropTypes.bool,
    enableCancelOnlyMode: PropTypes.bool
  }),
  // eslint-disable-next-line react/forbid-prop-types
  viewSettings: PropTypes.object,
  showAddons: PropTypes.bool
};
Sidebar.defaultProps = {
  details: {
    addOns: {},
    itinItems: []
  },
  viewSettings: {},
  showAddons: true
};

// import { getBookingService, getTravelerInfo, getHotels } from '@amtrav/webservice';
const showHotelRecommendations = details => {
  const addOns = details && details.addOns;
  const hotelOptions = addOns && addOns.hotels && addOns.hotels.options;
  const hotelAddonsAvail = (hotelOptions === null || hotelOptions === void 0 ? void 0 : hotelOptions.length) > 0;
  const flightItinItems = (details && details.itinItems || []).filter(itin => !isNilOrEmpty(itin.flight));
  const itinHasFlight = (flightItinItems === null || flightItinItems === void 0 ? void 0 : flightItinItems.length) > 0;
  return hotelAddonsAvail && itinHasFlight;
};
const createHotelTravelerRoomString = travelers => {
  let adults = 0;
  const hotelTravelers = [];
  const ageOfAdult = 18;
  travelers.forEach(person => {
    if (isNilOrEmpty(person.age) || person.age >= ageOfAdult) {
      adults += 1;
    } else {
      hotelTravelers.push(person.age);
    }
  });
  hotelTravelers.unshift(adults);
  return hotelTravelers.toString();
};
const createHotelsPayload = _ref => {
  let {
    hotel,
    bn,
    travelers
  } = _ref;
  return {
    HSS: 'H',
    checkin: hotel.arrivalDate,
    checkout: hotel.departDate,
    locId: hotel.locId,
    bn,
    rms: createHotelTravelerRoomString(travelers),
    recommendationQuery: {
      cabinClass: hotel.cabinClass,
      originAirport: hotel.originAirport,
      destinationAirport: hotel.destinationAirport
    }
  };
};
const getFirstTravNameNum = data => data.travelers && data.travelers[0] && data.travelers[0].nameNum;
const fetchTripDetails = (jsr, getBookingService) => getBookingService(jsr).then(detailsResponse => ({
  details: detailsResponse.data
}));
const fetchTripDetailsWithHotelRecommendations = (jsr, getBookingService, getHotels) => getBookingService(jsr).then(detailsResponse => {
  if (showHotelRecommendations(detailsResponse.data)) {
    var _detailsResponse$data;
    const firstHotelAddon = (_detailsResponse$data = detailsResponse.data.addOns) === null || _detailsResponse$data === void 0 || (_detailsResponse$data = _detailsResponse$data.hotels) === null || _detailsResponse$data === void 0 ? void 0 : _detailsResponse$data.options[0];
    const hotelsServicePayload = createHotelsPayload({
      hotel: firstHotelAddon,
      bn: jsr.bn,
      travelers: detailsResponse.data.travelers
    });
    return getHotels(hotelsServicePayload).then(htlResponse => ({
      details: detailsResponse.data,
      hotels: htlResponse.data,
      hotelsRequest: hotelsServicePayload
    }));
  }
  return {
    details: detailsResponse.data
  };
});
const fetchTripDetailsWithViewSettings = (jsr, getBookingService, getTravelerInfo) => getBookingService(jsr).then(detailsResponse => {
  const nameNum = getFirstTravNameNum(detailsResponse.data);

  // we don't need to make the getTravelerInfo call for unknown travelers
  if (nameNum === -1) {
    return {
      details: detailsResponse.data
    };
  }
  return getTravelerInfo({
    nameNum
  }).then(travResponse => ({
    details: detailsResponse.data,
    firstTraveler: travResponse.data,
    viewSettings: travResponse.data.viewSettings
  }));
});

export { AppApiContext, Charges$1 as ChargesMedium, Itinerary, Notes, Sidebar, StubProvider, Travelers, TripApprovalLayout, TripDetailsContext, TripDetailsLayout, TripSummarySection, fetchTripDetails, fetchTripDetailsWithHotelRecommendations, fetchTripDetailsWithViewSettings, useAppApi };
//# sourceMappingURL=index.js.map
